// src/client/attachments/add-attachments.ts
import { doc, runTransaction } from "firebase/firestore";
async function addAttachments(firestore, collection53, documentId3, attachments) {
  if (!attachments.length) return;
  const documentRef = doc(firestore, collection53, documentId3);
  await runTransaction(firestore, async (transaction) => {
    const snapshot = await transaction.get(documentRef);
    const existing = snapshot.data()?.attachments ?? [];
    transaction.update(documentRef, {
      attachments: [...existing, ...attachments]
    });
  });
}

// src/client/attachments/delete-attachments.ts
import { doc as doc2, getDoc, updateDoc } from "firebase/firestore";
async function removeAttachmentRefs(firestore, collection53, documentId3, attachmentIds) {
  if (attachmentIds.size === 0) return;
  try {
    const documentRef = doc2(firestore, collection53, documentId3);
    const snapshot = await getDoc(documentRef);
    if (!snapshot.exists()) {
      console.error("[removeAttachmentRefs] document not found:", { collection: collection53, documentId: documentId3 });
      return;
    }
    const currentAttachments = snapshot.data()?.attachments ?? [];
    const updated = currentAttachments.filter((a) => !attachmentIds.has(a.attachmentId));
    await updateDoc(documentRef, { attachments: updated });
  } catch (error) {
    console.error("[removeAttachmentRefs] failed:", { collection: collection53, documentId: documentId3, attachmentIds: [...attachmentIds], error });
    throw error;
  }
}
async function softDeleteAttachments(firestore, collection53, documentId3, attachmentIds) {
  if (attachmentIds.size === 0) return;
  try {
    const documentRef = doc2(firestore, collection53, documentId3);
    const snapshot = await getDoc(documentRef);
    if (!snapshot.exists()) {
      console.error("[softDeleteAttachments] document not found:", { collection: collection53, documentId: documentId3 });
      return;
    }
    const currentAttachments = snapshot.data()?.attachments ?? [];
    const updated = currentAttachments.map((a) => attachmentIds.has(a.attachmentId) ? { ...a, deleted: true } : a);
    await updateDoc(documentRef, { attachments: updated });
  } catch (error) {
    console.error("[softDeleteAttachments] failed:", { collection: collection53, documentId: documentId3, attachmentIds: [...attachmentIds], error });
    throw error;
  }
}

// src/client/attachments/use-attachment-storage.ts
import { Timestamp, doc as doc3, collection as firestoreCollection } from "firebase/firestore";
import { getBlob, getDownloadURL, ref, uploadBytes, uploadBytesResumable } from "firebase/storage";

// src/models/advanced-tracking.ts
var AdvancedTracking = class {
  acceleration;
  externalTemperature;
  humidity;
  latitude;
  light;
  longitude;
  pressure;
  temperature;
  batterPowerPercent;
  accuracy;
  timestamp;
  constructor(params) {
    this.acceleration = params.acceleration;
    this.externalTemperature = params.externalTemperature;
    this.humidity = params.humidity;
    this.latitude = params.latitude;
    this.light = params.light;
    this.longitude = params.longitude;
    this.pressure = params.pressure;
    this.batterPowerPercent = params.batterPowerPercent;
    this.accuracy = params.accuracy;
    this.temperature = params.temperature;
    this.timestamp = params.timestamp instanceof Date ? params.timestamp : params.timestamp ? new Date(params.timestamp) : void 0;
  }
  toJSON() {
    return {
      acceleration: this.acceleration,
      externalTemperature: this.externalTemperature,
      humidity: this.humidity,
      latitude: this.latitude,
      light: this.light,
      longitude: this.longitude,
      pressure: this.pressure,
      batterPowerPercent: this.batterPowerPercent,
      accuracy: this.accuracy,
      temperature: this.temperature,
      timestamp: this.timestamp
    };
  }
  serialize() {
    return JSON.parse(JSON.stringify(this));
  }
};
var AdvancedTrackingConverter = {
  toFirestore() {
    return {};
  },
  fromFirestore(doc42) {
    const data = doc42.data();
    return new AdvancedTracking({ ...data, id: doc42.id, timestamp: data.timestamp?.toDate() });
  }
};

// src/models/airport.ts
var Airport = class {
  name;
  city;
  code;
  countryCode;
  timezone;
  longitude;
  latitude;
  constructor(params) {
    this.name = params.name;
    this.city = params.city;
    this.code = params.code;
    this.countryCode = params.countryCode;
    this.timezone = params.timezone;
    this.longitude = params.longitude;
    this.latitude = params.latitude;
  }
  toJSON() {
    return {
      name: this.name,
      city: this.city,
      code: this.code,
      countryCode: this.countryCode,
      timezone: this.timezone,
      longitude: this.longitude,
      latitude: this.latitude
    };
  }
  serialize() {
    return JSON.parse(JSON.stringify(this));
  }
};

// src/utils/milliseconds-to-time-array.ts
import { Duration } from "luxon";
function millisecondsToTimeArray(milliseconds) {
  const { days, hours, minutes, seconds } = Duration.fromObject({ milliseconds }).shiftTo(
    "days",
    "hours",
    "minutes",
    "seconds",
    "milliseconds"
  );
  return [days > 0 ? days : 0, hours > 0 ? hours : 0, minutes > 0 ? minutes : 0, seconds > 0 ? seconds : 0];
}

// src/utils/chart-data/chart-data-point.ts
import { nanoid } from "nanoid";

// src/utils/chart-data/chart-data-segment.ts
import { nanoid as nanoid2 } from "nanoid";

// src/utils/date.ts
function toDate(value) {
  if (!value) return void 0;
  if (value instanceof Date) return value;
  if (typeof value === "object" && "toDate" in value && typeof value.toDate === "function") {
    return value.toDate();
  }
  return new Date(value);
}

// src/utils/format-date-time.ts
import { DateTime } from "luxon";
import * as tz from "moment-timezone";

// src/utils/format-duration.ts
function formatDuration(milliseconds, options) {
  if (milliseconds == null || Number.isNaN(milliseconds)) {
    return "-";
  }
  const formatter = new Intl.NumberFormat();
  if (options?.convertToUnit) {
    let value;
    const unitChar = options.convertToUnit;
    switch (options.convertToUnit) {
      case "h":
        value = Math.floor(milliseconds / (1e3 * 60 * 60));
        break;
      case "m":
        value = Math.floor(milliseconds / (1e3 * 60));
        break;
      case "s":
        value = Math.floor(milliseconds / 1e3);
        break;
      default:
        return "-";
    }
    return `${formatter.format(value)}${unitChar}`;
  }
  const [rawDays, rawHours, rawMinutes, rawSeconds] = millisecondsToTimeArray(milliseconds);
  const totalHours = rawHours + rawDays * 24;
  const activeUnits = options?.displayUnits ?? ["h", "m", "s"];
  const potentialParts = [];
  const showZeroUnits = options?.showZeroUnits;
  if (activeUnits.includes("h") && (totalHours > 0 || showZeroUnits)) {
    potentialParts.push(`${formatter.format(totalHours)}h`);
  }
  if (activeUnits.includes("m") && (rawMinutes > 0 || showZeroUnits)) {
    potentialParts.push(`${formatter.format(rawMinutes)}m`);
  }
  if (activeUnits.includes("s") && (rawSeconds > 0 || showZeroUnits)) {
    potentialParts.push(`${formatter.format(rawSeconds)}s`);
  }
  let finalParts = potentialParts;
  if (finalParts.length === 0) {
    if (milliseconds === 0) {
      if (activeUnits.includes("s")) return "0s";
      if (activeUnits.includes("m")) return "0m";
      if (activeUnits.includes("h")) return "0h";
      return "0s";
    } else {
      return "";
    }
  }
  if (options?.maxUnits != null) {
    if (options.maxUnits <= 0) {
      finalParts = [];
    } else if (options.maxUnits < finalParts.length) {
      finalParts = finalParts.slice(0, options.maxUnits);
    }
  }
  return finalParts.join(" ");
}

// src/utils/get-default-measurement-unit.ts
var METRIC_LANGUAGE_PREFIXES = [
  "de",
  "fr",
  "it",
  "es",
  "pt",
  "nl",
  "sv",
  "no",
  "nb",
  "nn",
  "da",
  "fi",
  "pl",
  "cs",
  "sk",
  "hu",
  "el",
  "ro",
  "bg",
  "hr",
  "sl",
  "lt",
  "lv",
  "et",
  "ru",
  "uk",
  "ja",
  "ko",
  "zh",
  "th",
  "vi",
  "id",
  "ms",
  "tr",
  "he",
  "ar",
  "hi",
  "bn"
];
var METRIC_EN_VARIANTS = ["en-au", "en-nz", "en-ca", "en-ie", "en-in", "en-za"];
function isMetricLocale(locale) {
  const normalized = locale.toLowerCase();
  if (METRIC_EN_VARIANTS.some((v) => normalized.startsWith(v))) return true;
  const language = normalized.split("-")[0];
  return METRIC_LANGUAGE_PREFIXES.includes(language);
}
function getDefaultMeasurementUnit(localeHint) {
  if (localeHint) {
    return isMetricLocale(localeHint) ? "metric" /* METRIC */ : "imperial" /* IMPERIAL */;
  }
  if (typeof window !== "undefined") {
    const locale = navigator.language ?? "en-US";
    return isMetricLocale(locale) ? "metric" /* METRIC */ : "imperial" /* IMPERIAL */;
  }
  return "imperial" /* IMPERIAL */;
}

// src/utils/get-interval-logs.ts
import { differenceInMinutes } from "date-fns";
import { DateTime as DateTime2 } from "luxon";

// src/utils/inventory/audit-period.ts
import { DateTime as DateTime3 } from "luxon";

// src/utils/inventory/barcode.ts
import { format, isValid, parse } from "date-fns";

// src/utils/resolve-location.ts
var STALE_THRESHOLD_MS = 5 * 60 * 1e3;

// src/models/attachment-ref.ts
var AttachmentRef = class {
  attachmentId;
  type;
  fileName;
  thumbnailName;
  displayName;
  status;
  fileSize;
  duration;
  dimensions;
  createdAt;
  updatedAt;
  deleted;
  constructor(params) {
    this.attachmentId = params.attachmentId;
    this.type = params.type;
    this.fileName = params.fileName;
    this.thumbnailName = params.thumbnailName;
    this.displayName = params.displayName;
    this.status = params.status;
    this.fileSize = params.fileSize;
    this.duration = params.duration;
    this.dimensions = params.dimensions;
    this.createdAt = toDate(params.createdAt);
    this.updatedAt = toDate(params.updatedAt);
    this.deleted = params.deleted;
  }
  toJSON() {
    return {
      attachmentId: this.attachmentId,
      type: this.type,
      fileName: this.fileName,
      thumbnailName: this.thumbnailName,
      displayName: this.displayName,
      status: this.status,
      fileSize: this.fileSize,
      duration: this.duration,
      dimensions: this.dimensions,
      createdAt: this.createdAt,
      updatedAt: this.updatedAt,
      deleted: this.deleted
    };
  }
  serialize() {
    return JSON.parse(JSON.stringify(this));
  }
};

// src/models/audit-log.ts
var AuditLog = class {
  id;
  userId;
  event;
  metadata;
  context;
  contextDate;
  timestampUser;
  timestampServer;
  timezone;
  language;
  locale;
  device;
  deviceIdentifier;
  appVersion;
  os;
  osVersion;
  constructor(params) {
    this.id = params.id;
    this.userId = params.userId;
    this.event = params.event;
    this.metadata = params.metadata;
    this.context = params.context;
    this.contextDate = typeof params.contextDate === "string" ? new Date(params.contextDate) : params.contextDate;
    this.timestampUser = typeof params.timestampUser === "string" ? new Date(params.timestampUser) : params.timestampUser;
    this.timestampServer = typeof params.timestampServer === "string" ? new Date(params.timestampServer) : params.timestampServer;
    this.timezone = params.timezone;
    this.language = params.language;
    this.locale = params.locale;
    this.device = params.device;
    this.deviceIdentifier = params.deviceIdentifier;
    this.appVersion = params.appVersion;
    this.os = params.os;
    this.osVersion = params.osVersion;
  }
  toJSON() {
    return {
      id: this.id,
      userId: this.userId,
      event: this.event,
      metadata: this.metadata,
      context: this.context,
      timestampUser: this.timestampUser,
      timestampServer: this.timestampServer,
      timezone: this.timezone,
      device: this.device,
      deviceIdentifier: this.deviceIdentifier,
      appVersion: this.appVersion,
      os: this.os,
      osVersion: this.osVersion
    };
  }
  serialize() {
    return JSON.parse(JSON.stringify(this));
  }
};
var AuditLogConverter = {
  toFirestore() {
    return {};
  },
  fromFirestore(doc42) {
    const data = doc42.data();
    return new AuditLog({
      ...data,
      id: doc42.id,
      contextDate: data.contextDate?.toDate(),
      timestampUser: data.timestampUser?.toDate(),
      timestampServer: data.timestampServer?.toDate()
    });
  }
};

// src/models/product-type.ts
var ProductTypeUtil = class {
  static toUIString(type, legalMark = true) {
    if (type == null) {
      return "No Device connected";
    }
    switch (type) {
      case "sherpapak-cts" /* SHERPAPAK */:
        return legalMark ? "SherpaPak\xAE" : "SherpaPak";
      case "lungguard" /* LUNGGUARD */:
        return legalMark ? "LUNGguard\xAE" : "LUNGguard";
      case "baroguard" /* BAROGUARD */:
        return legalMark ? "BAROguard\xAE" : "BAROguard";
      case "liverguard" /* LIVERGUARD */:
        return legalMark ? "LIVERguard\xAE" : "LIVERguard";
      case "sherpapak-kts" /* KTS */:
        return legalMark ? "Kidney Transport System" : "Kidney Transport System";
      case "kidneyvault" /* KIDNEYVAULT */:
        return legalMark ? "KidneyVault\u2122" : "KidneyVault";
      case "pancreaspak" /* PANCREASPAK */:
        return "PancreasPak\u2122";
      case "vantage-point" /* VANTAGEPOINT */:
        return "VantagePoint Tracking ";
      default:
        return "SherpaPak\xAE";
    }
  }
};

// src/models/organ-type.ts
var OrganTypeUtil = class {
  static toUIString(type, organPosition, capitalize = true) {
    switch (type) {
      case "heart" /* HEART */:
        return capitalize ? "Heart" : "heart";
      case "lung" /* LUNG */:
        return capitalize ? "Lung" : "lung";
      case "liver" /* LIVER */:
        return capitalize ? "Liver" : "liver";
      case "kidney" /* KIDNEY */:
        if (organPosition === "LEFT" /* LEFT */) {
          return capitalize ? "Left Kidney" : "left kidney";
        }
        if (organPosition === "RIGHT" /* RIGHT */) {
          return capitalize ? "Right Kidney" : "right kidney";
        }
        return capitalize ? "Kidneys" : "kidneys";
      case "pancreas" /* PANCREAS */:
        return capitalize ? "Pancreas" : "pancreas";
      default:
        return capitalize ? "Heart" : "heart";
    }
  }
  static fromProductType(productType) {
    switch (productType) {
      case "sherpapak-cts" /* SHERPAPAK */:
        return "heart" /* HEART */;
      case "lungguard" /* LUNGGUARD */:
      case "baroguard" /* BAROGUARD */:
        return "lung" /* LUNG */;
      case "liverguard" /* LIVERGUARD */:
        return "liver" /* LIVER */;
      case "sherpapak-kts" /* KTS */:
      case "kidneyvault" /* KIDNEYVAULT */:
        return "kidney" /* KIDNEY */;
      case "pancreaspak" /* PANCREASPAK */:
        return "pancreas" /* PANCREAS */;
      case "vantage-point" /* VANTAGEPOINT */:
        return null;
      default:
        return "heart" /* HEART */;
    }
  }
};

// src/models/timeline-entry-type.ts
var TimelineEntryTypeUtil = class {
  /**
   * Converts a timeline entry type to a human-readable UI string.
   *
   * For entries involving devices (e.g., "Lung Placed into..."), the product name
   * is included when available. When product is undefined, "Device" is used as fallback.
   *
   * @param type - The timeline entry type to convert
   * @param product - Optional product type for device-specific strings
   * @param annotations - Optional annotations for position or cannulation details
   * @returns A formatted string representation for display
   *
   * @example
   * ```typescript
   * // With product
   * TimelineEntryTypeUtil.toUIString(TimelineEntryType.LUNG_PLACED_IN_LUNGGUARD, ProductType.BAROGUARD);
   * // => "Lung(s) Placed into BAROguard®"
   *
   * // Without product (v7 sessions)
   * TimelineEntryTypeUtil.toUIString(TimelineEntryType.LUNG_PLACED_IN_LUNGGUARD);
   * // => "Lung(s) Placed into Device"
   * ```
   */
  static toUIString(type, product, annotations = {}) {
    const productName = product ? ProductTypeUtil.toUIString(product) : "Device";
    const lungPositionTypeAnnotation = product === "baroguard" /* BAROGUARD */ && annotations?.position != null ? ` - ${LungPositionTypeUtil.toUIString(annotations.position)}` : "";
    const cannulationTypeAnnotation = annotations?.cannulation != null ? ` - ${CannulationTypeUtil.toUIString(annotations.cannulation)}` : "";
    switch (type) {
      case "START_SESSION" /* START_SESSION */:
        return "Case Started";
      case "STOP_SESSION" /* STOP_SESSION */:
        return "Case Stopped";
      case "LEAVE_TX_CENTER" /* LEAVE_TX_CENTER */:
        return "Departed Recipient Center";
      case "ARRIVAL_AT_DONOR" /* ARRIVAL_AT_DONOR */:
        return "Arrived at Donor Center";
      case "DONOR_IN_ROOM" /* DONOR_IN_ROOM */:
        return "Donor In-room";
      case "HEAD_VESSEL_CLAMP" /* HEAD_VESSEL_CLAMP */:
        return "Head Vessel Clamp/Ligate";
      case "TIME_TO_INCISION" /* TIME_TO_INCISION */:
        return "Time of Incision";
      case "ORGAN_ACCEPTED" /* ORGAN_ACCEPTED */:
        return "Organ Accepted";
      case "ORGAN_DECLINED" /* ORGAN_DECLINED */:
        return "Organ Declined";
      case "OFFER_ACKNOWLEDGED" /* OFFER_ACKNOWLEDGED */:
        return "Offer Acknowledged";
      case "PROVISIONAL_YES" /* PROVISIONAL_YES */:
        return "Provisional Yes";
      case "SURGEON_REVIEW" /* SURGEON_REVIEW */:
        return "Offer in Review";
      case "OFFER_ACCEPTED" /* OFFER_ACCEPTED */:
        return "Offer Accepted";
      case "OFFER_DECLINED" /* OFFER_DECLINED */:
        return "Offer Declined";
      case "TIME_OF_WITHDRAWAL" /* TIME_OF_WITHDRAWAL */:
        return "Time of Withdrawal";
      case "WARM_ISCHEMIC_TIME_START" /* WARM_ISCHEMIC_TIME_START */:
        return "Warm Ischemic Time Start";
      case "OBSERVATION_PERIOD_START" /* OBSERVATION_PERIOD_START */:
        return "Observation Period Start";
      case "TIME_OF_DEATH" /* TIME_OF_DEATH */:
        return "Time of Death";
      case "HEPARIN_ADMINISTERED" /* HEPARIN_ADMINISTERED */:
        return "Heparin Administered";
      case "CROSSCLAMP_AT_DONOR" /* CROSSCLAMP_AT_DONOR */:
        return "Donor Crossclamp";
      case "ORGAN_REMOVAL" /* ORGAN_REMOVAL */:
        return "Heart Removal";
      case "ORGAN_REMOVAL_LUNG" /* ORGAN_REMOVAL_LUNG */:
        return "Lung Removal";
      case "AIR_LEAK_ASSESSMENT_COMPLETE" /* AIR_LEAK_ASSESSMENT_COMPLETE */:
        return "Air Leak Assessment Complete";
      case "ORGAN_REMOVAL_LIVER" /* ORGAN_REMOVAL_LIVER */:
        return "Liver Removal";
      case "HEART_PLACED_IN_SHERPAPAK" /* HEART_PLACED_IN_SHERPAPAK */:
        return `Heart Placed into ${productName}`;
      case "LUNG_PLACED_IN_LUNGGUARD" /* LUNG_PLACED_IN_LUNGGUARD */:
        return `Lung(s) Placed into ${productName}${lungPositionTypeAnnotation}`;
      case "LIVER_PLACED_IN_LIVERGUARD" /* LIVER_PLACED_IN_LIVERGUARD */:
        return `Liver Placed into ${productName}`;
      case "LEAVE_DONOR" /* LEAVE_DONOR */:
        return "Departed Donor Center";
      case "ARRIVE_AT_RECIPIENT" /* ARRIVE_AT_RECIPIENT */:
        return "Arrived at Recipient Center";
      case "HEART_REMOVED_FROM_SHERPAPAK" /* HEART_REMOVED_FROM_SHERPAPAK */:
        return `Heart Removed from ${productName}`;
      case "LIVER_REMOVED_FROM_LIVERGUARD" /* LIVER_REMOVED_FROM_LIVERGUARD */:
        return `Liver Removed from ${productName}`;
      case "LUNGS_REMOVED_FROM_LUNGGUARD" /* LUNGS_REMOVED_FROM_LUNGGUARD */:
        return `Lungs Removed from ${productName}`;
      case "LEFT_LUNG_REMOVED_FROM_LUNGGUARD" /* LEFT_LUNG_REMOVED_FROM_LUNGGUARD */:
        return `Left Lung Removed from ${productName}`;
      case "RIGHT_LUNG_REMOVED_FROM_LUNGGUARD" /* RIGHT_LUNG_REMOVED_FROM_LUNGGUARD */:
        return `Right Lung Removed from ${productName}`;
      case "ANASTOMOSIS" /* ANASTOMOSIS */:
        return "Anastomosis";
      case "LEFT_LUNG_ANASTOMOSIS_COMPLETE" /* LEFT_LUNG_ANASTOMOSIS_COMPLETE */:
        return "Left Lung Anastomosis Complete";
      case "RIGHT_LUNG_ANASTOMOSIS_COMPLETE" /* RIGHT_LUNG_ANASTOMOSIS_COMPLETE */:
        return "Right Lung Anastomosis Complete";
      case "CROSSCLAMP_AT_RECIPIENT" /* CROSSCLAMP_AT_RECIPIENT */:
        return "Recipient Off Clamp";
      case "RECIPIENT_LEFT_LUNG_OFF_CLAMP" /* RECIPIENT_LEFT_LUNG_OFF_CLAMP */:
        return "Recipient Left Lung Off Clamp";
      case "RECIPIENT_RIGHT_LUNG_OFF_CLAMP" /* RECIPIENT_RIGHT_LUNG_OFF_CLAMP */:
        return "Recipient Right Lung Off Clamp";
      case "PORTAL_REPERFUSION" /* PORTAL_REPERFUSION */:
        return "Portal Reperfusion";
      case "ARTERIAL_REPERFUSION" /* ARTERIAL_REPERFUSION */:
        return "Arterial Reperfusion";
      case "PERFUSION_START" /* PERFUSION_START */:
        return "Start Aortic Flush";
      case "PERFUSION_STOP" /* PERFUSION_STOP */:
        return "End Aortic Flush";
      case "ON_CARDIO_PULMONARY_BYPASS_1" /* ON_CARDIO_PULMONARY_BYPASS_1 */:
        return "On Cardio Pulmonary Bypass 1";
      case "START_WEAN_1" /* START_WEAN_1 */:
        return "Start Wean 1";
      case "OFF_CARDIO_PULMONARY_BYPASS_1" /* OFF_CARDIO_PULMONARY_BYPASS_1 */:
        return "Off Cardio Pulmonary Bypass 1";
      case "ON_CARDIO_PULMONARY_BYPASS_2" /* ON_CARDIO_PULMONARY_BYPASS_2 */:
        return "On Cardio Pulmonary Bypass 2";
      case "START_WEAN_2" /* START_WEAN_2 */:
        return "Start Wean 2";
      case "OFF_CARDIO_PULMONARY_BYPASS_2" /* OFF_CARDIO_PULMONARY_BYPASS_2 */:
        return "Off Cardio Pulmonary Bypass 2";
      case "LEAVE_OPO" /* LEAVE_OPO */:
        return "Departed OPO";
      case "ARRIVE_AT_OPO" /* ARRIVE_AT_OPO */:
        return "Arrived at OPO";
      case "KIDNEY_DONOR_FINAL_BLOOD_UREA_NITROGEN" /* KIDNEY_DONOR_FINAL_BLOOD_UREA_NITROGEN */:
        return "Donor Final Blood Urea Nitrogen";
      case "KIDNEY_DONOR_FINAL_SERUM_CREATININE" /* KIDNEY_DONOR_FINAL_SERUM_CREATININE */:
        return "Donor Final Serum Creatinine";
      case "KIDNEY_DONOR_KIDNEY_DONOR_PROFILE_INDEX" /* KIDNEY_DONOR_KIDNEY_DONOR_PROFILE_INDEX */:
        return "Donor Kidney Donor Profile Index";
      case "KIDNEY_WITHDRAWAL_OF_SUPPORT" /* KIDNEY_WITHDRAWAL_OF_SUPPORT */:
        return "Withdrawal of Support";
      case "KIDNEY_START_AGONAL_PHASE" /* KIDNEY_START_AGONAL_PHASE */:
        return "Start of Agonal Phase";
      case "KIDNEY_START_FLUSH" /* KIDNEY_START_FLUSH */:
        return "Start Flush";
      case "KIDNEY_X_CLAMP" /* KIDNEY_X_CLAMP */:
        return "X Clamp";
      case "LEFT_KIDNEY_REMOVAL" /* LEFT_KIDNEY_REMOVAL */:
        return "Left Kidney Removal";
      case "RIGHT_KIDNEY_REMOVAL" /* RIGHT_KIDNEY_REMOVAL */:
        return "Right Kidney Removal";
      case "LEFT_KIDNEY_PLACED_ON_ICE" /* LEFT_KIDNEY_PLACED_ON_ICE */:
        return "Left Kidney Placed on Ice";
      case "RIGHT_KIDNEY_PLACED_ON_ICE" /* RIGHT_KIDNEY_PLACED_ON_ICE */:
        return "Right Kidney Placed on Ice";
      case "LEFT_KIDNEY_PLACED_INTO_STORAGE" /* LEFT_KIDNEY_PLACED_INTO_STORAGE */:
        return `Left Kidney Placed into ${productName}`;
      case "RIGHT_KIDNEY_PLACED_INTO_STORAGE" /* RIGHT_KIDNEY_PLACED_INTO_STORAGE */:
        return `Right Kidney Placed into ${productName}`;
      case "LEFT_KIDNEY_PLACED_IN_THIRD_PARTY_STORAGE" /* LEFT_KIDNEY_PLACED_IN_THIRD_PARTY_STORAGE */:
        return "Left Kidney Placed into Non-Integrated Perfusion Device";
      case "RIGHT_KIDNEY_PLACED_IN_THIRD_PARTY_STORAGE" /* RIGHT_KIDNEY_PLACED_IN_THIRD_PARTY_STORAGE */:
        return "Right Kidney Placed into Non-Integrated Perfusion Device";
      case "LEFT_KIDNEY_HANDED_OFF_TO_COURIER" /* LEFT_KIDNEY_HANDED_OFF_TO_COURIER */:
        return "Left Kidney Handed off to Courier/Air";
      case "RIGHT_KIDNEY_HANDED_OFF_TO_COURIER" /* RIGHT_KIDNEY_HANDED_OFF_TO_COURIER */:
        return "Right Kidney Handed off to Courier/Air";
      case "LEFT_KIDNEY_ARRIVED_AT_TXC_OR_OPO" /* LEFT_KIDNEY_ARRIVED_AT_TXC_OR_OPO */:
        return "Left Kidney Arrived at TXC or OPO";
      case "RIGHT_KIDNEY_ARRIVED_AT_TXC_OR_OPO" /* RIGHT_KIDNEY_ARRIVED_AT_TXC_OR_OPO */:
        return "Right Kidney Arrived at TXC or OPO";
      case "LEFT_KIDNEY_OUT_OF_STORAGE" /* LEFT_KIDNEY_OUT_OF_STORAGE */:
        return `Left Kidney Out of ${productName}`;
      case "RIGHT_KIDNEY_OUT_OF_STORAGE" /* RIGHT_KIDNEY_OUT_OF_STORAGE */:
        return `Right Kidney Out of ${productName}`;
      case "LEFT_KIDNEY_REMOVED_FROM_ICE" /* LEFT_KIDNEY_REMOVED_FROM_ICE */:
        return "Left Kidney Removed from Ice";
      case "RIGHT_KIDNEY_REMOVED_FROM_ICE" /* RIGHT_KIDNEY_REMOVED_FROM_ICE */:
        return "Right Kidney Removed from Ice";
      case "LEFT_KIDNEY_REMOVED_FROM_THIRD_PARTY_STORAGE" /* LEFT_KIDNEY_REMOVED_FROM_THIRD_PARTY_STORAGE */:
        return "Left Kidney Removed from Non-Integrated Perfusion Device";
      case "RIGHT_KIDNEY_REMOVED_FROM_THIRD_PARTY_STORAGE" /* RIGHT_KIDNEY_REMOVED_FROM_THIRD_PARTY_STORAGE */:
        return "Right Kidney Removed from Non-Integrated Perfusion Device";
      case "LEFT_KIDNEY_ARTERY_ANASTOMOSIS" /* LEFT_KIDNEY_ARTERY_ANASTOMOSIS */:
        return "Left Kidney Artery Anastomosis";
      case "RIGHT_KIDNEY_ARTERY_ANASTOMOSIS" /* RIGHT_KIDNEY_ARTERY_ANASTOMOSIS */:
        return "Right Kidney Artery Anastomosis";
      case "LEFT_KIDNEY_VEIN_ANASTOMOSIS" /* LEFT_KIDNEY_VEIN_ANASTOMOSIS */:
        return "Left Kidney Vein Anastomosis";
      case "RIGHT_KIDNEY_VEIN_ANASTOMOSIS" /* RIGHT_KIDNEY_VEIN_ANASTOMOSIS */:
        return "Right Kidney Vein Anastomosis";
      case "LEFT_KIDNEY_URETER_ANASTOMOSIS" /* LEFT_KIDNEY_URETER_ANASTOMOSIS */:
        return "Left Kidney Ureter Anastomosis";
      case "RIGHT_KIDNEY_URETER_ANASTOMOSIS" /* RIGHT_KIDNEY_URETER_ANASTOMOSIS */:
        return "Right Kidney Ureter Anastomosis";
      case "LEFT_KIDNEY_ACCEPTED" /* LEFT_KIDNEY_ACCEPTED */:
        return "Left Kidney Accepted for Recovery";
      case "RIGHT_KIDNEY_ACCEPTED" /* RIGHT_KIDNEY_ACCEPTED */:
        return "Right Kidney Accepted for Recovery";
      case "LEFT_KIDNEY_ALLOCATION_COMPLETE" /* LEFT_KIDNEY_ALLOCATION_COMPLETE */:
        return "Left Kidney Allocation Complete";
      case "RIGHT_KIDNEY_ALLOCATION_COMPLETE" /* RIGHT_KIDNEY_ALLOCATION_COMPLETE */:
        return "Right Kidney Allocation Complete";
      case "LEFT_KIDNEY_NOT_RECOVERED" /* LEFT_KIDNEY_NOT_RECOVERED */:
        return "Left Kidney Not Recovered";
      case "RIGHT_KIDNEY_NOT_RECOVERED" /* RIGHT_KIDNEY_NOT_RECOVERED */:
        return "Right Kidney Not Recovered";
      case "LEFT_KIDNEY_LIST_EXHAUSTED" /* LEFT_KIDNEY_LIST_EXHAUSTED */:
        return "List Exhausted (Left Kidney)";
      case "RIGHT_KIDNEY_LIST_EXHAUSTED" /* RIGHT_KIDNEY_LIST_EXHAUSTED */:
        return "List Exhausted (Right Kidney)";
      case "ORGAN_REMOVAL_PANCREAS" /* ORGAN_REMOVAL_PANCREAS */:
        return "Pancreas Removal";
      case "PANCREAS_PLACED_IN_PANCREASPAK" /* PANCREAS_PLACED_IN_PANCREASPAK */:
        return `Pancreas Placed into ${productName}`;
      case "PANCREAS_REMOVED_FROM_PANCREASPAK" /* PANCREAS_REMOVED_FROM_PANCREASPAK */:
        return `Pancreas Removed from ${productName}`;
      case "EVLP_START" /* EVLP_START */:
        return "EVLP Start";
      case "EVLP_STOP" /* EVLP_STOP */:
        return "EVLP Stop";
      case "CANNULATION" /* CANNULATION */:
        return `Cannulation${cannulationTypeAnnotation}`;
      case "COLD_PRESERVATION_FLUSH" /* COLD_PRESERVATION_FLUSH */:
        return "Cold Preservation Flush";
      case "TIMEOUT" /* TIMEOUT */:
        return "Timeout";
      case "CUSTOM" /* CUSTOM */:
        return "Custom Event";
      case "UNKNOWN" /* UNKNOWN */:
        return "Unknown Event";
      case "START_LOGGER" /* START_LOGGER */:
        return "Logger Started";
      case "STOP_LOGGER" /* STOP_LOGGER */:
        return "Logger Stopped";
      case "PLANE_DEPARTURE_RECIPIENT" /* PLANE_DEPARTURE_RECIPIENT */:
        return "Take-off at Recipient Airport";
      case "PLANE_ARRIVAL_DONOR" /* PLANE_ARRIVAL_DONOR */:
        return "Landed at Donor Airport";
      case "PLANE_DEPARTURE_DONOR" /* PLANE_DEPARTURE_DONOR */:
        return "Take-off at Donor Airport";
      case "PLANE_ARRIVAL_RECIPIENT" /* PLANE_ARRIVAL_RECIPIENT */:
        return "Landed at Recipient Airport";
      case "LEFT_KIDNEY_DECLINED" /* LEFT_KIDNEY_DECLINED */:
        return "Left Kidney Declined";
      case "RIGHT_KIDNEY_DECLINED" /* RIGHT_KIDNEY_DECLINED */:
        return "Right Kidney Declined";
      default:
        return "Unknown Event";
    }
  }
  static getOrganPosition(type) {
    const byPositions = {
      ["LEFT" /* LEFT */]: [
        "LEFT_LUNG_REMOVED_FROM_LUNGGUARD" /* LEFT_LUNG_REMOVED_FROM_LUNGGUARD */,
        "LEFT_LUNG_ANASTOMOSIS_COMPLETE" /* LEFT_LUNG_ANASTOMOSIS_COMPLETE */,
        "RECIPIENT_LEFT_LUNG_OFF_CLAMP" /* RECIPIENT_LEFT_LUNG_OFF_CLAMP */,
        "LEFT_KIDNEY_REMOVAL" /* LEFT_KIDNEY_REMOVAL */,
        "LEFT_KIDNEY_PLACED_ON_ICE" /* LEFT_KIDNEY_PLACED_ON_ICE */,
        "LEFT_KIDNEY_PLACED_INTO_STORAGE" /* LEFT_KIDNEY_PLACED_INTO_STORAGE */,
        "LEFT_KIDNEY_PLACED_IN_THIRD_PARTY_STORAGE" /* LEFT_KIDNEY_PLACED_IN_THIRD_PARTY_STORAGE */,
        "LEFT_KIDNEY_HANDED_OFF_TO_COURIER" /* LEFT_KIDNEY_HANDED_OFF_TO_COURIER */,
        "LEFT_KIDNEY_ARRIVED_AT_TXC_OR_OPO" /* LEFT_KIDNEY_ARRIVED_AT_TXC_OR_OPO */,
        "LEFT_KIDNEY_OUT_OF_STORAGE" /* LEFT_KIDNEY_OUT_OF_STORAGE */,
        "LEFT_KIDNEY_REMOVED_FROM_ICE" /* LEFT_KIDNEY_REMOVED_FROM_ICE */,
        "LEFT_KIDNEY_REMOVED_FROM_THIRD_PARTY_STORAGE" /* LEFT_KIDNEY_REMOVED_FROM_THIRD_PARTY_STORAGE */,
        "LEFT_KIDNEY_ARTERY_ANASTOMOSIS" /* LEFT_KIDNEY_ARTERY_ANASTOMOSIS */,
        "LEFT_KIDNEY_VEIN_ANASTOMOSIS" /* LEFT_KIDNEY_VEIN_ANASTOMOSIS */,
        "LEFT_KIDNEY_URETER_ANASTOMOSIS" /* LEFT_KIDNEY_URETER_ANASTOMOSIS */,
        "LEFT_KIDNEY_ACCEPTED" /* LEFT_KIDNEY_ACCEPTED */,
        "LEFT_KIDNEY_ALLOCATION_COMPLETE" /* LEFT_KIDNEY_ALLOCATION_COMPLETE */,
        "LEFT_KIDNEY_NOT_RECOVERED" /* LEFT_KIDNEY_NOT_RECOVERED */,
        "LEFT_KIDNEY_LIST_EXHAUSTED" /* LEFT_KIDNEY_LIST_EXHAUSTED */,
        "LEFT_KIDNEY_DECLINED" /* LEFT_KIDNEY_DECLINED */
      ],
      ["RIGHT" /* RIGHT */]: [
        "RIGHT_LUNG_REMOVED_FROM_LUNGGUARD" /* RIGHT_LUNG_REMOVED_FROM_LUNGGUARD */,
        "RIGHT_LUNG_ANASTOMOSIS_COMPLETE" /* RIGHT_LUNG_ANASTOMOSIS_COMPLETE */,
        "RECIPIENT_RIGHT_LUNG_OFF_CLAMP" /* RECIPIENT_RIGHT_LUNG_OFF_CLAMP */,
        "RIGHT_KIDNEY_REMOVAL" /* RIGHT_KIDNEY_REMOVAL */,
        "RIGHT_KIDNEY_PLACED_ON_ICE" /* RIGHT_KIDNEY_PLACED_ON_ICE */,
        "RIGHT_KIDNEY_PLACED_INTO_STORAGE" /* RIGHT_KIDNEY_PLACED_INTO_STORAGE */,
        "RIGHT_KIDNEY_PLACED_IN_THIRD_PARTY_STORAGE" /* RIGHT_KIDNEY_PLACED_IN_THIRD_PARTY_STORAGE */,
        "RIGHT_KIDNEY_HANDED_OFF_TO_COURIER" /* RIGHT_KIDNEY_HANDED_OFF_TO_COURIER */,
        "RIGHT_KIDNEY_ARRIVED_AT_TXC_OR_OPO" /* RIGHT_KIDNEY_ARRIVED_AT_TXC_OR_OPO */,
        "RIGHT_KIDNEY_OUT_OF_STORAGE" /* RIGHT_KIDNEY_OUT_OF_STORAGE */,
        "RIGHT_KIDNEY_REMOVED_FROM_ICE" /* RIGHT_KIDNEY_REMOVED_FROM_ICE */,
        "RIGHT_KIDNEY_REMOVED_FROM_THIRD_PARTY_STORAGE" /* RIGHT_KIDNEY_REMOVED_FROM_THIRD_PARTY_STORAGE */,
        "RIGHT_KIDNEY_ARTERY_ANASTOMOSIS" /* RIGHT_KIDNEY_ARTERY_ANASTOMOSIS */,
        "RIGHT_KIDNEY_VEIN_ANASTOMOSIS" /* RIGHT_KIDNEY_VEIN_ANASTOMOSIS */,
        "RIGHT_KIDNEY_URETER_ANASTOMOSIS" /* RIGHT_KIDNEY_URETER_ANASTOMOSIS */,
        "RIGHT_KIDNEY_ACCEPTED" /* RIGHT_KIDNEY_ACCEPTED */,
        "RIGHT_KIDNEY_ALLOCATION_COMPLETE" /* RIGHT_KIDNEY_ALLOCATION_COMPLETE */,
        "RIGHT_KIDNEY_NOT_RECOVERED" /* RIGHT_KIDNEY_NOT_RECOVERED */,
        "RIGHT_KIDNEY_LIST_EXHAUSTED" /* RIGHT_KIDNEY_LIST_EXHAUSTED */,
        "RIGHT_KIDNEY_DECLINED" /* RIGHT_KIDNEY_DECLINED */
      ]
    };
    if (byPositions["LEFT" /* LEFT */].includes(type)) {
      return "LEFT" /* LEFT */;
    }
    if (byPositions["RIGHT" /* RIGHT */].includes(type)) {
      return "RIGHT" /* RIGHT */;
    }
    return null;
  }
  static attributes(type) {
    switch (type) {
      case "HEART_PLACED_IN_SHERPAPAK" /* HEART_PLACED_IN_SHERPAPAK */:
        return ["STARTS_LOGGER" /* STARTS_LOGGER */];
      case "LUNG_PLACED_IN_LUNGGUARD" /* LUNG_PLACED_IN_LUNGGUARD */:
        return ["STARTS_LOGGER" /* STARTS_LOGGER */];
      case "LIVER_PLACED_IN_LIVERGUARD" /* LIVER_PLACED_IN_LIVERGUARD */:
        return ["STARTS_LOGGER" /* STARTS_LOGGER */];
      case "PANCREAS_PLACED_IN_PANCREASPAK" /* PANCREAS_PLACED_IN_PANCREASPAK */:
        return ["STARTS_LOGGER" /* STARTS_LOGGER */];
      case "LEFT_KIDNEY_PLACED_INTO_STORAGE" /* LEFT_KIDNEY_PLACED_INTO_STORAGE */:
        return ["STARTS_LOGGER" /* STARTS_LOGGER */];
      case "RIGHT_KIDNEY_PLACED_INTO_STORAGE" /* RIGHT_KIDNEY_PLACED_INTO_STORAGE */:
        return ["STARTS_LOGGER" /* STARTS_LOGGER */];
      case "HEART_REMOVED_FROM_SHERPAPAK" /* HEART_REMOVED_FROM_SHERPAPAK */:
        return ["STOPS_LOGGER" /* STOPS_LOGGER */];
      case "LUNGS_REMOVED_FROM_LUNGGUARD" /* LUNGS_REMOVED_FROM_LUNGGUARD */:
        return ["STOPS_LOGGER" /* STOPS_LOGGER */];
      case "LIVER_REMOVED_FROM_LIVERGUARD" /* LIVER_REMOVED_FROM_LIVERGUARD */:
        return ["STOPS_LOGGER" /* STOPS_LOGGER */];
      case "PANCREAS_REMOVED_FROM_PANCREASPAK" /* PANCREAS_REMOVED_FROM_PANCREASPAK */:
        return ["STOPS_LOGGER" /* STOPS_LOGGER */];
      case "LEFT_LUNG_REMOVED_FROM_LUNGGUARD" /* LEFT_LUNG_REMOVED_FROM_LUNGGUARD */:
        return ["STOPS_LOGGER" /* STOPS_LOGGER */];
      case "LEFT_KIDNEY_OUT_OF_STORAGE" /* LEFT_KIDNEY_OUT_OF_STORAGE */:
        return ["STOPS_LOGGER" /* STOPS_LOGGER */];
      case "RIGHT_LUNG_REMOVED_FROM_LUNGGUARD" /* RIGHT_LUNG_REMOVED_FROM_LUNGGUARD */:
        return ["STOPS_LOGGER" /* STOPS_LOGGER */];
      case "RIGHT_KIDNEY_OUT_OF_STORAGE" /* RIGHT_KIDNEY_OUT_OF_STORAGE */:
        return ["STOPS_LOGGER" /* STOPS_LOGGER */];
      default:
        return [];
    }
  }
};

// src/models/cannulation-type.ts
var CannulationTypeUtil = class {
  static toUIString(type) {
    switch (type) {
      case "TANRP" /* TANRP */:
        return "TA-NRP";
      case "ANRP" /* ANRP */:
        return "A-NRP";
      default:
        return "unknown";
    }
  }
};

// src/models/messages/message-preview.ts
var MessagePreview = class {
  id;
  content;
  senderId;
  timestamp;
  attachments;
  constructor(params) {
    this.id = params.id;
    this.content = params.content;
    this.senderId = params.senderId;
    this.timestamp = toDate(params.timestamp) || /* @__PURE__ */ new Date();
    this.attachments = params.attachments;
  }
  toJSON() {
    return {
      id: this.id,
      content: this.content,
      senderId: this.senderId,
      timestamp: this.timestamp,
      attachments: this.attachments
    };
  }
  serialize() {
    return JSON.parse(JSON.stringify(this));
  }
};

// src/models/chat/chat-settings.ts
var ChatSettings = class {
  allowNewMembers;
  adminOnly;
  disappearingMessages;
  constructor(params) {
    this.allowNewMembers = params.allowNewMembers;
    this.adminOnly = params.adminOnly;
    this.disappearingMessages = params.disappearingMessages;
  }
  toJSON() {
    return {
      allowNewMembers: this.allowNewMembers,
      adminOnly: this.adminOnly,
      disappearingMessages: this.disappearingMessages
    };
  }
  serialize() {
    return JSON.parse(JSON.stringify(this));
  }
};

// src/models/chat/chat.ts
var Chat = class {
  id;
  sessionId;
  type;
  name;
  description;
  avatar;
  memberIds;
  activeMemberIds;
  createdAt;
  createdBy;
  updatedAt;
  lastMessage;
  settings;
  adminIds;
  archived;
  mutedBy;
  organizationId;
  meta;
  constructor(params) {
    this.id = params.id;
    this.sessionId = params.sessionId;
    this.type = params.type;
    this.name = params.name;
    this.description = params.description;
    this.avatar = params.avatar;
    this.memberIds = params.memberIds ?? [];
    this.activeMemberIds = params.activeMemberIds ?? [];
    this.createdBy = params.createdBy;
    this.adminIds = params.adminIds ?? [];
    this.archived = params.archived ?? false;
    this.mutedBy = params.mutedBy ?? [];
    this.organizationId = params.organizationId;
    this.meta = params.meta;
    this.lastMessage = params.lastMessage ? new MessagePreview(params.lastMessage) : void 0;
    this.settings = params.settings ? new ChatSettings(params.settings) : new ChatSettings({
      allowNewMembers: true,
      adminOnly: false,
      disappearingMessages: void 0
    });
    this.createdAt = toDate(params.createdAt) || /* @__PURE__ */ new Date();
    this.updatedAt = toDate(params.updatedAt);
  }
  toJSON() {
    return {
      id: this.id,
      sessionId: this.sessionId,
      type: this.type,
      name: this.name,
      description: this.description,
      avatar: this.avatar,
      memberIds: this.memberIds,
      activeMemberIds: this.activeMemberIds,
      createdAt: this.createdAt,
      createdBy: this.createdBy,
      updatedAt: this.updatedAt,
      lastMessage: this.lastMessage?.toJSON(),
      settings: this.settings.toJSON(),
      adminIds: this.adminIds,
      archived: this.archived,
      mutedBy: this.mutedBy,
      organizationId: this.organizationId,
      meta: this.meta
    };
  }
  serialize() {
    return JSON.parse(JSON.stringify(this));
  }
};
var ChatConverter = {
  toFirestore() {
    return {};
  },
  fromFirestore(doc42) {
    const data = doc42.data();
    return new Chat({
      ...data,
      id: doc42.id,
      createdAt: data.createdAt?.toDate(),
      updatedAt: data.updatedAt?.toDate(),
      lastMessage: data.lastMessage ? {
        ...data.lastMessage,
        timestamp: data.lastMessage.timestamp?.toDate()
      } : void 0
    });
  }
};

// src/models/chat/chat-member.ts
var ChatMember = class {
  userId;
  firstname;
  lastname;
  organizationId;
  organizationName;
  organizationRole;
  chatRole;
  joinedAt;
  leftAt;
  addedBy;
  constructor(params) {
    this.userId = params.userId;
    this.firstname = params.firstname;
    this.lastname = params.lastname;
    this.organizationId = params.organizationId;
    this.organizationName = params.organizationName;
    this.organizationRole = params.organizationRole;
    this.chatRole = params.chatRole;
    this.addedBy = params.addedBy;
    this.joinedAt = toDate(params.joinedAt) || /* @__PURE__ */ new Date();
    this.leftAt = toDate(params.leftAt);
  }
  toJSON() {
    return {
      userId: this.userId,
      firstname: this.firstname,
      lastname: this.lastname,
      organizationId: this.organizationId,
      organizationName: this.organizationName,
      organizationRole: this.organizationRole,
      chatRole: this.chatRole,
      joinedAt: this.joinedAt,
      leftAt: this.leftAt,
      addedBy: this.addedBy
    };
  }
  serialize() {
    return JSON.parse(JSON.stringify(this));
  }
};
var ChatMemberConverter = {
  toFirestore() {
    return {};
  },
  fromFirestore(doc42) {
    const data = doc42.data();
    return new ChatMember({
      ...data,
      userId: doc42.id,
      joinedAt: data.joinedAt?.toDate(),
      leftAt: data.leftAt?.toDate()
    });
  }
};

// src/models/cursor.ts
var ReadCursor = class {
  documentId;
  readAt;
  constructor(params) {
    this.documentId = params.documentId;
    this.readAt = toDate(params.readAt);
  }
  toJSON() {
    return {
      documentId: this.documentId,
      readAt: this.readAt
    };
  }
  serialize() {
    return JSON.parse(JSON.stringify(this));
  }
};

// src/models/chat/user-chat.ts
var UserChat = class {
  userId;
  chatId;
  sessionId;
  type;
  name;
  displayName;
  avatar;
  memberIds;
  activeMemberIds;
  lastMessage;
  lastActivity;
  lastActivityAt;
  unreadCount;
  lastReadCursor;
  muted;
  mutedUntil;
  pinned;
  archived;
  organizationId;
  createdAt;
  updatedAt;
  constructor(params) {
    this.userId = params.userId;
    this.chatId = params.chatId;
    this.sessionId = params.sessionId;
    this.type = params.type;
    this.name = params.name;
    this.displayName = params.displayName;
    this.avatar = params.avatar;
    this.memberIds = params.memberIds ?? [];
    this.activeMemberIds = params.activeMemberIds ?? [];
    this.lastActivity = params.lastActivity;
    this.lastActivityAt = toDate(params.lastActivityAt);
    this.unreadCount = params.unreadCount ?? 0;
    this.lastReadCursor = params.lastReadCursor ? new ReadCursor(params.lastReadCursor) : void 0;
    this.muted = params.muted ?? false;
    this.pinned = params.pinned ?? false;
    this.archived = params.archived ?? false;
    this.organizationId = params.organizationId;
    this.lastMessage = params.lastMessage ? new MessagePreview(params.lastMessage) : void 0;
    this.mutedUntil = toDate(params.mutedUntil);
    this.createdAt = toDate(params.createdAt) || /* @__PURE__ */ new Date();
    this.updatedAt = toDate(params.updatedAt);
  }
  toJSON() {
    return {
      userId: this.userId,
      chatId: this.chatId,
      sessionId: this.sessionId,
      type: this.type,
      name: this.name,
      displayName: this.displayName,
      avatar: this.avatar,
      memberIds: this.memberIds,
      activeMemberIds: this.activeMemberIds,
      lastMessage: this.lastMessage?.toJSON(),
      lastActivity: this.lastActivity,
      lastActivityAt: this.lastActivityAt,
      unreadCount: this.unreadCount,
      lastReadCursor: this.lastReadCursor?.toJSON(),
      muted: this.muted,
      mutedUntil: this.mutedUntil,
      pinned: this.pinned,
      archived: this.archived,
      organizationId: this.organizationId,
      createdAt: this.createdAt,
      updatedAt: this.updatedAt
    };
  }
  serialize() {
    return JSON.parse(JSON.stringify(this));
  }
};
var UserChatConverter = {
  toFirestore() {
    return {};
  },
  fromFirestore(doc42) {
    const data = doc42.data();
    return new UserChat({
      ...data,
      chatId: doc42.id,
      lastReadCursor: data.lastReadCursor ? { documentId: data.lastReadCursor.documentId, readAt: data.lastReadCursor.readAt?.toDate() } : void 0,
      mutedUntil: data.mutedUntil?.toDate(),
      createdAt: data.createdAt?.toDate(),
      updatedAt: data.updatedAt?.toDate(),
      lastActivityAt: data.lastActivityAt?.toDate(),
      lastMessage: data.lastMessage ? {
        ...data.lastMessage,
        timestamp: data.lastMessage.timestamp?.toDate()
      } : void 0
    });
  }
};

// src/models/logger-log-entry.ts
var LoggerLogEntry2 = class {
  /** Timestamp of the log entry */
  timestamp;
  /** Event type (e.g., 'reading', 'started', 'stopped') */
  event;
  /** Sensor data */
  data;
  constructor(params) {
    this.timestamp = params.timestamp instanceof Date ? params.timestamp : new Date(params.timestamp);
    this.event = params.event;
    this.data = params.data ?? {};
  }
  /** Whether this is a sensor data reading (vs event like started/stopped) */
  get isReading() {
    return this.event === "reading" || this.event == null;
  }
  toJSON() {
    return {
      timestamp: this.timestamp,
      event: this.event,
      data: this.data
    };
  }
  serialize() {
    return JSON.parse(JSON.stringify(this));
  }
};

// src/models/deprecated/organ-reading.ts
var OrganReading = class {
  data;
  event;
  timestamp;
  constructor(params) {
    this.data = params.data;
    this.event = params.event;
    this.timestamp = typeof params.timestamp === "string" ? new Date(params.timestamp) : params.timestamp;
  }
  get isReading() {
    return this.event === "reading";
  }
  get isStartedEvent() {
    return this.event === "started";
  }
  get isStoppedEvent() {
    return this.event === "stopped";
  }
  /** Converts this deprecated OrganReading to a v7 LoggerLogEntry. */
  toLoggerLogEntry() {
    return new LoggerLogEntry2({
      timestamp: this.timestamp,
      event: this.event,
      data: {
        probe: this.data?.probe,
        ambient: this.data?.ambient,
        pressure: this.data?.pressure,
        pumpVoltage: this.data?.pumpVoltage,
        fluidPressure: this.data?.fluidPressure,
        flow: this.data?.flow,
        resistance: this.data?.resistance,
        averageFluidPressure: this.data?.averageFluidPressure,
        averageFlow: this.data?.averageFlow,
        averageResistance: this.data?.averageResistance,
        rssi: this.data?.rssi
      }
    });
  }
  toJSON() {
    return {
      data: this.data,
      event: this.event,
      timestamp: this.timestamp
    };
  }
  serialize() {
    return JSON.parse(JSON.stringify(this));
  }
};

// src/models/deprecated/reading.ts
var Reading = class {
  id;
  event;
  ambient;
  probe;
  pressure;
  pumpVoltage;
  timestamp;
  constructor(params) {
    this.id = params.id;
    this.event = params.event;
    this.ambient = params.ambient;
    this.probe = params.probe;
    this.pressure = params.pressure;
    this.pumpVoltage = params.pumpVoltage;
    this.timestamp = typeof params.timestamp === "string" ? new Date(params.timestamp) : params.timestamp;
  }
  get isReading() {
    return this.event === "reading";
  }
  get isStartedEvent() {
    return this.event === "started";
  }
  get isStoppedEvent() {
    return this.event === "stopped";
  }
  toOrganReading() {
    return new OrganReading({
      data: {
        ambient: this.ambient,
        probe: this.probe,
        pressure: this.pressure,
        pumpVoltage: this.pumpVoltage
      },
      event: this.event,
      timestamp: this.timestamp
    });
  }
  /** Converts this deprecated Reading to a v7 LoggerLogEntry. */
  toLoggerLogEntry() {
    return new LoggerLogEntry2({
      timestamp: this.timestamp,
      event: this.event,
      data: {
        probe: this.probe,
        ambient: this.ambient,
        pressure: this.pressure,
        pumpVoltage: this.pumpVoltage
      }
    });
  }
  toJSON() {
    return {
      event: this.event,
      ambient: this.ambient,
      probe: this.probe,
      pressure: this.pressure,
      pumpVoltage: this.pumpVoltage,
      timestamp: this.timestamp
    };
  }
  toReadingCompressed() {
    return new ReadingCompressed({
      e: this.event,
      a: this.ambient,
      p: this.probe,
      pr: this.pressure,
      pv: this.pumpVoltage,
      t: this.timestamp
    });
  }
  serialize() {
    return JSON.parse(JSON.stringify(this));
  }
};

// src/models/deprecated/reading-compressed.ts
var ReadingCompressed = class {
  e;
  a;
  p;
  pr;
  pv;
  t;
  constructor(params) {
    this.e = params.e;
    this.a = params.a;
    this.p = params.p;
    this.pr = params.pr;
    this.pv = params.pv;
    this.t = typeof params.t === "string" ? new Date(params.t) : params.t;
  }
  toJSON() {
    return {
      e: this.e,
      a: this.a,
      p: this.p,
      pr: this.pr,
      pv: this.pv,
      t: this.t
    };
  }
  serialize() {
    return JSON.parse(JSON.stringify(this));
  }
  toReading() {
    return new Reading({
      event: this.e,
      ambient: this.a,
      probe: this.p,
      pressure: this.pr,
      pumpVoltage: this.pv,
      timestamp: this.t
    });
  }
};

// src/models/deprecated/log.ts
var Log = class {
  readings;
  constructor(params) {
    this.readings = params.readings ? params.readings.map((reading) => new ReadingCompressed(reading).toReading()) : [];
  }
  toJSON() {
    return {
      readings: this.readings.map((reading) => reading.toJSON())
    };
  }
  serialize() {
    return JSON.parse(JSON.stringify(this));
  }
};
var LogConverter = {
  toFirestore() {
    return {};
  },
  fromFirestore(doc42) {
    const data = doc42.data();
    return new Log({
      readings: data.log?.map((reading) => new ReadingCompressed({ ...reading, t: reading.t?.toDate() })) ?? []
    });
  }
};

// src/models/logger-app-location.ts
var LoggerAppLocation = class {
  /** Document ID */
  id;
  /** Timestamp of the location reading */
  timestamp;
  /** Latitude in decimal degrees */
  latitude;
  /** Longitude in decimal degrees */
  longitude;
  /** Horizontal accuracy in meters */
  horizontalAccuracy;
  /** Vertical accuracy in meters */
  verticalAccuracy;
  /** Course/heading in degrees (0-360) */
  course;
  /** Speed in meters per second */
  speed;
  constructor(params) {
    this.id = params.id;
    this.timestamp = params.timestamp instanceof Date ? params.timestamp : new Date(params.timestamp);
    this.latitude = params.latitude;
    this.longitude = params.longitude;
    this.horizontalAccuracy = params.horizontalAccuracy;
    this.verticalAccuracy = params.verticalAccuracy;
    this.course = params.course;
    this.speed = params.speed;
  }
  toJSON() {
    return {
      id: this.id,
      timestamp: this.timestamp,
      latitude: this.latitude,
      longitude: this.longitude,
      horizontalAccuracy: this.horizontalAccuracy,
      verticalAccuracy: this.verticalAccuracy,
      course: this.course,
      speed: this.speed
    };
  }
  serialize() {
    return JSON.parse(JSON.stringify(this));
  }
};
var LoggerAppLocationConverter = {
  toFirestore() {
    return {};
  },
  fromFirestore(doc42) {
    const data = doc42.data();
    return new LoggerAppLocation({
      ...data,
      id: doc42.id,
      timestamp: data.timestamp?.toDate()
    });
  }
};

// src/models/deprecated/logger-location.ts
var LoggerLocation = class {
  latitude;
  longitude;
  horizontalAccuracy;
  verticalAccuracy;
  timestamp;
  constructor(params) {
    this.latitude = params.latitude;
    this.longitude = params.longitude;
    this.horizontalAccuracy = params.horizontalAccuracy ?? -1;
    this.verticalAccuracy = params.verticalAccuracy ?? -1;
    this.timestamp = params.timestamp instanceof Date ? params.timestamp : new Date(params.timestamp);
  }
  /** Converts this deprecated LoggerLocation to a v7 LoggerAppLocation. */
  toLoggerAppLocation() {
    return new LoggerAppLocation({
      latitude: this.latitude,
      longitude: this.longitude,
      horizontalAccuracy: this.horizontalAccuracy,
      verticalAccuracy: this.verticalAccuracy,
      timestamp: this.timestamp
    });
  }
  toJSON() {
    return {
      latitude: this.latitude,
      longitude: this.longitude,
      horizontalAccuracy: this.horizontalAccuracy,
      verticalAccuracy: this.verticalAccuracy,
      timestamp: this.timestamp
    };
  }
  serialize() {
    return JSON.parse(JSON.stringify(this));
  }
};
var LoggerLocationConverter = {
  toFirestore() {
    return {};
  },
  fromFirestore(doc42) {
    const data = doc42.data();
    return new LoggerLocation({ ...data, id: doc42.id, timestamp: data.timestamp?.toDate() });
  }
};

// src/models/logger-connection.ts
var LoggerConnection = class {
  /** User ID of the connected user */
  userId;
  /** Session ID where the logger is connected */
  sessionId;
  /** UUID of the paired mobile device */
  pairedDeviceIdentifier;
  /** App version used for connection */
  appVersion;
  /** Device model name */
  device;
  /** Operating system name */
  os;
  /** Operating system version */
  osVersion;
  constructor(params) {
    this.userId = params.userId ?? params.connectedBy ?? "";
    this.sessionId = params.sessionId;
    this.pairedDeviceIdentifier = params.pairedDeviceIdentifier;
    this.appVersion = params.appVersion;
    this.device = params.device;
    this.os = params.os;
    this.osVersion = params.osVersion;
  }
  /**
   * Converts logger connection to a plain JSON object.
   * @returns Plain object representation of the connection state
   */
  toJSON() {
    return {
      userId: this.userId,
      sessionId: this.sessionId,
      pairedDeviceIdentifier: this.pairedDeviceIdentifier,
      appVersion: this.appVersion,
      device: this.device,
      os: this.os,
      osVersion: this.osVersion
    };
  }
  /**
   * Serializes logger connection to a JSON-safe object (removes class instances).
   * @returns JSON-safe plain object
   */
  serialize() {
    return JSON.parse(JSON.stringify(this));
  }
};

// src/models/logger-data.ts
var LoggerData = class {
  /** Probe temperature in degrees Celsius */
  probe;
  /** Ambient temperature in degrees Celsius */
  ambient;
  /** Barometric pressure in hPa/mbar (BAROguard only) */
  pressure;
  /** Battery voltage reading */
  batteryVoltage;
  /** Pump voltage reading (lung/BAROguard) */
  pumpVoltage;
  /** Raw fluid pressure reading (kidney) */
  fluidPressure;
  /** Raw flow rate reading (kidney) */
  flow;
  /** Raw resistance reading (kidney) */
  resistance;
  /** Averaged fluid pressure reading (kidney) */
  averageFluidPressure;
  /** Averaged flow rate reading (kidney) */
  averageFlow;
  /** Averaged resistance reading (kidney) */
  averageResistance;
  /** Bluetooth signal strength */
  rssi;
  /** Timestamp of the reading */
  timestamp;
  constructor(params) {
    this.probe = params.probe;
    this.ambient = params.ambient;
    this.pressure = params.pressure;
    this.batteryVoltage = params.batteryVoltage;
    this.pumpVoltage = params.pumpVoltage;
    this.fluidPressure = params.fluidPressure;
    this.flow = params.flow;
    this.resistance = params.resistance;
    this.averageFluidPressure = params.averageFluidPressure;
    this.averageFlow = params.averageFlow;
    this.averageResistance = params.averageResistance;
    this.rssi = params.rssi;
    this.timestamp = params.timestamp ? params.timestamp instanceof Date ? params.timestamp : new Date(params.timestamp) : void 0;
  }
  /**
   * Converts logger data to a plain JSON object.
   * @returns Plain object representation of the sensor data
   */
  toJSON() {
    return {
      probe: this.probe,
      ambient: this.ambient,
      pressure: this.pressure,
      batteryVoltage: this.batteryVoltage,
      pumpVoltage: this.pumpVoltage,
      fluidPressure: this.fluidPressure,
      flow: this.flow,
      resistance: this.resistance,
      averageFluidPressure: this.averageFluidPressure,
      averageFlow: this.averageFlow,
      averageResistance: this.averageResistance,
      rssi: this.rssi,
      timestamp: this.timestamp
    };
  }
  /**
   * Serializes logger data to a JSON-safe object (removes class instances).
   * @returns JSON-safe plain object
   */
  serialize() {
    return JSON.parse(JSON.stringify(this));
  }
};

// src/models/logger-data-range-edit-bound.ts
var LoggerDataRangeEditBound = class {
  /** Timeline entry type this bound is anchored to */
  timelineEntryType;
  /** Original timestamp from the timeline entry */
  timestamp;
  /** Manual override timestamp (takes precedence over timestamp) */
  overrideTimestamp;
  constructor(params) {
    this.timelineEntryType = params.timelineEntryType;
    this.timestamp = params.timestamp ? params.timestamp instanceof Date ? params.timestamp : new Date(params.timestamp) : void 0;
    this.overrideTimestamp = params.overrideTimestamp instanceof Date ? params.overrideTimestamp : params.overrideTimestamp ? new Date(params.overrideTimestamp) : void 0;
  }
  toJSON() {
    return {
      timelineEntryType: this.timelineEntryType,
      timestamp: this.timestamp,
      overrideTimestamp: this.overrideTimestamp
    };
  }
  serialize() {
    return JSON.parse(JSON.stringify(this));
  }
};

// src/models/logger-data-range-edit-trim.ts
var LoggerDataRangeEditTrim = class {
  /** Start of the excluded time range (inclusive) */
  from;
  /** End of the excluded time range (inclusive) */
  to;
  constructor(params) {
    this.from = params.from ? params.from instanceof Date ? params.from : new Date(params.from) : void 0;
    this.to = params.to ? params.to instanceof Date ? params.to : new Date(params.to) : void 0;
  }
  toJSON() {
    return {
      from: this.from,
      to: this.to
    };
  }
  serialize() {
    return JSON.parse(JSON.stringify(this));
  }
};

// src/models/logger-data-range-edit.ts
var LoggerDataRangeEdit = class {
  /** Start and end bounds for the visible data range */
  bounds;
  /** Time ranges to exclude from report calculations */
  trims;
  /** Sensor reading interval override in seconds (overrides product-based default) */
  intervalOverride;
  constructor(params) {
    this.bounds = params.bounds ? {
      from: params.bounds.from.map((bound) => new LoggerDataRangeEditBound(bound)),
      to: params.bounds.to.map((bound) => new LoggerDataRangeEditBound(bound))
    } : {
      from: [],
      to: []
    };
    this.trims = params.trims?.map((trim) => new LoggerDataRangeEditTrim(trim)) ?? [];
    this.intervalOverride = params.intervalOverride;
  }
  /**
   * Resolves the outermost effective start and end bounds.
   *
   * Filters out bounds without any timestamp, then sorts ascending
   * to return the earliest bound from each side. Override timestamps
   * take precedence over original timestamps.
   *
   * @returns The earliest `from` and `to` bounds with valid timestamps
   */
  get outerBounds() {
    const filteredFromBounds = this.bounds.from.filter((b) => b.timestamp != null || b.overrideTimestamp != null);
    const filteredToBounds = this.bounds.to.filter((b) => b.timestamp != null || b.overrideTimestamp != null);
    const from = filteredFromBounds.sort((a, b) => {
      const aTime = (a.overrideTimestamp ?? a.timestamp).getTime();
      const bTime = (b.overrideTimestamp ?? b.timestamp).getTime();
      return aTime - bTime;
    })[0];
    const to = filteredToBounds.sort((a, b) => {
      const aTime = (a.overrideTimestamp ?? a.timestamp).getTime();
      const bTime = (b.overrideTimestamp ?? b.timestamp).getTime();
      return aTime - bTime;
    })[0];
    return {
      from,
      to
    };
  }
  toJSON() {
    return {
      bounds: {
        from: this.bounds.from.map((bound) => bound.toJSON()),
        to: this.bounds.to.map((bound) => bound.toJSON())
      },
      trims: this.trims.map((trim) => trim.toJSON()),
      intervalOverride: this.intervalOverride
    };
  }
  serialize() {
    return JSON.parse(JSON.stringify(this));
  }
};

// src/models/logger-header.ts
var LoggerHeader = class {
  /** Launcher application identifier */
  launcher;
  /** Logging interval in seconds */
  interval;
  /** Start delay in seconds */
  delay;
  /** Time offset */
  offset;
  /** Whether daylight saving time was active */
  isDst;
  /** Firmware version (number from Firestore, string for display) */
  firmwareVersion;
  /** Number of times this logger has been deployed */
  deploymentCount;
  /** Time zone identifier (e.g. 'America/New_York') */
  timeZone;
  /** Date the logger configuration was created */
  creationDate;
  /** Date the logger started logging */
  startDate;
  constructor(params) {
    this.launcher = params.launcher;
    this.interval = params.interval;
    this.delay = params.delay;
    this.offset = params.offset;
    this.isDst = params.isDst;
    this.firmwareVersion = params.firmwareVersion;
    this.deploymentCount = params.deploymentCount;
    this.timeZone = params.timeZone;
    this.creationDate = typeof params.creationDate === "string" ? new Date(params.creationDate) : params.creationDate;
    this.startDate = typeof params.startDate === "string" ? new Date(params.startDate) : params.startDate;
  }
  /**
   * Converts logger header to a plain JSON object.
   * @returns Plain object representation of the logger header
   */
  toJSON() {
    return {
      launcher: this.launcher,
      interval: this.interval,
      delay: this.delay,
      offset: this.offset,
      isDst: this.isDst,
      firmwareVersion: this.firmwareVersion,
      deploymentCount: this.deploymentCount,
      timeZone: this.timeZone,
      creationDate: this.creationDate,
      startDate: this.startDate
    };
  }
  /**
   * Serializes logger header to a JSON-safe object (removes class instances).
   * @returns JSON-safe plain object
   */
  serialize() {
    return JSON.parse(JSON.stringify(this));
  }
};

// src/models/session-report-data.ts
var SessionReportData = class {
  ambientAverage;
  ambientMaximum;
  ambientMinimum;
  probeAverage;
  probeMaximum;
  probeMinimum;
  pressureAverage;
  pressureMaximum;
  pressureMinimum;
  averageFluidPressureAverage;
  averageFluidPressureMaximum;
  averageFluidPressureMinimum;
  averageFlowAverage;
  averageFlowMinimum;
  averageFlowMaximum;
  averageResistanceAverage;
  averageResistanceMinimum;
  averageResistanceMaximum;
  pumpVoltageStart;
  pumpVoltageEnd;
  distance;
  distanceTotal;
  distanceDataSource;
  readingsCount;
  readingsCountTotal;
  loggerStartedAt;
  loggerStoppedAt;
  loggingDurationMillis;
  firstReading;
  lastReading;
  timeOnPump;
  timeInTransport;
  constructor(params) {
    this.ambientAverage = params.ambientAverage;
    this.ambientMaximum = params.ambientMaximum;
    this.ambientMinimum = params.ambientMinimum;
    this.probeAverage = params.probeAverage;
    this.probeMaximum = params.probeMaximum;
    this.probeMinimum = params.probeMinimum;
    this.pressureAverage = params.pressureAverage;
    this.pressureMaximum = params.pressureMaximum;
    this.pressureMinimum = params.pressureMinimum;
    this.averageFluidPressureAverage = params.averageFluidPressureAverage;
    this.averageFluidPressureMaximum = params.averageFluidPressureMaximum;
    this.averageFluidPressureMinimum = params.averageFluidPressureMinimum;
    this.averageFlowAverage = params.averageFlowAverage;
    this.averageFlowMaximum = params.averageFlowMaximum;
    this.averageFlowMinimum = params.averageFlowMinimum;
    this.averageResistanceAverage = params.averageResistanceAverage;
    this.averageResistanceMaximum = params.averageResistanceMaximum;
    this.averageResistanceMinimum = params.averageResistanceMinimum;
    this.pumpVoltageStart = params.pumpVoltageStart;
    this.pumpVoltageEnd = params.pumpVoltageEnd;
    this.distance = params.distance;
    this.distanceTotal = params.distanceTotal;
    this.distanceDataSource = params.distanceDataSource;
    this.readingsCount = params.readingsCount;
    this.readingsCountTotal = params.readingsCountTotal;
    this.loggingDurationMillis = params.loggingDurationMillis;
    this.timeInTransport = params.timeInTransport;
    this.loggerStartedAt = params.loggerStartedAt instanceof Date ? params.loggerStartedAt : params.loggerStartedAt ? new Date(params.loggerStartedAt) : void 0;
    this.loggerStoppedAt = params.loggerStoppedAt instanceof Date ? params.loggerStoppedAt : params.loggerStoppedAt ? new Date(params.loggerStoppedAt) : void 0;
    this.firstReading = params.firstReading instanceof Date ? params.firstReading : params.firstReading ? new Date(params.firstReading) : void 0;
    this.lastReading = params.lastReading instanceof Date ? params.lastReading : params.lastReading ? new Date(params.lastReading) : void 0;
    if (params.timeOnPump) {
      const startTimestamp = params.timeOnPump.startTimestamp instanceof Date ? params.timeOnPump.startTimestamp : new Date(params.timeOnPump.startTimestamp);
      const endTimestamp = params.timeOnPump.endTimestamp instanceof Date ? params.timeOnPump.endTimestamp : params.timeOnPump.endTimestamp ? new Date(params.timeOnPump.endTimestamp) : void 0;
      this.timeOnPump = {
        ...params.timeOnPump,
        startTimestamp,
        endTimestamp
      };
    }
  }
  toJSON() {
    return {
      ambientAverage: this.ambientAverage,
      ambientMaximum: this.ambientMaximum,
      ambientMinimum: this.ambientMinimum,
      probeAverage: this.probeAverage,
      probeMaximum: this.probeMaximum,
      probeMinimum: this.probeMinimum,
      pressureAverage: this.pressureAverage,
      pressureMaximum: this.pressureMaximum,
      pressureMinimum: this.pressureMinimum,
      averageFluidPressureAverage: this.averageFluidPressureAverage,
      averageFluidPressureMaximum: this.averageFluidPressureMaximum,
      averageFluidPressureMinimum: this.averageFluidPressureMinimum,
      averageFlowAverage: this.averageFlowAverage,
      averageFlowMaximum: this.averageFlowMaximum,
      averageFlowMinimum: this.averageFlowMinimum,
      averageResistanceAverage: this.averageResistanceAverage,
      averageResistanceMaximum: this.averageResistanceMaximum,
      averageResistanceMinimum: this.averageResistanceMinimum,
      pumpVoltageStart: this.pumpVoltageStart,
      pumpVoltageEnd: this.pumpVoltageEnd,
      distance: this.distance,
      distanceTotal: this.distanceTotal,
      distanceDataSource: this.distanceDataSource,
      readingsCount: this.readingsCount,
      readingsCountTotal: this.readingsCountTotal,
      loggerStartedAt: this.loggerStartedAt,
      loggerStoppedAt: this.loggerStoppedAt,
      loggingDurationMillis: this.loggingDurationMillis,
      firstReading: this.firstReading,
      lastReading: this.lastReading,
      timeOnPump: this.timeOnPump,
      timeInTransport: this.timeInTransport
    };
  }
  serialize() {
    return JSON.parse(JSON.stringify(this));
  }
};

// src/models/logger.ts
var Logger = class {
  /** Unique logger identifier (Firestore document ID) */
  id;
  /** Logger serial number (from physical device) */
  serialNo;
  /** Logger type (CX402, MX1105, CX460) */
  type;
  /** Passkey for logger pairing */
  passkey;
  /** Whether the logger data is publicly accessible via shared session */
  shared;
  /** Logger state */
  state;
  /** Product type */
  product;
  /** Logger configuration and firmware info */
  header;
  /** Current/last sensor data */
  data;
  /** Array of session IDs this logger has been used with */
  linkedSessions;
  /** Denormalized user IDs from all linked sessions (for security rules) */
  userIds;
  /** Denormalized organization IDs from all linked sessions (for security rules) */
  organizationIds;
  /** Denormalized area IDs from all linked sessions (for security rules) */
  areaIds;
  /** Denormalized territory IDs from all linked sessions (for security rules) */
  territoryIds;
  /** Active connection state (undefined when disconnected) */
  connection;
  /** Tive tracker ID for advanced tracking (v7+) */
  advancedTrackerId;
  /** Report data for external/customer reports */
  reportData;
  /** Report data for internal Paragonix use */
  internalReportData;
  /** Manual data range edits (trims, bounds) applied to readings */
  dataRangeEdit;
  /** Document creation timestamp */
  createdAt;
  /** Document last update timestamp */
  updatedAt;
  constructor(params) {
    this.id = params.id;
    this.serialNo = params.serialNo;
    this.type = params.type;
    this.passkey = params.passkey;
    this.shared = params.shared;
    this.state = params.state;
    this.product = params.product;
    this.header = params.header ? new LoggerHeader(params.header) : void 0;
    this.data = params.data ? new LoggerData(params.data) : void 0;
    this.linkedSessions = params.linkedSessions ?? [];
    this.userIds = params.userIds ?? [];
    this.organizationIds = params.organizationIds ?? [];
    this.areaIds = params.areaIds ?? [];
    this.territoryIds = params.territoryIds ?? [];
    this.connection = params.connection ? new LoggerConnection(params.connection) : void 0;
    this.advancedTrackerId = params.advancedTrackerId;
    this.reportData = params.reportData ? new SessionReportData(params.reportData) : void 0;
    this.internalReportData = params.internalReportData ? new SessionReportData(params.internalReportData) : void 0;
    this.dataRangeEdit = params.dataRangeEdit ? new LoggerDataRangeEdit(params.dataRangeEdit) : void 0;
    this.createdAt = params.createdAt instanceof Date ? params.createdAt : new Date(params.createdAt);
    this.updatedAt = params.updatedAt ? params.updatedAt instanceof Date ? params.updatedAt : new Date(params.updatedAt) : void 0;
  }
  toJSON() {
    return {
      id: this.id,
      serialNo: this.serialNo,
      type: this.type,
      passkey: this.passkey,
      shared: this.shared,
      state: this.state,
      product: this.product,
      header: this.header?.toJSON(),
      data: this.data?.toJSON(),
      linkedSessions: this.linkedSessions,
      userIds: this.userIds,
      organizationIds: this.organizationIds,
      areaIds: this.areaIds,
      territoryIds: this.territoryIds,
      connection: this.connection?.toJSON(),
      advancedTrackerId: this.advancedTrackerId,
      reportData: this.reportData?.toJSON(),
      internalReportData: this.internalReportData?.toJSON(),
      dataRangeEdit: this.dataRangeEdit?.toJSON(),
      createdAt: this.createdAt,
      updatedAt: this.updatedAt
    };
  }
  serialize() {
    return JSON.parse(JSON.stringify(this));
  }
};
var LoggerConverter = {
  toFirestore() {
    return {};
  },
  fromFirestore(doc42) {
    const data = doc42.data();
    return new Logger({
      ...data,
      id: doc42.id,
      data: data.data ? {
        ...data.data,
        timestamp: data.data.timestamp?.toDate?.() ?? data.data.timestamp
      } : void 0,
      header: data.header ? {
        ...data.header,
        creationDate: data.header.creationDate?.toDate?.() ?? data.header.creationDate,
        startDate: data.header.startDate?.toDate?.() ?? data.header.startDate
      } : void 0,
      connection: data.connection ? {
        ...data.connection
      } : void 0,
      reportData: parseSessionReportData(data.reportData),
      internalReportData: parseSessionReportData(data.internalReportData),
      dataRangeEdit: parseDataRangeEdit(data.dataRangeEdit),
      createdAt: data.createdAt?.toDate(),
      updatedAt: data.updatedAt?.toDate()
    });
  }
};
function parseSessionReportData(data) {
  return data ? {
    ...data,
    loggerStartedAt: data.loggerStartedAt?.toDate(),
    loggerStoppedAt: data.loggerStoppedAt?.toDate(),
    firstReading: data.firstReading?.toDate(),
    lastReading: data.lastReading?.toDate(),
    timeOnPump: data.timeOnPump ? {
      ...data.timeOnPump,
      startTimestamp: data.timeOnPump.startTimestamp?.toDate(),
      endTimestamp: data.timeOnPump.endTimestamp?.toDate()
    } : void 0
  } : void 0;
}
function parseDataRangeEdit(data) {
  return data ? {
    ...data,
    bounds: {
      from: data.bounds.from ? parseDataRangeEditBounds(data.bounds.from) : [],
      to: data.bounds.to ? parseDataRangeEditBounds(data.bounds.to) : []
    },
    trims: data.trims ? parseDataRangeEditTrims(data.trims) : void 0
  } : void 0;
}
function parseDataRangeEditBounds(data) {
  return data ? data.map((bound) => parseDataRangeEditBound(bound)) : void 0;
}
function parseDataRangeEditBound(data) {
  return data ? {
    ...data,
    timestamp: data.timestamp?.toDate(),
    overrideTimestamp: data.overrideTimestamp?.toDate()
  } : void 0;
}
function parseDataRangeEditTrims(data) {
  return data ? data.map((trim) => parseDataRangeEditTrim(trim)) : void 0;
}
function parseDataRangeEditTrim(data) {
  return data ? {
    ...data,
    from: data.from?.toDate(),
    to: data.to?.toDate()
  } : void 0;
}

// src/models/deprecated/organ-data-range-edit-bound.ts
var OrganDataRangeEditBound = class {
  timelineEntryType;
  timestamp;
  overrideTimestamp;
  constructor(params) {
    this.timelineEntryType = params.timelineEntryType;
    this.timestamp = params.timestamp ? params.timestamp instanceof Date ? params.timestamp : new Date(params.timestamp) : void 0;
    this.overrideTimestamp = params.overrideTimestamp instanceof Date ? params.overrideTimestamp : params.overrideTimestamp ? new Date(params.overrideTimestamp) : void 0;
  }
  toJSON() {
    return {
      timelineEntryType: this.timelineEntryType,
      timestamp: this.timestamp,
      overrideTimestamp: this.overrideTimestamp
    };
  }
  serialize() {
    return JSON.parse(JSON.stringify(this));
  }
};

// src/models/deprecated/organ-data-range-edit-trim.ts
var OrganDataRangeEditTrim = class {
  from;
  to;
  constructor(params) {
    this.from = params.from ? params.from instanceof Date ? params.from : new Date(params.from) : void 0;
    this.to = params.to ? params.to instanceof Date ? params.to : new Date(params.to) : void 0;
  }
  toJSON() {
    return {
      from: this.from,
      to: this.to
    };
  }
  serialize() {
    return JSON.parse(JSON.stringify(this));
  }
};

// src/models/deprecated/organ-data-range-edit.ts
var OrganDataRangeEdit = class {
  bounds;
  trims;
  intervalOverride;
  constructor(params) {
    this.bounds = params.bounds ? {
      from: params.bounds.from.map((bound) => new OrganDataRangeEditBound(bound)),
      to: params.bounds.to.map((bound) => new OrganDataRangeEditBound(bound))
    } : {
      from: [],
      to: []
    };
    this.trims = params.trims?.map((trim) => new OrganDataRangeEditTrim(trim)) ?? [];
    this.intervalOverride = params.intervalOverride;
  }
  get outerBounds() {
    const filteredFromBounds = this.bounds.from.filter((b) => b.timestamp != null || b.overrideTimestamp != null);
    const filteredToBounds = this.bounds.to.filter((b) => b.timestamp != null || b.overrideTimestamp != null);
    const from = filteredFromBounds.sort((a, b) => {
      const aTime = (a.overrideTimestamp ?? a.timestamp).getTime();
      const bTime = (b.overrideTimestamp ?? b.timestamp).getTime();
      return aTime - bTime;
    })[0];
    const to = filteredToBounds.sort((a, b) => {
      const aTime = (a.overrideTimestamp ?? a.timestamp).getTime();
      const bTime = (b.overrideTimestamp ?? b.timestamp).getTime();
      return aTime - bTime;
    })[0];
    return {
      from,
      to
    };
  }
  toJSON() {
    return {
      bounds: {
        from: this.bounds.from.map((bound) => bound.toJSON()),
        to: this.bounds.to.map((bound) => bound.toJSON())
      },
      trims: this.trims.map((trim) => trim.toJSON()),
      intervalOverride: this.intervalOverride
    };
  }
  serialize() {
    return JSON.parse(JSON.stringify(this));
  }
};

// src/models/organ-logger-header.ts
var OrganLoggerHeader = class {
  launcher;
  interval;
  delay;
  offset;
  isDst;
  firmwareVersion;
  deploymentCount;
  timeZone;
  creationDate;
  startDate;
  constructor(params) {
    this.launcher = params.launcher;
    this.interval = params.interval;
    this.delay = params.delay;
    this.offset = params.offset;
    this.isDst = params.isDst;
    this.firmwareVersion = params.firmwareVersion;
    this.deploymentCount = params.deploymentCount;
    this.timeZone = params.timeZone;
    this.creationDate = typeof params.creationDate === "string" ? new Date(params.creationDate) : params.creationDate;
    this.startDate = typeof params.startDate === "string" ? new Date(params.startDate) : params.startDate;
  }
  toJSON() {
    return {
      launcher: this.launcher,
      interval: this.interval,
      delay: this.delay,
      offset: this.offset,
      isDst: this.isDst,
      firmwareVersion: this.firmwareVersion,
      deploymentCount: this.deploymentCount,
      timeZone: this.timeZone,
      creationDate: this.creationDate,
      startDate: this.startDate
    };
  }
  serialize() {
    return JSON.parse(JSON.stringify(this));
  }
};

// src/models/deprecated/organ-logger-connection.ts
var OrganLoggerConnection = class {
  connectedBy;
  pairedDeviceIdentifier;
  constructor(params) {
    this.connectedBy = params.connectedBy;
    this.pairedDeviceIdentifier = params.pairedDeviceIdentifier;
  }
  toJSON() {
    return {
      connectedBy: this.connectedBy,
      pairedDeviceIdentifier: this.pairedDeviceIdentifier
    };
  }
  serialize() {
    return JSON.parse(JSON.stringify(this));
  }
};

// src/models/deprecated/organ-logger-data.ts
var OrganLoggerData = class {
  probe;
  ambient;
  pressure;
  pumpVoltage;
  fluidPressure;
  flow;
  resistance;
  averageFluidPressure;
  averageFlow;
  averageResistance;
  batteryVoltage;
  rssi;
  timestamp;
  constructor(params) {
    this.probe = params.probe;
    this.ambient = params.ambient;
    this.pressure = params.pressure;
    this.pumpVoltage = params.pumpVoltage;
    this.fluidPressure = params.fluidPressure;
    this.flow = params.flow;
    this.resistance = params.resistance;
    this.averageFluidPressure = params.averageFluidPressure;
    this.averageFlow = params.averageFlow;
    this.averageResistance = params.averageResistance;
    this.batteryVoltage = params.batteryVoltage;
    this.rssi = params.rssi;
    this.timestamp = typeof params.timestamp === "string" ? new Date(params.timestamp) : params.timestamp;
  }
  toJSON() {
    return {
      probe: this.probe,
      ambient: this.ambient,
      pressure: this.pressure,
      pumpVoltage: this.pumpVoltage,
      fluidPressure: this.fluidPressure,
      flow: this.flow,
      resistance: this.resistance,
      averageFluidPressure: this.averageFluidPressure,
      averageFlow: this.averageFlow,
      averageResistance: this.averageResistance,
      batteryVoltage: this.batteryVoltage,
      rssi: this.rssi,
      timestamp: this.timestamp
    };
  }
  serialize() {
    return JSON.parse(JSON.stringify(this));
  }
};

// src/models/deprecated/organ-logger.ts
var OrganLogger = class {
  serialNo;
  passkey;
  type;
  state;
  connection;
  data;
  header;
  constructor(params) {
    this.serialNo = params.serialNo;
    this.passkey = params.passkey;
    this.type = params.type;
    this.state = params.state;
    this.connection = params.connection ? new OrganLoggerConnection(params.connection) : void 0;
    this.data = params.data ? new OrganLoggerData(params.data) : void 0;
    this.header = params.header ? new OrganLoggerHeader(params.header) : void 0;
  }
  toJSON() {
    return {
      serialNo: this.serialNo,
      passkey: this.passkey,
      type: this.type,
      state: this.state,
      connection: this.connection?.toJSON(),
      data: this.data?.toJSON(),
      header: this.header?.toJSON()
    };
  }
  serialize() {
    return JSON.parse(JSON.stringify(this));
  }
};

// src/models/deprecated/organ.ts
var Organ = class {
  id;
  referenceId;
  sessionId;
  product;
  organ;
  position;
  advancedTrackerId;
  logger;
  reportData;
  internalReportData;
  dataRangeEdit;
  constructor(params) {
    this.id = params.id;
    this.referenceId = params.referenceId;
    this.sessionId = params.sessionId;
    if (!params.product && !params.organ) {
      this.product = "sherpapak-cts" /* SHERPAPAK */;
      this.organ = "heart" /* HEART */;
    } else if (params.product && !params.organ) {
      this.product = params.product;
      this.organ = OrganTypeUtil.fromProductType(params.product) ?? "heart" /* HEART */;
    } else {
      this.product = params.product;
      this.organ = params.organ;
    }
    this.position = params.position;
    this.logger = params.logger ? new OrganLogger(params.logger) : void 0;
    this.advancedTrackerId = params.advancedTrackerId;
    this.reportData = params.reportData ? new SessionReportData(params.reportData) : void 0;
    this.internalReportData = params.internalReportData ? new SessionReportData(params.internalReportData) : void 0;
    this.dataRangeEdit = params.dataRangeEdit ? new OrganDataRangeEdit(params.dataRangeEdit) : void 0;
  }
  /** Converts this deprecated Organ to a v7 Logger with default values for irrelevant fields. */
  toLogger() {
    return new Logger({
      id: this.id,
      serialNo: this.logger?.serialNo ?? "",
      type: this.logger?.type ?? "",
      passkey: this.logger?.passkey ?? "",
      shared: false,
      product: this.product,
      linkedSessions: [this.sessionId],
      userIds: [],
      organizationIds: [],
      areaIds: [],
      territoryIds: [],
      data: this.logger?.data?.toJSON(),
      header: this.logger?.header?.toJSON(),
      connection: this.logger?.connection ? { ...this.logger.connection.toJSON(), sessionId: this.sessionId } : void 0,
      advancedTrackerId: this.advancedTrackerId,
      reportData: this.reportData?.toJSON(),
      internalReportData: this.internalReportData?.toJSON(),
      dataRangeEdit: this.dataRangeEdit?.toJSON(),
      createdAt: /* @__PURE__ */ new Date(0)
    });
  }
  toJSON() {
    return {
      id: this.id,
      referenceId: this.referenceId,
      sessionId: this.sessionId,
      product: this.product,
      organ: this.organ,
      position: this.position,
      logger: this.logger?.toJSON(),
      advancedTrackerId: this.advancedTrackerId,
      reportData: this.reportData?.toJSON(),
      internalReportData: this.internalReportData?.toJSON(),
      dataRangeEdit: this.dataRangeEdit?.toJSON()
    };
  }
};
var OrganConverter = {
  toFirestore() {
    return {};
  },
  fromFirestore(doc42) {
    const data = doc42.data();
    return new Organ({
      ...data,
      id: doc42.id,
      logger: parseOrganLogger(data.logger),
      reportData: parseSessionReportData2(data.reportData),
      internalReportData: parseSessionReportData2(data.internalReportData),
      dataRangeEdit: parseOrganDataRangeEdit(data.dataRangeEdit)
    });
  }
};
function parseOrganLogger(data) {
  return data ? {
    ...data,
    data: data.data ? parseOrganLoggerData(data.data) : void 0,
    header: data.header ? parseOrganLoggerHeader(data.header) : void 0
  } : void 0;
}
function parseOrganLoggerHeader(data) {
  return data ? {
    ...data,
    creationDate: data.creationDate?.toDate?.() ?? data.creationDate,
    startDate: data.startDate?.toDate?.() ?? data.startDate
  } : void 0;
}
function parseOrganLoggerData(data) {
  return data ? {
    ...data,
    timestamp: data.timestamp?.toDate()
  } : void 0;
}
function parseSessionReportData2(data) {
  return data ? {
    ...data,
    loggerStartedAt: data.loggerStartedAt?.toDate(),
    loggerStoppedAt: data.loggerStoppedAt?.toDate(),
    firstReading: data.firstReading?.toDate(),
    lastReading: data.lastReading?.toDate(),
    timeOnPump: data.timeOnPump ? {
      ...data.timeOnPump,
      startTimestamp: data.timeOnPump.startTimestamp?.toDate(),
      endTimestamp: data.timeOnPump.endTimestamp?.toDate()
    } : void 0
  } : void 0;
}
function parseOrganDataRangeEdit(data) {
  return data ? {
    ...data,
    bounds: {
      from: data.bounds.from ? parseOrganDataRangeEditBounds(data.bounds.from) : [],
      to: data.bounds.to ? parseOrganDataRangeEditBounds(data.bounds.to) : []
    },
    trims: data.trims ? parseOrganDataRangeEditTrims(data.trims) : void 0
  } : void 0;
}
function parseOrganDataRangeEditBounds(data) {
  return data ? data.map((bound) => parseOrganDataRangeEditBound(bound)) : void 0;
}
function parseOrganDataRangeEditBound(data) {
  return data ? {
    ...data,
    timestamp: data.timestamp?.toDate(),
    overrideTimestamp: data.overrideTimestamp?.toDate()
  } : void 0;
}
function parseOrganDataRangeEditTrims(data) {
  return data ? data.map((trim) => parseOrganDataRangeEditTrim(trim)) : void 0;
}
function parseOrganDataRangeEditTrim(data) {
  return data ? {
    ...data,
    from: data.from?.toDate(),
    to: data.to?.toDate()
  } : void 0;
}

// src/models/deprecated/organ-readings-page.ts
var OrganReadingsPage = class {
  id;
  logs;
  start;
  end;
  numberOfLogs;
  pageSize;
  order;
  constructor(params) {
    this.id = params.id;
    this.logs = params.logs?.map((log) => new OrganReading(log)) ?? [];
    this.numberOfLogs = params.numberOfLogs;
    this.pageSize = params.pageSize;
    this.order = params.order;
    this.start = typeof params.start === "string" ? new Date(params.start) : params.start;
    this.end = typeof params.end === "string" ? new Date(params.end) : params.end;
  }
  toJSON() {
    return {
      id: this.id,
      logs: this.logs.map((log) => log.toJSON()),
      start: this.start,
      end: this.end,
      numberOfLogs: this.numberOfLogs,
      pageSize: this.pageSize,
      order: this.order
    };
  }
  serialize() {
    return JSON.parse(JSON.stringify(this));
  }
};
var OrganReadingsPageConverter = {
  toFirestore() {
    return {};
  },
  fromFirestore(doc42) {
    const data = doc42.data();
    return new OrganReadingsPage({
      ...data,
      logs: data.logs?.map((log) => new OrganReading({ ...log, timestamp: log.timestamp?.toDate() })) ?? [],
      start: data.start?.toDate(),
      end: data.end?.toDate()
    });
  }
};

// src/models/destination.ts
var Destination = class {
  id;
  name;
  handle;
  organs;
  types;
  isSessionOption;
  isOrganizationOption;
  /**
   * @deprecated Use DESTINATION_OWNERS data file for email notifications instead.
   */
  owner;
  placeId;
  location;
  address;
  addressComponents;
  country;
  region;
  countryCode;
  subdivisionCode;
  subdivisionName;
  areaId;
  territoryId;
  timezone;
  constructor(params) {
    this.id = params.id;
    this.name = params.name;
    this.handle = params.handle;
    this.organs = params.organs ?? [];
    this.types = params.types ?? [];
    this.isSessionOption = params.isSessionOption ?? false;
    this.isOrganizationOption = params.isOrganizationOption ?? false;
    this.owner = params.owner;
    this.placeId = params.placeId;
    this.location = params.location;
    this.address = params.address;
    this.addressComponents = params.addressComponents;
    this.country = params.country;
    this.region = params.region;
    this.countryCode = params.countryCode;
    this.subdivisionCode = params.subdivisionCode;
    this.subdivisionName = params.subdivisionName;
    this.areaId = params.areaId;
    this.territoryId = params.territoryId;
    this.timezone = params.timezone;
  }
  toJSON() {
    return {
      id: this.id,
      name: this.name,
      handle: this.handle,
      organs: this.organs,
      types: this.types,
      isSessionOption: this.isSessionOption,
      isOrganizationOption: this.isOrganizationOption,
      owner: this.owner,
      placeId: this.placeId,
      location: this.location,
      address: this.address,
      addressComponents: this.addressComponents,
      country: this.country,
      region: this.region,
      countryCode: this.countryCode,
      subdivisionCode: this.subdivisionCode,
      subdivisionName: this.subdivisionName,
      areaId: this.areaId,
      territoryId: this.territoryId,
      timezone: this.timezone
    };
  }
  serialize() {
    return JSON.parse(JSON.stringify(this));
  }
  /**
   * Converts this Destination to a RecipientCenter object.
   *
   * Extracts only the essential fields needed for storing recipient center
   * information on session documents.
   *
   * @returns A RecipientCenter with core location and identification fields
   */
  toRecipientCenter() {
    return {
      name: this.name,
      handle: this.handle,
      placeId: this.placeId,
      location: this.location,
      address: this.address,
      addressComponents: this.addressComponents,
      timezone: this.timezone
    };
  }
};
var DestinationConverter = {
  toFirestore() {
    return {};
  },
  fromFirestore(doc42) {
    const data = doc42.data();
    return new Destination({ ...data, id: doc42.id });
  }
};

// src/models/donor-data.ts
var DonorData = class {
  height;
  weight;
  age;
  ageRedacted;
  bmi;
  sex;
  bloodType;
  ethnicity;
  race;
  kdpi;
  causeOfDeath;
  mechanismOfInjury;
  circumstanceOfDeath;
  additionalDetails;
  createdAt;
  updatedAt;
  constructor(params) {
    this.height = params.height;
    this.weight = params.weight;
    this.age = params.age;
    this.ageRedacted = params.ageRedacted;
    this.bmi = params.bmi;
    this.sex = params.sex;
    this.bloodType = params.bloodType;
    this.ethnicity = params.ethnicity;
    this.race = params.race;
    this.kdpi = params.kdpi;
    this.causeOfDeath = params.causeOfDeath;
    this.mechanismOfInjury = params.mechanismOfInjury;
    this.circumstanceOfDeath = params.circumstanceOfDeath;
    this.additionalDetails = params.additionalDetails;
    this.createdAt = params.createdAt instanceof Date ? params.createdAt : params.createdAt ? new Date(params.createdAt) : void 0;
    this.updatedAt = params.updatedAt instanceof Date ? params.updatedAt : params.updatedAt ? new Date(params.updatedAt) : void 0;
  }
  toJSON() {
    return {
      height: this.height,
      weight: this.weight,
      age: this.age,
      ageRedacted: this.ageRedacted,
      bmi: this.bmi,
      sex: this.sex,
      bloodType: this.bloodType,
      ethnicity: this.ethnicity,
      race: this.race,
      kdpi: this.kdpi,
      causeOfDeath: this.causeOfDeath,
      mechanismOfInjury: this.mechanismOfInjury,
      circumstanceOfDeath: this.circumstanceOfDeath,
      additionalDetails: this.additionalDetails,
      createdAt: this.createdAt,
      updatedAt: this.updatedAt
    };
  }
  serialize() {
    return JSON.parse(JSON.stringify(this));
  }
};
var DonorDataConverter = {
  toFirestore() {
    return {};
  },
  fromFirestore(doc42) {
    const data = doc42.data();
    return new DonorData({
      ...data,
      createdAt: data.createdAt?.toDate(),
      updatedAt: data.updatedAt?.toDate()
    });
  }
};

// src/models/draft-state-validation.ts
var VALID_DRAFT_STATES = {
  ["DONOR_SUMMARY_EXTRACTION" /* DONOR_SUMMARY_EXTRACTION */]: [
    "WAITING_FOR_UPLOAD" /* WAITING_FOR_UPLOAD */,
    "PROCESSING" /* PROCESSING */,
    "COMPLETE" /* COMPLETE */,
    "ERROR" /* ERROR */
  ],
  ["MANUAL" /* MANUAL */]: ["COMPLETE" /* COMPLETE */]
};
var VALID_STATE_TRANSITIONS = {
  ["WAITING_FOR_UPLOAD" /* WAITING_FOR_UPLOAD */]: ["PROCESSING" /* PROCESSING */, "ERROR" /* ERROR */],
  ["PROCESSING" /* PROCESSING */]: ["COMPLETE" /* COMPLETE */, "ERROR" /* ERROR */],
  ["COMPLETE" /* COMPLETE */]: [],
  // Terminal state - no transitions allowed
  ["ERROR" /* ERROR */]: ["WAITING_FOR_UPLOAD" /* WAITING_FOR_UPLOAD */]
  // Can retry from error
};
function isValidStateForDraftType(draftType, state) {
  const validStates = VALID_DRAFT_STATES[draftType];
  return validStates.includes(state);
}
function assertValidStateForDraftType(draftType, state) {
  if (!isValidStateForDraftType(draftType, state)) {
    const validStates = VALID_DRAFT_STATES[draftType].join(", ");
    throw new Error(`Invalid state "${state}" for draft type "${draftType}". Valid states: ${validStates}`);
  }
}
function isValidStateTransition(currentState, nextState) {
  const allowedTransitions = VALID_STATE_TRANSITIONS[currentState];
  return allowedTransitions.includes(nextState);
}

// src/models/extraction.ts
var Extraction = class {
  /** When a file was uploaded (optional) */
  uploadedAt;
  /** When processing completed (optional) */
  processedAt;
  /** Results from extraction process (optional) */
  result;
  /** Available snippet field names for PDF image previews */
  snippetFields;
  constructor(params) {
    this.uploadedAt = params.uploadedAt instanceof Date ? params.uploadedAt : params.uploadedAt ? new Date(params.uploadedAt) : void 0;
    this.processedAt = params.processedAt instanceof Date ? params.processedAt : params.processedAt ? new Date(params.processedAt) : void 0;
    this.result = params.result;
    this.snippetFields = params.snippetFields;
  }
  /**
   * Converts the Extraction instance to a plain JSON object.
   *
   * @returns Plain object representation of the extraction data
   */
  toJSON() {
    return {
      uploadedAt: this.uploadedAt,
      processedAt: this.processedAt,
      result: this.result,
      snippetFields: this.snippetFields
    };
  }
};

// src/models/draft.ts
var Draft = class _Draft {
  // ─────────────────────────────────────────────────────────────────────────────
  // Document ID
  // ─────────────────────────────────────────────────────────────────────────────
  id;
  // ─────────────────────────────────────────────────────────────────────────────
  // Session-Level Fields (ROOT - copied to session)
  // ─────────────────────────────────────────────────────────────────────────────
  organ;
  /** Session type (e.g., CLINICAL, TRAINING) */
  sessionType;
  donationType;
  opoHandle;
  opo;
  donorCenter;
  recipientCenter;
  matchRunId;
  offerStatus;
  rank;
  sequence;
  unosId;
  attachments;
  donorOr;
  recipientOr;
  preservationMethods;
  product;
  // ─────────────────────────────────────────────────────────────────────────────
  // Draft Lifecycle Fields (ROOT - NOT copied to session)
  // ─────────────────────────────────────────────────────────────────────────────
  type;
  state;
  userId;
  converted;
  sessionId;
  convertedAt;
  createdAt;
  // ─────────────────────────────────────────────────────────────────────────────
  // Extraction Data (NOT copied to session)
  // ─────────────────────────────────────────────────────────────────────────────
  extraction;
  constructor(params) {
    this.id = params.id;
    this.organ = params.organ;
    this.sessionType = params.sessionType;
    this.donationType = params.donationType;
    this.opoHandle = params.opoHandle;
    this.opo = params.opo;
    this.donorCenter = params.donorCenter;
    this.recipientCenter = params.recipientCenter;
    this.matchRunId = params.matchRunId;
    this.offerStatus = params.offerStatus;
    this.rank = params.rank;
    this.sequence = params.sequence;
    this.unosId = params.unosId;
    this.attachments = params.attachments?.filter((att) => att != null).map((att) => new AttachmentRef(att));
    this.donorOr = params.donorOr;
    this.recipientOr = params.recipientOr;
    this.preservationMethods = params.preservationMethods;
    this.product = params.product;
    this.type = params.type;
    this.state = params.state;
    this.userId = params.userId;
    this.converted = params.converted;
    this.sessionId = params.sessionId;
    this.convertedAt = params.convertedAt instanceof Date ? params.convertedAt : params.convertedAt ? new Date(params.convertedAt) : void 0;
    this.createdAt = params.createdAt instanceof Date ? params.createdAt : params.createdAt ? new Date(params.createdAt) : void 0;
    this.extraction = params.extraction ? new Extraction(params.extraction) : void 0;
    assertValidStateForDraftType(this.type, this.state);
  }
  toJSON() {
    return {
      id: this.id,
      // Session-level fields
      organ: this.organ,
      sessionType: this.sessionType,
      donationType: this.donationType,
      opoHandle: this.opoHandle,
      opo: this.opo,
      donorCenter: this.donorCenter,
      recipientCenter: this.recipientCenter,
      matchRunId: this.matchRunId,
      offerStatus: this.offerStatus,
      rank: this.rank,
      sequence: this.sequence,
      unosId: this.unosId,
      attachments: this.attachments?.map((attachment) => attachment.toJSON()),
      donorOr: this.donorOr,
      recipientOr: this.recipientOr,
      preservationMethods: this.preservationMethods,
      product: this.product,
      // Draft lifecycle fields
      type: this.type,
      state: this.state,
      userId: this.userId,
      converted: this.converted,
      sessionId: this.sessionId,
      convertedAt: this.convertedAt,
      createdAt: this.createdAt,
      // Extraction data (returns plain object or undefined)
      extraction: this.extraction?.toJSON()
    };
  }
  serialize() {
    return JSON.parse(JSON.stringify(this));
  }
  /**
   * Creates a new Draft instance with an updated state.
   * Validates that the state transition is valid and that the new state
   * is allowed for this draft type.
   *
   * @param newState - The state to transition to
   * @returns A new Draft instance with the updated state
   * @throws {Error} If the state transition is invalid or the state is not allowed for this draft type
   *
   * @example
   * ```typescript
   * const draft = new Draft({
   *   id: '123',
   *   organ: OrganType.HEART,
   *   type: OfferDraftType.DONOR_SUMMARY_EXTRACTION,
   *   state: DraftStateType.WAITING_FOR_UPLOAD,
   *   userId: 'user-456',
   * });
   *
   * // Valid transition
   * const processingDraft = draft.withState(DraftStateType.PROCESSING);
   *
   * // Invalid transition - throws error
   * const completeDraft = draft.withState(DraftStateType.COMPLETE);
   * ```
   */
  withState(newState) {
    if (!isValidStateTransition(this.state, newState)) {
      throw new Error(
        `Invalid state transition from "${this.state}" to "${newState}". This transition is not allowed in the state machine.`
      );
    }
    return new _Draft({
      id: this.id,
      // Session-level fields
      organ: this.organ,
      sessionType: this.sessionType,
      donationType: this.donationType,
      opoHandle: this.opoHandle,
      opo: this.opo,
      donorCenter: this.donorCenter,
      recipientCenter: this.recipientCenter,
      matchRunId: this.matchRunId,
      offerStatus: this.offerStatus,
      rank: this.rank,
      sequence: this.sequence,
      unosId: this.unosId,
      attachments: this.attachments,
      donorOr: this.donorOr,
      recipientOr: this.recipientOr,
      preservationMethods: this.preservationMethods,
      product: this.product,
      // Draft lifecycle fields with updated state
      type: this.type,
      state: newState,
      userId: this.userId,
      converted: this.converted,
      sessionId: this.sessionId,
      convertedAt: this.convertedAt,
      createdAt: this.createdAt,
      // Extraction data
      extraction: this.extraction
    });
  }
  // ─────────────────────────────────────────────────────────────────────────────
  // Convenience accessors for extraction fields
  // ─────────────────────────────────────────────────────────────────────────────
  /** When the PDF was uploaded */
  get uploadedAt() {
    return this.extraction?.uploadedAt;
  }
  /** When the draft was processed */
  get processedAt() {
    return this.extraction?.processedAt;
  }
  /** Extraction result from PDF processing */
  get extractionResult() {
    return this.extraction?.result;
  }
  /** Confidence level of extraction */
  get confidence() {
    return this.extraction?.result?.confidence;
  }
  /** PDF type that was processed */
  get pdfType() {
    return this.extraction?.result?.pdfType;
  }
  /** Error message if processing failed */
  get errorMessage() {
    return this.extraction?.result?.error;
  }
};
var DraftConverter = {
  toFirestore() {
    return {};
  },
  fromFirestore(doc42) {
    const data = doc42.data();
    const extraction = data.extraction || {};
    return new Draft({
      id: doc42.id,
      // Session-level fields from root
      organ: data.organ,
      sessionType: data.sessionType,
      donationType: data.donationType,
      opoHandle: data.opoHandle,
      opo: data.opo,
      donorCenter: data.donorCenter,
      recipientCenter: data.recipientCenter,
      matchRunId: data.matchRunId,
      offerStatus: data.offerStatus,
      rank: data.rank,
      sequence: data.sequence,
      unosId: data.unosId,
      attachments: data.attachments,
      donorOr: data.donorOr ? { ...data.donorOr, time: toDate(data.donorOr.time) } : void 0,
      recipientOr: data.recipientOr ? { ...data.recipientOr, time: toDate(data.recipientOr.time) } : void 0,
      preservationMethods: data.preservationMethods,
      product: data.product,
      // Draft lifecycle fields from root
      type: data.type,
      state: data.state,
      userId: data.userId,
      converted: data.converted,
      sessionId: data.sessionId,
      convertedAt: toDate(data.convertedAt),
      createdAt: toDate(data.createdAt),
      // Extraction data (only if any extraction fields exist)
      extraction: data.extraction ? {
        uploadedAt: toDate(extraction.uploadedAt),
        processedAt: toDate(extraction.processedAt),
        result: extraction.result,
        snippetFields: extraction.snippetFields
      } : void 0
    });
  }
};

// src/models/flight.ts
var Flight = class {
  id;
  flightId;
  ident;
  airline;
  state;
  cancelled;
  progressPercent;
  arrivalDelay;
  actualArrival;
  actualDeparture;
  departureDelay;
  estimatedDeparture;
  estimatedArrival;
  plannedArrival;
  plannedDeparture;
  origin;
  destination;
  destinationType;
  dataTimestamp;
  deleted;
  deletedAt;
  constructor(params) {
    this.id = params.id;
    this.flightId = params.flightId;
    this.ident = params.ident;
    this.airline = params.airline;
    this.state = params.state;
    this.cancelled = params.cancelled;
    this.progressPercent = params.progressPercent;
    this.arrivalDelay = params.arrivalDelay;
    this.departureDelay = params.departureDelay;
    this.origin = params.origin ? new Airport(params.origin) : void 0;
    this.destination = params.destination ? new Airport(params.destination) : void 0;
    this.destinationType = params.destinationType;
    this.dataTimestamp = params.dataTimestamp;
    this.deleted = params.deleted ?? false;
    this.deletedAt = params.deletedAt;
    this.actualArrival = params.actualArrival instanceof Date ? params.actualArrival : params.actualArrival ? new Date(params.actualArrival) : void 0;
    this.actualDeparture = params.actualDeparture instanceof Date ? params.actualDeparture : params.actualDeparture ? new Date(params.actualDeparture) : void 0;
    this.estimatedDeparture = params.estimatedDeparture instanceof Date ? params.estimatedDeparture : params.estimatedDeparture ? new Date(params.estimatedDeparture) : void 0;
    this.estimatedArrival = params.estimatedArrival instanceof Date ? params.estimatedArrival : params.estimatedArrival ? new Date(params.estimatedArrival) : void 0;
    this.plannedArrival = params.plannedArrival instanceof Date ? params.plannedArrival : params.plannedArrival ? new Date(params.plannedArrival) : void 0;
    this.plannedDeparture = params.plannedDeparture instanceof Date ? params.plannedDeparture : params.plannedDeparture ? new Date(params.plannedDeparture) : void 0;
  }
  toJSON() {
    return {
      id: this.id,
      flightId: this.flightId,
      ident: this.ident,
      airline: this.airline,
      state: this.state,
      cancelled: this.cancelled,
      progressPercent: this.progressPercent,
      arrivalDelay: this.arrivalDelay,
      actualArrival: this.actualArrival,
      actualDeparture: this.actualDeparture,
      departureDelay: this.departureDelay,
      estimatedDeparture: this.estimatedDeparture,
      estimatedArrival: this.estimatedArrival,
      plannedArrival: this.plannedArrival,
      plannedDeparture: this.plannedDeparture,
      origin: this.origin?.toJSON(),
      destination: this.destination?.toJSON(),
      destinationType: this.destinationType,
      dataTimestamp: this.dataTimestamp,
      deleted: this.deleted,
      deletedAt: this.deletedAt
    };
  }
  serialize() {
    return JSON.parse(JSON.stringify(this));
  }
};
var FlightConverter = {
  toFirestore() {
    return {};
  },
  fromFirestore(doc42) {
    const data = doc42.data();
    return new Flight({
      ...data,
      id: doc42.id,
      actualArrival: data.actualArrival?.toDate(),
      actualDeparture: data.actualDeparture?.toDate(),
      estimatedDeparture: data.estimatedDeparture?.toDate(),
      estimatedArrival: data.estimatedArrival?.toDate(),
      plannedArrival: data.plannedArrival?.toDate(),
      plannedDeparture: data.plannedDeparture?.toDate(),
      dataTimestamp: data.dataTimestamp?.toDate()
    });
  }
};

// src/models/flight-position.ts
var FlightPosition = class {
  flightId;
  latitude;
  longitude;
  heading;
  altitude;
  groundspeed;
  timestamp;
  constructor(params) {
    this.flightId = params.flightId;
    this.latitude = params.latitude;
    this.longitude = params.longitude;
    this.heading = params.heading;
    this.altitude = params.altitude;
    this.groundspeed = params.groundspeed;
    this.timestamp = params.timestamp instanceof Date ? params.timestamp : params.timestamp ? new Date(params.timestamp) : void 0;
  }
  toJSON() {
    return {
      flightId: this.flightId,
      latitude: this.latitude,
      longitude: this.longitude,
      heading: this.heading,
      altitude: this.altitude,
      groundspeed: this.groundspeed,
      timestamp: this.timestamp
    };
  }
  serialize() {
    return JSON.parse(JSON.stringify(this));
  }
};
var FlightPositionConverter = {
  toFirestore() {
    return {};
  },
  fromFirestore(doc42) {
    const data = doc42.data();
    return new FlightPosition({ ...data, id: doc42.id, timestamp: data.timestamp?.toDate() });
  }
};

// src/models/inventory-location.ts
var InventoryLocation = class {
  id;
  name;
  type;
  state;
  placeId;
  address;
  addressComponents;
  location;
  userIds;
  openAuditPeriod;
  openAuditId;
  createdAt;
  testRecord;
  constructor(params) {
    this.id = params.id;
    this.name = params.name;
    this.placeId = params.placeId;
    this.address = params.address;
    this.addressComponents = params.addressComponents;
    this.location = params.location;
    this.type = params.type;
    this.state = params.state;
    this.userIds = params.userIds ?? [];
    this.openAuditPeriod = params.openAuditPeriod;
    this.openAuditId = params.openAuditId;
    this.createdAt = typeof params.createdAt === "string" ? new Date(params.createdAt) : params.createdAt;
    this.testRecord = params.testRecord ?? false;
  }
  toJSON() {
    return {
      id: this.id,
      name: this.name,
      placeId: this.placeId,
      address: this.address,
      addressComponents: this.addressComponents,
      location: this.location,
      type: this.type,
      state: this.state,
      userIds: this.userIds,
      openAuditPeriod: this.openAuditPeriod,
      openAuditId: this.openAuditId,
      createdAt: this.createdAt,
      testRecord: this.testRecord
    };
  }
  serialize() {
    return JSON.parse(JSON.stringify(this));
  }
};
var InventoryLocationConverter = {
  toFirestore() {
    return {};
  },
  fromFirestore(doc42) {
    const data = doc42.data();
    return new InventoryLocation({
      ...data,
      id: doc42.id,
      createdAt: data.createdAt?.toDate(),
      testRecord: data.testRecord
    });
  }
};

// src/models/logger-app-log-page.ts
var LoggerAppLogPage = class {
  /** Document ID (page identifier) */
  id;
  /** Page order number (0-indexed) */
  order;
  /** Timestamp of the first log entry in this page */
  start;
  /** Timestamp of the last log entry in this page */
  end;
  /** Maximum entries per page (typically 500) */
  pageSize;
  /** Actual number of log entries in this page */
  numberOfLogs;
  /** Array of log entries */
  logs;
  constructor(params) {
    this.id = params.id;
    this.order = params.order;
    this.start = params.start instanceof Date ? params.start : new Date(params.start);
    this.end = params.end instanceof Date ? params.end : new Date(params.end);
    this.pageSize = params.pageSize;
    this.numberOfLogs = params.numberOfLogs;
    this.logs = params.logs?.map((log) => new LoggerLogEntry2(log)) ?? [];
  }
  toJSON() {
    return {
      id: this.id,
      order: this.order,
      start: this.start,
      end: this.end,
      pageSize: this.pageSize,
      numberOfLogs: this.numberOfLogs,
      logs: this.logs.map((log) => log.toJSON())
    };
  }
  serialize() {
    return JSON.parse(JSON.stringify(this));
  }
};
var LoggerAppLogPageConverter = {
  toFirestore() {
    return {};
  },
  fromFirestore(doc42) {
    const data = doc42.data();
    return new LoggerAppLogPage({
      ...data,
      id: doc42.id,
      start: data.start?.toDate(),
      end: data.end?.toDate(),
      logs: data.logs?.map((log) => ({
        ...log,
        timestamp: log.timestamp?.toDate()
      })) ?? []
    });
  }
};

// src/models/logger-chart.ts
var LoggerChartPoint = class {
  timestamp;
  probe;
  ambient;
  airPressure;
  fluidPressure;
  flow;
  resistance;
  constructor(params) {
    this.timestamp = params.timestamp instanceof Date ? params.timestamp : new Date(params.timestamp);
    this.probe = params.probe;
    this.ambient = params.ambient;
    this.airPressure = params.airPressure;
    this.fluidPressure = params.fluidPressure;
    this.flow = params.flow;
    this.resistance = params.resistance;
  }
};
var LoggerChartData = class {
  start;
  end;
  pointCount;
  points;
  updatedAt;
  constructor(params) {
    this.start = params.start instanceof Date ? params.start : new Date(params.start);
    this.end = params.end instanceof Date ? params.end : new Date(params.end);
    this.pointCount = params.pointCount;
    this.points = params.points?.map((p) => new LoggerChartPoint(p)) ?? [];
    this.updatedAt = params.updatedAt instanceof Date ? params.updatedAt : new Date(params.updatedAt);
  }
};
var LoggerChartDataConverter = {
  toFirestore() {
    return {};
  },
  fromFirestore(doc42) {
    const data = doc42.data();
    return new LoggerChartData({
      start: data.start?.toDate(),
      end: data.end?.toDate(),
      pointCount: data.pointCount,
      updatedAt: data.updatedAt?.toDate(),
      points: data.points?.map((p) => ({
        ...p,
        timestamp: p.timestamp?.toDate()
      })) ?? []
    });
  }
};

// src/models/lung-position.ts
var LungPositionTypeUtil = class {
  static toUIString(type) {
    switch (type) {
      case "SUPINE" /* SUPINE */:
        return "Supine";
      case "PRONE" /* PRONE */:
        return "Prone";
      default:
        return "unknown";
    }
  }
};

// src/models/messages/message-mention.ts
var MessageMention = class {
  type;
  userId;
  start;
  length;
  constructor(params) {
    this.type = params.type;
    this.userId = params.userId;
    this.start = params.start;
    this.length = params.length;
  }
  toJSON() {
    return {
      type: this.type,
      // Only include userId when set so @all entries stay clean in Firestore.
      ...this.userId !== void 0 && { userId: this.userId },
      start: this.start,
      length: this.length
    };
  }
  serialize() {
    return JSON.parse(JSON.stringify(this));
  }
};

// src/models/messages/message-reactions.ts
var MessageReaction = class {
  emoji;
  updatedAt;
  constructor(params) {
    this.emoji = params.emoji;
    this.updatedAt = toDate(params.updatedAt) || /* @__PURE__ */ new Date();
  }
  toJSON() {
    return {
      emoji: this.emoji,
      updatedAt: this.updatedAt
    };
  }
};
var MessageReactions = class {
  reactions;
  constructor(params = {}) {
    this.reactions = {};
    for (const [userId, reaction] of Object.entries(params ?? {})) {
      if (!reaction?.emoji) continue;
      this.reactions[userId] = new MessageReaction(reaction);
    }
  }
  has(userId) {
    return this.reactions?.[userId] != null;
  }
  get(userId) {
    return this.reactions?.[userId];
  }
  toJSON() {
    return Object.fromEntries(
      Object.entries(this.reactions).map(([userId, reaction]) => [userId, reaction.toJSON()])
    );
  }
  serialize() {
    return JSON.parse(JSON.stringify(this));
  }
};

// src/models/messages/message.ts
var Message = class {
  id;
  parentId;
  senderId;
  createdAt;
  content;
  attachments;
  mentionRanges;
  reactions;
  editedAt;
  deletedAt;
  deletedBy;
  deleted;
  deliveryStatus;
  type;
  systemEvent;
  timestamp;
  constructor(params) {
    this.id = params.id;
    this.parentId = params.parentId;
    this.senderId = params.senderId;
    this.createdAt = toDate(params.createdAt);
    this.content = params.content;
    this.attachments = params.attachments?.filter((a) => a != null).map((a) => new AttachmentRef(a)) ?? [];
    this.mentionRanges = params.mentionRanges?.filter((m) => m != null).map((m) => new MessageMention(m)) ?? [];
    this.reactions = new MessageReactions(params.reactions);
    this.editedAt = toDate(params.editedAt);
    this.deletedAt = toDate(params.deletedAt);
    this.deletedBy = params.deletedBy;
    this.deleted = params.deleted;
    this.deliveryStatus = params.deliveryStatus;
    this.type = params.type;
    this.systemEvent = params.systemEvent;
    this.timestamp = toDate(params.timestamp) || toDate(params.createdAt) || /* @__PURE__ */ new Date();
  }
  toPreview() {
    const attachments = this.attachments.length > 0 ? {
      images: this.attachments.filter((a) => a.type === "IMAGE" /* IMAGE */).length,
      videos: this.attachments.filter((a) => a.type === "VIDEO" /* VIDEO */).length,
      documents: this.attachments.filter((a) => a.type === "DOCUMENT" /* DOCUMENT */).length
    } : void 0;
    return new MessagePreview({
      id: this.id,
      content: this.content,
      senderId: this.senderId,
      timestamp: this.timestamp,
      attachments
    });
  }
  toJSON() {
    return {
      id: this.id,
      parentId: this.parentId,
      senderId: this.senderId,
      createdAt: this.createdAt,
      content: this.content,
      attachments: this.attachments.map((a) => a.toJSON()),
      mentionRanges: this.mentionRanges.map((m) => m.toJSON()),
      reactions: this.reactions.toJSON(),
      editedAt: this.editedAt,
      deletedAt: this.deletedAt,
      deletedBy: this.deletedBy,
      deleted: this.deleted,
      deliveryStatus: this.deliveryStatus,
      type: this.type,
      systemEvent: this.systemEvent,
      timestamp: this.timestamp
    };
  }
  serialize() {
    return JSON.parse(JSON.stringify(this));
  }
};
var MessageConverter = {
  toFirestore() {
    return {};
  },
  fromFirestore(doc42) {
    const data = doc42.data();
    const createdAt = data.createdAt?.toDate();
    const timestamp = data.timestamp?.toDate() || createdAt;
    return new Message({
      ...data,
      id: doc42.id,
      timestamp,
      createdAt,
      type: data.type ?? "TEXT" /* TEXT */,
      editedAt: data.editedAt?.toDate(),
      deletedAt: data.deletedAt?.toDate()
    });
  }
};

// src/models/messages/message-receipt.ts
var MessageReceipt = class {
  userId;
  receivedAt;
  readAt;
  constructor(params) {
    this.userId = params.userId;
    this.receivedAt = toDate(params.receivedAt);
    this.readAt = toDate(params.readAt);
  }
  toJSON() {
    return {
      userId: this.userId,
      receivedAt: this.receivedAt,
      readAt: this.readAt
    };
  }
  serialize() {
    return JSON.parse(JSON.stringify(this));
  }
};
var MessageReceiptConverter = {
  toFirestore() {
    return {};
  },
  fromFirestore(doc42) {
    const data = doc42.data();
    return new MessageReceipt({
      userId: doc42.id,
      receivedAt: data.receivedAt?.toDate(),
      readAt: data.readAt?.toDate()
    });
  }
};

// src/models/meta.ts
var META_REQUIRED_FIELDS = [
  "userId",
  "deviceIdentifier",
  "timestampUser",
  "timezone",
  "language",
  "locale",
  "device",
  "os",
  "appVersion",
  "osVersion",
  "appId"
];
var META_OPTIONAL_FIELDS = ["viaCloudFunction", "cloudFunctionVersion"];
var META_ALL_FIELDS = [...META_REQUIRED_FIELDS, ...META_OPTIONAL_FIELDS];

// src/models/notes-data.ts
var NotesData = class {
  highlights;
  critical;
  createdAt;
  updatedAt;
  constructor(params) {
    this.highlights = params.highlights;
    this.critical = params.critical;
    this.createdAt = params.createdAt instanceof Date ? params.createdAt : params.createdAt ? new Date(params.createdAt) : void 0;
    this.updatedAt = params.updatedAt instanceof Date ? params.updatedAt : params.updatedAt ? new Date(params.updatedAt) : void 0;
  }
  toJSON() {
    return {
      highlights: this.highlights,
      critical: this.critical,
      createdAt: this.createdAt,
      updatedAt: this.updatedAt
    };
  }
  serialize() {
    return JSON.parse(JSON.stringify(this));
  }
};
var NotesDataConverter = {
  toFirestore() {
    return {};
  },
  fromFirestore(doc42) {
    const data = doc42.data();
    return new NotesData({
      ...data,
      createdAt: data.createdAt?.toDate(),
      updatedAt: data.updatedAt?.toDate()
    });
  }
};

// src/models/organization.ts
var Organization = class {
  id;
  name;
  handle;
  adminIds;
  userIds;
  status;
  organTypes;
  types;
  isInternal;
  placeId;
  areaId;
  territoryId;
  address;
  addressComponents;
  location;
  country;
  timezone;
  logoFileName;
  logoUrl;
  domains;
  areDomainsWhitelisted;
  createdAt;
  updatedAt;
  validationStartedAt;
  activatedAt;
  pilot;
  constructor(params) {
    this.id = params.id;
    this.name = params.name;
    this.handle = params.handle;
    this.adminIds = params.adminIds ?? [];
    this.userIds = params.userIds ?? [];
    this.status = params.status;
    this.organTypes = params.organTypes ?? [];
    this.types = params.types ?? [];
    this.isInternal = params.isInternal ?? false;
    this.placeId = params.placeId;
    this.areaId = params.areaId;
    this.territoryId = params.territoryId;
    this.address = params.address;
    this.addressComponents = params.addressComponents;
    this.location = params.location;
    this.country = params.country;
    this.timezone = params.timezone;
    this.logoFileName = params.logoFileName;
    this.logoUrl = params.logoUrl;
    this.domains = params.domains ?? [];
    this.areDomainsWhitelisted = params.areDomainsWhitelisted ?? false;
    this.createdAt = typeof params.createdAt === "string" ? new Date(params.createdAt) : params.createdAt;
    this.updatedAt = typeof params.updatedAt === "string" ? new Date(params.updatedAt) : params.updatedAt;
    this.validationStartedAt = typeof params.validationStartedAt === "string" ? new Date(params.validationStartedAt) : params.validationStartedAt;
    this.activatedAt = typeof params.activatedAt === "string" ? new Date(params.activatedAt) : params.activatedAt;
    this.pilot = params.pilot;
  }
  serialize() {
    return JSON.parse(JSON.stringify(this));
  }
  toJSON() {
    return {
      id: this.id,
      name: this.name,
      handle: this.handle,
      adminIds: this.adminIds,
      userIds: this.userIds,
      status: this.status,
      organTypes: this.organTypes,
      types: this.types,
      isInternal: this.isInternal,
      placeId: this.placeId,
      areaId: this.areaId,
      territoryId: this.territoryId,
      address: this.address,
      addressComponents: this.addressComponents,
      location: this.location,
      country: this.country,
      timezone: this.timezone,
      logoFileName: this.logoFileName,
      logoUrl: this.logoUrl,
      domains: this.domains,
      areDomainsWhitelisted: this.areDomainsWhitelisted,
      createdAt: this.createdAt,
      updatedAt: this.updatedAt,
      validationStartedAt: this.validationStartedAt,
      activatedAt: this.activatedAt,
      pilot: this.pilot
    };
  }
};
var OrganizationConverter = {
  toFirestore() {
    return {};
  },
  fromFirestore(doc42) {
    const data = doc42.data();
    return new Organization({
      ...data,
      id: doc42.id,
      createdAt: data.createdAt?.toDate(),
      updatedAt: data.updatedAt?.toDate(),
      validationStartedAt: data.validationStartedAt?.toDate(),
      activatedAt: data.activatedAt?.toDate()
    });
  }
};

// src/models/organization-member-permissions.ts
var OrganizationMemberPermissions = class {
  canViewAllSessions;
  canCreateSessions;
  constructor(params) {
    this.canViewAllSessions = params?.canViewAllSessions ?? true;
    this.canCreateSessions = params?.canCreateSessions ?? true;
  }
  toJSON() {
    return {
      canViewAllSessions: this.canViewAllSessions,
      canCreateSessions: this.canCreateSessions
    };
  }
};

// src/models/organization-member.ts
var OrganizationMember = class {
  id;
  email;
  isEmailWhitelisted;
  firstname;
  lastname;
  status;
  accessRole;
  role;
  title;
  startOfEmployment;
  createdAt;
  updatedAt;
  accessRequestedAt;
  invitedAt;
  invitedBy;
  lastInvitedAt;
  lastInvitedBy;
  activatedAt;
  deactivatedAt;
  deactivatedBy;
  deactivationReason;
  deactivationText;
  permissions;
  /**
   * Resolved default session access level for this organization member.
   * Always populated — falls back to {@link SessionMemberAccessLevel.FULL}
   * when the underlying document does not set a value.
   */
  defaultSessionAccessLevel;
  constructor(params) {
    this.id = params.id;
    this.email = params.email;
    this.isEmailWhitelisted = params.isEmailWhitelisted ?? false;
    this.firstname = params.firstname;
    this.lastname = params.lastname;
    this.status = params.status;
    this.accessRole = params.accessRole;
    this.role = params.role;
    this.title = params.title;
    this.startOfEmployment = typeof params.startOfEmployment === "string" ? new Date(params.startOfEmployment) : params.startOfEmployment;
    this.createdAt = typeof params.createdAt === "string" ? new Date(params.createdAt) : params.createdAt;
    this.updatedAt = typeof params.updatedAt === "string" ? new Date(params.updatedAt) : params.updatedAt;
    this.accessRequestedAt = typeof params.accessRequestedAt === "string" ? new Date(params.accessRequestedAt) : params.accessRequestedAt;
    this.invitedAt = typeof params.invitedAt === "string" ? new Date(params.invitedAt) : params.invitedAt;
    this.invitedBy = params.invitedBy;
    this.lastInvitedAt = typeof params.lastInvitedAt === "string" ? new Date(params.lastInvitedAt) : params.lastInvitedAt;
    this.lastInvitedBy = params.lastInvitedBy;
    this.activatedAt = typeof params.activatedAt === "string" ? new Date(params.activatedAt) : params.activatedAt;
    this.deactivatedAt = typeof params.deactivatedAt === "string" ? new Date(params.deactivatedAt) : params.deactivatedAt;
    this.deactivatedBy = params.deactivatedBy;
    this.deactivationReason = params.deactivationReason;
    this.deactivationText = params.deactivationText;
    this.permissions = new OrganizationMemberPermissions(params.permissions);
    this.defaultSessionAccessLevel = params.defaultSessionAccessLevel ?? "FULL" /* FULL */;
  }
  get fullname() {
    return `${this.firstname} ${this.lastname}`;
  }
  get initials() {
    return `${this.firstname?.charAt(0) ?? ""}${this.lastname?.charAt(0) ?? ""}`;
  }
  serialize() {
    return JSON.parse(JSON.stringify(this));
  }
  toJSON() {
    return {
      id: this.id,
      email: this.email,
      isEmailWhitelisted: this.isEmailWhitelisted,
      firstname: this.firstname,
      lastname: this.lastname,
      status: this.status,
      accessRole: this.accessRole,
      role: this.role,
      title: this.title,
      startOfEmployment: this.startOfEmployment,
      createdAt: this.createdAt,
      updatedAt: this.updatedAt,
      accessRequestedAt: this.accessRequestedAt,
      invitedAt: this.invitedAt,
      invitedBy: this.invitedBy,
      lastInvitedAt: this.lastInvitedAt,
      lastInvitedBy: this.lastInvitedBy,
      activatedAt: this.activatedAt,
      deactivatedAt: this.deactivatedAt,
      deactivatedBy: this.deactivatedBy,
      deactivationReason: this.deactivationReason,
      deactivationText: this.deactivationText,
      permissions: this.permissions.toJSON(),
      defaultSessionAccessLevel: this.defaultSessionAccessLevel
    };
  }
};
var OrganizationMemberConverter = {
  toFirestore() {
    return {};
  },
  fromFirestore(doc42) {
    const data = doc42.data();
    return new OrganizationMember({
      id: doc42.id,
      ...data,
      startOfEmployment: data.startOfEmployment?.toDate(),
      createdAt: data.createdAt?.toDate(),
      updatedAt: data.updatedAt?.toDate(),
      accessRequestedAt: data.accessRequestedAt?.toDate(),
      invitedAt: data.invitedAt?.toDate(),
      lastInvitedAt: data.lastInvitedAt?.toDate(),
      activatedAt: data.activatedAt?.toDate(),
      deactivatedAt: data.deactivatedAt?.toDate()
    });
  }
};

// src/models/organization-preselected-member.ts
var OrganizationPreselectedMember = class extends OrganizationMember {
  isConfirmed;
  confirmedAt;
  constructor(params) {
    super(params);
    this.isConfirmed = params.isConfirmed;
    this.confirmedAt = typeof params.confirmedAt === "string" ? new Date(params.confirmedAt) : params.confirmedAt;
  }
  toJSON() {
    return {
      ...super.toJSON(),
      isConfirmed: this.isConfirmed,
      confirmedAt: this.confirmedAt
    };
  }
  serialize() {
    return {
      ...super.serialize(),
      ...JSON.parse(JSON.stringify(this))
    };
  }
};
var OrganizationPreselectedMemberConverter = {
  toFirestore() {
    return {};
  },
  fromFirestore(doc42) {
    const data = doc42.data();
    return new OrganizationPreselectedMember({
      ...data,
      id: doc42.id,
      createdAt: data.createdAt?.toDate(),
      updatedAt: data.updatedAt?.toDate(),
      confirmedAt: data.confirmedAt?.toDate()
    });
  }
};

// src/models/organization-unregistered-invite.ts
var OrganizationUnregisteredInvite = class {
  id;
  email;
  organizationId;
  invitedBy;
  createdAt;
  updatedAt;
  constructor(params) {
    this.id = params.id;
    this.email = params.email;
    this.organizationId = params.organizationId;
    this.invitedBy = params.invitedBy;
    this.createdAt = typeof params.createdAt === "string" ? new Date(params.createdAt) : params.createdAt;
    this.updatedAt = typeof params.updatedAt === "string" ? new Date(params.updatedAt) : params.updatedAt;
  }
  serialize() {
    return JSON.parse(JSON.stringify(this));
  }
};
var OrganizationUnregisteredInviteConverter = {
  toFirestore() {
    return {};
  },
  fromFirestore(doc42) {
    const data = doc42.data();
    return new OrganizationUnregisteredInvite({
      ...data,
      id: doc42.id,
      createdAt: data.createdAt?.toDate(),
      updatedAt: data.updatedAt?.toDate()
    });
  }
};

// src/models/potential-session.ts
var PotentialSession = class {
  // ─────────────────────────────────────────────────────────────────────────────
  // Identifiers
  // ─────────────────────────────────────────────────────────────────────────────
  /** Unique identifier (same as the referenced session ID) */
  id;
  /** Reference to the actual session document */
  sessionId;
  // ─────────────────────────────────────────────────────────────────────────────
  // Denormalized Session Data
  // ─────────────────────────────────────────────────────────────────────────────
  /**
   * Session access key (always string).
   * Converted from number if stored as legacy joinKey in database.
   */
  key;
  /** Date when the session was created */
  sessionDate;
  /** Type of organ being transported */
  organ;
  /** Paragonix product type used */
  product;
  /** Recipient center destination handle */
  destination;
  /** UNOS donor identifier */
  unosId;
  // ─────────────────────────────────────────────────────────────────────────────
  // Review Status
  // ─────────────────────────────────────────────────────────────────────────────
  /** Current status in the review workflow */
  status;
  // ─────────────────────────────────────────────────────────────────────────────
  // Identification
  // ─────────────────────────────────────────────────────────────────────────────
  /** How the session was identified as potentially belonging to the organization */
  reason;
  /** Additional details about the identification reason */
  reasonDetails;
  /** Timestamp when the session was identified */
  identifiedAt;
  /** User ID who identified the session */
  identifiedBy;
  // ─────────────────────────────────────────────────────────────────────────────
  // Review Tracking
  // ─────────────────────────────────────────────────────────────────────────────
  /** Timestamp when review was requested */
  reviewRequestedAt;
  /** User ID who requested the review */
  reviewRequestedBy;
  /** Timestamp when the review was completed */
  reviewedAt;
  /** User ID who completed the review */
  reviewedBy;
  /** Notes from the reviewer */
  reviewNotes;
  /** Reason for rejection (if rejected) */
  rejectionReason;
  // ─────────────────────────────────────────────────────────────────────────────
  // Metadata
  // ─────────────────────────────────────────────────────────────────────────────
  /** Timestamp when the document was created */
  createdAt;
  /** Timestamp when the document was last updated */
  updatedAt;
  // ─────────────────────────────────────────────────────────────────────────────
  // Constructor
  // ─────────────────────────────────────────────────────────────────────────────
  constructor(params) {
    this.id = params.id;
    this.sessionId = params.sessionId;
    this.key = typeof params.key === "number" ? String(params.key) : params.key;
    this.sessionDate = typeof params.sessionDate === "string" ? new Date(params.sessionDate) : params.sessionDate;
    this.organ = params.organ;
    this.product = params.product;
    this.destination = params.destination;
    this.unosId = params.unosId;
    this.status = params.status;
    this.reason = params.reason;
    this.reasonDetails = {
      ...params.reasonDetails,
      employmentDates: params.reasonDetails.employmentDates?.map((date) => typeof date === "string" ? new Date(date) : date)
    };
    this.identifiedAt = typeof params.identifiedAt === "string" ? new Date(params.identifiedAt) : params.identifiedAt;
    this.identifiedBy = params.identifiedBy;
    this.reviewRequestedAt = params.reviewRequestedAt ? typeof params.reviewRequestedAt === "string" ? new Date(params.reviewRequestedAt) : params.reviewRequestedAt : void 0;
    this.reviewRequestedBy = params.reviewRequestedBy;
    this.reviewedAt = params.reviewedAt ? typeof params.reviewedAt === "string" ? new Date(params.reviewedAt) : params.reviewedAt : void 0;
    this.reviewedBy = params.reviewedBy;
    this.reviewNotes = params.reviewNotes;
    this.rejectionReason = params.rejectionReason;
    this.createdAt = typeof params.createdAt === "string" ? new Date(params.createdAt) : params.createdAt;
    this.updatedAt = typeof params.updatedAt === "string" ? new Date(params.updatedAt) : params.updatedAt;
  }
  // ─────────────────────────────────────────────────────────────────────────────
  // Computed Properties
  // ─────────────────────────────────────────────────────────────────────────────
  /**
   * Whether this potential session can be reviewed (approved/rejected).
   */
  get isReviewable() {
    return this.status === "identified" /* IDENTIFIED */ || this.status === "pending_review" /* PENDING_REVIEW */;
  }
  /**
   * Whether this potential session has been finalized (approved or rejected).
   */
  get isFinalized() {
    return this.status === "approved" /* APPROVED */ || this.status === "rejected" /* REJECTED */;
  }
  // ─────────────────────────────────────────────────────────────────────────────
  // Methods
  // ─────────────────────────────────────────────────────────────────────────────
  /**
   * Gets a human-readable description of why this session was identified.
   *
   * @returns A formatted description string
   */
  getReasonDescription() {
    switch (this.reason) {
      case "member_employment_date":
        const members = this.reasonDetails.memberNames?.join(", ") || "Unknown members";
        return `Identified via ${members}`;
      case "manual_addition":
        return `Manually added by ${this.reasonDetails.addedByAdminName || "admin"}`;
      case "destination_match":
        return "Destination organization match";
      default:
        return "Unknown reason";
    }
  }
  toJSON() {
    return {
      id: this.id,
      sessionId: this.sessionId,
      key: this.key,
      sessionDate: this.sessionDate,
      organ: this.organ,
      product: this.product,
      destination: this.destination,
      unosId: this.unosId,
      status: this.status,
      reason: this.reason,
      reasonDetails: this.reasonDetails,
      identifiedAt: this.identifiedAt,
      identifiedBy: this.identifiedBy,
      reviewRequestedAt: this.reviewRequestedAt,
      reviewRequestedBy: this.reviewRequestedBy,
      reviewedAt: this.reviewedAt,
      reviewedBy: this.reviewedBy,
      reviewNotes: this.reviewNotes,
      rejectionReason: this.rejectionReason,
      createdAt: this.createdAt,
      updatedAt: this.updatedAt
    };
  }
  serialize() {
    return JSON.parse(JSON.stringify(this));
  }
};
var PotentialSessionConverter = {
  toFirestore() {
    return {};
  },
  fromFirestore(doc42) {
    const data = doc42.data();
    return new PotentialSession({
      ...data,
      id: doc42.id,
      sessionDate: data.sessionDate?.toDate(),
      reasonDetails: {
        ...data.reasonDetails,
        employmentDates: data.reasonDetails?.employmentDates?.map((date) => date?.toDate())
      },
      identifiedAt: data.identifiedAt?.toDate(),
      reviewRequestedAt: data.reviewRequestedAt?.toDate(),
      reviewedAt: data.reviewedAt?.toDate(),
      createdAt: data.createdAt?.toDate(),
      updatedAt: data.updatedAt?.toDate()
    });
  }
};

// src/models/preservation-method.ts
var ORGAN_PRODUCTS = {
  ["heart" /* HEART */]: ["sherpapak-cts" /* SHERPAPAK */],
  ["lung" /* LUNG */]: ["lungguard" /* LUNGGUARD */, "baroguard" /* BAROGUARD */],
  ["liver" /* LIVER */]: ["liverguard" /* LIVERGUARD */],
  ["kidney" /* KIDNEY */]: ["kidneyvault" /* KIDNEYVAULT */],
  ["pancreas" /* PANCREAS */]: ["pancreaspak" /* PANCREASPAK */]
};
var ORGAN_MISC_METHODS = {
  ["heart" /* HEART */]: ["ICE" /* ICE */, "NMP" /* NMP */],
  ["lung" /* LUNG */]: ["ICE" /* ICE */, "NMP" /* NMP */],
  ["liver" /* LIVER */]: ["ICE" /* ICE */, "NMP" /* NMP */, "HOPE" /* HOPE */],
  ["kidney" /* KIDNEY */]: ["ICE" /* ICE */, "HMP" /* HMP */],
  ["pancreas" /* PANCREAS */]: ["ICE" /* ICE */]
};

// src/models/recipient-data.ts
var RecipientData = class {
  height;
  weight;
  age;
  bmi;
  sex;
  bloodType;
  additionalDetails;
  createdAt;
  updatedAt;
  constructor(params) {
    this.height = params.height;
    this.weight = params.weight;
    this.age = params.age;
    this.bmi = params.bmi;
    this.sex = params.sex;
    this.bloodType = params.bloodType;
    this.additionalDetails = params.additionalDetails;
    this.createdAt = params.createdAt instanceof Date ? params.createdAt : params.createdAt ? new Date(params.createdAt) : void 0;
    this.updatedAt = params.updatedAt instanceof Date ? params.updatedAt : params.updatedAt ? new Date(params.updatedAt) : void 0;
  }
  toJSON() {
    return {
      height: this.height,
      weight: this.weight,
      age: this.age,
      bmi: this.bmi,
      sex: this.sex,
      bloodType: this.bloodType,
      additionalDetails: this.additionalDetails,
      createdAt: this.createdAt,
      updatedAt: this.updatedAt
    };
  }
  serialize() {
    return JSON.parse(JSON.stringify(this));
  }
};
var RecipientDataConverter = {
  toFirestore() {
    return {};
  },
  fromFirestore(doc42) {
    const data = doc42.data();
    return new RecipientData({
      ...data,
      createdAt: data.createdAt?.toDate(),
      updatedAt: data.updatedAt?.toDate()
    });
  }
};

// src/models/demo-state.ts
var DemoState = class {
  sessionTemplateId;
  originalStartTime;
  snapshotId;
  autoPlaySchedulerId;
  constructor(params) {
    this.sessionTemplateId = params.sessionTemplateId;
    this.originalStartTime = params.originalStartTime instanceof Date ? params.originalStartTime : params.originalStartTime ? new Date(params.originalStartTime) : void 0;
    this.snapshotId = params.snapshotId;
    this.autoPlaySchedulerId = params.autoPlaySchedulerId;
  }
  toJSON() {
    return {
      sessionTemplateId: this.sessionTemplateId,
      originalStartTime: this.originalStartTime,
      snapshotId: this.snapshotId,
      autoPlaySchedulerId: this.autoPlaySchedulerId
    };
  }
  serialize() {
    return JSON.parse(JSON.stringify(this));
  }
};

// src/models/session-ischemic-time.ts
var SessionIschemicTime = class {
  id;
  type;
  organPosition;
  startType;
  startTimestamp;
  endType;
  endTimestamp;
  constructor(params) {
    this.id = params.id;
    this.type = params.type ?? "ISCHEMIC_TIME" /* ISCHEMIC_TIME */;
    this.organPosition = params.organPosition;
    this.startType = params.startType;
    this.endType = params.endType;
    this.startTimestamp = params.startTimestamp instanceof Date ? params.startTimestamp : new Date(params.startTimestamp);
    this.endTimestamp = params.endTimestamp instanceof Date ? params.endTimestamp : params.endTimestamp ? new Date(params.endTimestamp) : void 0;
  }
  get durationFormatted() {
    if (this.startTimestamp == null || this.endTimestamp == null) {
      return "0";
    }
    const milliseconds = this.endTimestamp.getTime() - this.startTimestamp.getTime();
    return formatDuration(milliseconds);
  }
  toJSON() {
    return {
      id: this.id,
      type: this.type,
      organPosition: this.organPosition,
      startType: this.startType,
      startTimestamp: this.startTimestamp,
      endType: this.endType,
      endTimestamp: this.endTimestamp
    };
  }
  serialize() {
    return JSON.parse(JSON.stringify(this));
  }
};
var SessionIschemicTimeConverter = {
  toFirestore() {
    return {};
  },
  fromFirestore(doc42) {
    const data = doc42.data();
    return new SessionIschemicTime({
      ...data,
      id: doc42.id,
      startTimestamp: data.startTimestamp?.toDate(),
      endTimestamp: data.endTimestamp?.toDate()
    });
  }
};

// src/models/session-metadata.ts
var SessionMetadata = class {
  forceStopAfter48Hours;
  reportSentTo;
  sessionCreatedInfoMailSent;
  sessionStoppedInfoMailSent;
  stopReminderSent;
  constructor(params) {
    this.forceStopAfter48Hours = params?.forceStopAfter48Hours ?? false;
    this.reportSentTo = params?.reportSentTo ?? [];
    this.sessionCreatedInfoMailSent = params?.sessionCreatedInfoMailSent ?? false;
    this.sessionStoppedInfoMailSent = params?.sessionStoppedInfoMailSent ?? false;
    this.stopReminderSent = params?.stopReminderSent ?? false;
  }
  toJSON() {
    return {
      forceStopAfter48Hours: this.forceStopAfter48Hours,
      reportSentTo: this.reportSentTo,
      sessionCreatedInfoMailSent: this.sessionCreatedInfoMailSent,
      sessionStoppedInfoMailSent: this.sessionStoppedInfoMailSent,
      stopReminderSent: this.stopReminderSent
    };
  }
  serialize() {
    return JSON.parse(JSON.stringify(this));
  }
};

// src/models/session-metrics.ts
var SessionMetrics = class {
  memberCount;
  timelineCount;
  constructor(params) {
    this.memberCount = params.memberCount;
    this.timelineCount = params.timelineCount;
  }
  toJSON() {
    return {
      memberCount: this.memberCount,
      timelineCount: this.timelineCount
    };
  }
  serialize() {
    return JSON.parse(JSON.stringify(this));
  }
};

// src/models/session.ts
var Session = class {
  // ─────────────────────────────────────────────────────────────────────────────
  // Identifiers
  // ─────────────────────────────────────────────────────────────────────────────
  /** Unique session identifier (Firestore document ID) */
  id;
  /** UNOS donor identifier */
  unosId;
  /** UNOS match run identifier */
  matchRunId;
  /**
   * Session access key.
   * For v7+ sessions: Letter + 5 digits (e.g., "A12345")
   * For legacy sessions: Derived from joinKey as string (e.g., "123456")
   */
  key;
  /**
   * Numeric join key for session access.
   * @deprecated Use `key` instead. Only present in legacy sessions (schemaVersion < 7).
   */
  joinKey;
  // ─────────────────────────────────────────────────────────────────────────────
  // Session Hierarchy
  // ─────────────────────────────────────────────────────────────────────────────
  /** Parent session ID for breakout sessions */
  parentSessionId;
  /** Position of this organ in a multi-organ session. */
  organPosition;
  /** Map of organ positions to child session IDs */
  childSessionIds;
  /** IDs of cancelled child sessions */
  cancelledChildSessionIds;
  // ─────────────────────────────────────────────────────────────────────────────
  // Sharing
  // ─────────────────────────────────────────────────────────────────────────────
  /** Unique key for sharing session access */
  shareKey;
  /** Whether session has been shared externally */
  shared;
  /** Timestamp when session was shared */
  sharedAt;
  /** User ID who shared the session */
  sharedBy;
  // ─────────────────────────────────────────────────────────────────────────────
  // Session Configuration
  // ─────────────────────────────────────────────────────────────────────────────
  /** Type of organ being transported */
  organ;
  /** Paragonix product type used */
  product;
  /** Session type (clinical, demo, etc.) */
  sessionType;
  /** Donation type (DCD, DBD, etc.) */
  donationType;
  /** Preservation methods used during transport */
  preservationMethods;
  /** Data schema version for migrations */
  schemaVersion;
  /**
   * Offer schema version for offer-management pilot migrations.
   * @see SessionParams.offerSchemaVersion
   */
  offerSchemaVersion;
  // ─────────────────────────────────────────────────────────────────────────────
  // Session State
  // ─────────────────────────────────────────────────────────────────────────────
  /** Whether session has been completed */
  finished;
  /** Current review state for completed sessions */
  reviewState;
  /** Whether session has been cancelled */
  cancelled;
  /** User ID who cancelled the session */
  cancelledBy;
  /** Timestamp when session was cancelled */
  cancelledAt;
  /** Reason for breakout session cancellation */
  cancellationReason;
  // ─────────────────────────────────────────────────────────────────────────────
  // Users & Access
  // ─────────────────────────────────────────────────────────────────────────────
  /**
   * User ID of session administrator.
   * @deprecated Not set for schemaVersion >= 7. Use `createdBy` instead.
   */
  admin;
  /** Array of user IDs with access to this session */
  userIds;
  /** User IDs retained after organization removal */
  retainedUserIds;
  /** Organization IDs with access to this session */
  organizationIds;
  /** Area IDs for geographic filtering */
  areaIds;
  /** Territory IDs for geographic filtering */
  territoryIds;
  // ─────────────────────────────────────────────────────────────────────────────
  // Device Information
  // ─────────────────────────────────────────────────────────────────────────────
  /**
   * Device model that created the session.
   * @deprecated Not set for schemaVersion >= 7.
   */
  device;
  /**
   * App version that created the session.
   * @deprecated Not set for schemaVersion >= 7.
   */
  appVersion;
  /**
   * Operating system name.
   * @deprecated Not set for schemaVersion >= 7.
   */
  os;
  /**
   * Operating system version.
   * @deprecated Not set for schemaVersion >= 7.
   */
  osVersion;
  /**
   * Identifier of paired Bluetooth device.
   * @deprecated Not set for schemaVersion >= 7.
   */
  pairedDeviceIdentifier;
  // ─────────────────────────────────────────────────────────────────────────────
  // Location & Routing
  // ─────────────────────────────────────────────────────────────────────────────
  /** Donor hospital/center information */
  donorCenter;
  /** Recipient transplant center information */
  recipientCenter;
  /** Recipient center handle (destination identifier) */
  destination;
  /** OPO handle (OPO identifier) */
  opoHandle;
  /** OPO (Organ Procurement Organization) information */
  opo;
  /** Calculated distance between centers */
  distance;
  /** Estimated time of arrival */
  eta;
  /** User ID who set the ETA */
  etaSetBy;
  // ─────────────────────────────────────────────────────────────────────────────
  // Advanced Tracking & Flights
  // ─────────────────────────────────────────────────────────────────────────────
  /** Whether advanced tracking is enabled */
  hasAdvancedTracking;
  /**
   * Advanced tracker device identifier.
   * @deprecated Not set for schemaVersion >= 7.
   */
  advancedTrackerId;
  /**
   * Advanced tracking data.
   * @deprecated Not set for schemaVersion >= 7.
   */
  advancedTracking;
  /** Whether flight tracking is enabled */
  hasFlights;
  /**
   * Array of flight information.
   * @deprecated Not set for schemaVersion >= 7.
   */
  flights;
  /**
   * Current flight position data.
   * @deprecated Not set for schemaVersion >= 7.
   */
  flightPosition;
  // ─────────────────────────────────────────────────────────────────────────────
  // Logger & Sensor Data
  // ─────────────────────────────────────────────────────────────────────────────
  /**
   * Logger device passkey.
   * @deprecated Not set for schemaVersion >= 7. Passkey is stored on the Logger document.
   */
  loggerPasskey;
  /**
   * Primary logger serial number.
   * @deprecated Not set for schemaVersion >= 7. Use `loggers` map and Logger documents.
   */
  loggerSerialNo;
  /**
   * All logger serial numbers ever associated with this session. Append-only
   * audit trail — entries are never removed, even when the logger is unlinked.
   * Use `currentLoggerSerialNumbers` for the live set.
   *
   * Synced by:
   * - v7+: `syncLoggersToArrays` in the on-session-updated trigger
   * - v6: `syncLoggerSerialNumbersToSessionRoot` in the on-organ-updated trigger
   * - v5 and earlier: not synced by any trigger
   */
  loggerSerialNumbers;
  /**
   * Currently linked logger serial numbers. Reflects live state — entries are
   * removed when the logger is unlinked, unlike the append-only
   * `loggerSerialNumbers`.
   *
   * Synced alongside `loggerSerialNumbers`:
   * - v7+: by `syncLoggersToArrays` in the on-session-updated trigger,
   *   computed from `session.loggers[*].serialNo`.
   * - v6: by `syncLoggerSerialNumbersToSessionRoot` in the on-organ-updated
   *   trigger, recomputed by reading all organs and collecting their
   *   `logger.serialNo` values.
   * - v5 and earlier: not synced by any trigger; populated by migration only.
   */
  currentLoggerSerialNumbers;
  /**
   * Whether the session currently has any logger linked. Mirrors
   * `currentLoggerSerialNumbers.length > 0`; denormalized so Algolia can filter
   * on it directly. Synced together with `currentLoggerSerialNumbers` in the
   * same triggers (v7+ and v6 only).
   */
  loggerConnected;
  /**
   * All advanced tracker IDs ever used with this session (v7+ sessions).
   * Synced from session.loggers via trigger. Append-only for audit trail.
   */
  advancedTrackerIds;
  /**
   * All product types ever used with this session (v7+ sessions).
   * Synced from session.loggers via trigger. Append-only for audit trail.
   */
  products;
  /**
   * Current logger state (STARTED, STOPPED, etc.).
   * @deprecated Not set for schemaVersion >= 7. Logger state is on the Logger document.
   */
  loggerState;
  /**
   * User ID who connected the logger.
   * @deprecated Not set for schemaVersion >= 7. Connection info is on the Logger document.
   */
  loggerConnectedBy;
  /**
   * Latest logger sensor data.
   * @deprecated Not set for schemaVersion >= 7. Live data is on the Logger document.
   */
  loggerData;
  /**
   * Latest GPS location from logger.
   * @deprecated Not set for schemaVersion >= 7. Locations are in `/loggers/{loggerId}/appLocations`.
   */
  location;
  /** Logger IDs by position (v7+ sessions only) */
  loggers;
  /**
   * Ischemic time tracking data.
   * @deprecated Not set for schemaVersion >= 7.
   */
  ischemicTime;
  // ─────────────────────────────────────────────────────────────────────────────
  // Reports & Metrics
  // ─────────────────────────────────────────────────────────────────────────────
  /** Session metadata (operational flags, report settings, etc.) */
  metadata;
  /** Report data for session summary */
  reportData;
  /** Aggregated session metrics */
  metrics;
  /** PDF summary availability per organ position */
  pdfSummaryAvailable;
  /** Most recent timeline event type */
  lastTimelineEvent;
  // ─────────────────────────────────────────────────────────────────────────────
  // OR Schedule
  // ─────────────────────────────────────────────────────────────────────────────
  /** Donor OR schedule (type, time, surgeon) */
  donorOr;
  /** Recipient OR schedule (type, time, surgeon) */
  recipientOr;
  // ─────────────────────────────────────────────────────────────────────────────
  // Offer Fields
  // ─────────────────────────────────────────────────────────────────────────────
  /** Current phase in the offer workflow */
  phase;
  /** Offer allocation status */
  offerStatus;
  /** Position/rank in the match run */
  rank;
  /** Sequence identifier (A, B, C, etc.) */
  sequence;
  /** Offer attachments (donor summaries, lab results, etc.) */
  attachments;
  // ─────────────────────────────────────────────────────────────────────────────
  // Timestamps & Creation
  // ─────────────────────────────────────────────────────────────────────────────
  /** Timestamp when session was created */
  createdAt;
  /** User ID who created the session */
  createdBy;
  /** Organization ID that created the session */
  createdByOrganization;
  /** How the session was created (app, web, offer, etc.) */
  createdFrom;
  /** Timestamp when session ended */
  endedAt;
  // ─────────────────────────────────────────────────────────────────────────────
  // Demo Mode
  // ─────────────────────────────────────────────────────────────────────────────
  /** Whether this is a demo session */
  demo;
  /** Parent session ID for demo clones */
  demoParentId;
  /** Demo playback state */
  demoState;
  // ─────────────────────────────────────────────────────────────────────────────
  // Constructor
  // ─────────────────────────────────────────────────────────────────────────────
  constructor(params) {
    this.id = params.id;
    this.unosId = params.unosId;
    this.matchRunId = params.matchRunId;
    const key = params.key ?? (params.joinKey != null ? String(params.joinKey) : null);
    if (!key) {
      throw new Error(`Session ${params.id} has no key or joinKey`);
    }
    this.key = key;
    this.joinKey = params.joinKey;
    this.parentSessionId = params.parentSessionId;
    this.organPosition = params.organPosition;
    this.childSessionIds = params.childSessionIds;
    this.cancelledChildSessionIds = params.cancelledChildSessionIds;
    this.shareKey = params.shareKey;
    this.shared = params.shared ?? false;
    this.sharedBy = params.sharedBy;
    if (!params.product && !params.organ) {
      this.product = "sherpapak-cts" /* SHERPAPAK */;
      this.organ = "heart" /* HEART */;
    } else if (params.product && !params.organ) {
      this.product = params.product;
      this.organ = OrganTypeUtil.fromProductType(params.product) ?? "heart" /* HEART */;
    } else {
      this.product = params.product ?? Object.values(params.loggers ?? {}).find((l) => l?.product)?.product;
      this.organ = params.organ;
    }
    this.sessionType = params.sessionType;
    this.donationType = params.donationType;
    this.preservationMethods = params.preservationMethods;
    this.schemaVersion = params.schemaVersion;
    this.offerSchemaVersion = params.offerSchemaVersion;
    this.finished = params.finished ?? false;
    this.reviewState = params.reviewState;
    this.cancelled = params.cancelled;
    this.cancelledBy = params.cancelledBy;
    this.cancellationReason = params.cancellationReason;
    this.admin = params.admin;
    this.userIds = params.userIds ?? [];
    this.retainedUserIds = params.retainedUserIds;
    this.organizationIds = params.organizationIds ?? [];
    this.areaIds = params.areaIds ?? [];
    this.territoryIds = params.territoryIds ?? [];
    this.device = params.device;
    this.appVersion = params.appVersion;
    this.os = params.os;
    this.osVersion = params.osVersion;
    this.pairedDeviceIdentifier = params.pairedDeviceIdentifier;
    this.donorCenter = params.donorCenter;
    this.recipientCenter = params.recipientCenter;
    this.destination = params.destination;
    this.opoHandle = params.opoHandle;
    this.opo = params.opo;
    this.distance = params.distance;
    this.etaSetBy = params.etaSetBy;
    this.hasAdvancedTracking = params.hasAdvancedTracking ?? false;
    this.advancedTrackerId = params.advancedTrackerId;
    this.advancedTracking = params.advancedTracking ? new AdvancedTracking(params.advancedTracking) : void 0;
    this.hasFlights = params.hasFlights ?? false;
    this.flights = params.flights?.map((flight) => new Flight(flight)) ?? [];
    this.flightPosition = params.flightPosition ? new FlightPosition(params.flightPosition) : void 0;
    this.loggerPasskey = params.loggerPasskey;
    this.loggerSerialNo = params.loggerSerialNo;
    this.loggerSerialNumbers = params.loggerSerialNumbers;
    this.currentLoggerSerialNumbers = params.currentLoggerSerialNumbers;
    this.loggerConnected = params.loggerConnected;
    this.advancedTrackerIds = params.advancedTrackerIds;
    this.products = params.products;
    this.loggerState = params.loggerState;
    this.loggerConnectedBy = params.loggerConnectedBy;
    this.loggerData = params.loggerData ? new LoggerData(params.loggerData) : void 0;
    this.location = params.location ? new LoggerLocation(params.location) : void 0;
    this.loggers = params.loggers;
    this.ischemicTime = params.ischemicTime ? new SessionIschemicTime(params.ischemicTime) : void 0;
    this.metadata = params.metadata ? new SessionMetadata(params.metadata) : void 0;
    this.reportData = params.reportData ? new SessionReportData(params.reportData) : void 0;
    this.metrics = params.metrics ? new SessionMetrics(params.metrics) : void 0;
    this.pdfSummaryAvailable = params.pdfSummaryAvailable;
    this.lastTimelineEvent = params.lastTimelineEvent;
    this.donorOr = params.donorOr;
    this.recipientOr = params.recipientOr;
    this.phase = params.phase;
    this.offerStatus = params.offerStatus;
    this.rank = params.rank;
    this.sequence = params.sequence;
    this.attachments = params.attachments?.filter((att) => att != null).map((att) => new AttachmentRef(att)) ?? [];
    this.createdBy = params.createdBy;
    this.createdByOrganization = params.createdByOrganization;
    this.createdFrom = params.createdFrom;
    this.demo = params.demo ?? false;
    this.demoParentId = params.demoParentId;
    this.demoState = params.demoState ? new DemoState(params.demoState) : void 0;
    this.sharedAt = params.sharedAt instanceof Date ? params.sharedAt : params.sharedAt ? new Date(params.sharedAt) : void 0;
    this.cancelledAt = params.cancelledAt instanceof Date ? params.cancelledAt : params.cancelledAt ? new Date(params.cancelledAt) : void 0;
    this.eta = params.eta instanceof Date ? params.eta : params.eta ? new Date(params.eta) : void 0;
    this.createdAt = params.createdAt instanceof Date ? params.createdAt : params.createdAt ? new Date(params.createdAt) : /* @__PURE__ */ new Date();
    this.endedAt = params.endedAt instanceof Date ? params.endedAt : params.endedAt ? new Date(params.endedAt) : void 0;
  }
  // ─────────────────────────────────────────────────────────────────────────────
  // Methods
  // ─────────────────────────────────────────────────────────────────────────────
  /**
   * Converts session to an Organ instance for organ-specific operations.
   * Only available for v1-v6 sessions (v7+ sessions don't use organs subcollection).
   * @returns An Organ instance with session data
   * @throws Error if called on a v7+ session
   * @deprecated This method is for v6 and earlier sessions only. v7+ sessions use
   * centralized logger collections at `/loggers/{loggerId}` instead of the organs subcollection.
   */
  toOrgan() {
    if (this.schemaVersion >= 7) {
      throw new Error("toOrgan() is not available for v7+ sessions. The organs subcollection does not exist for v7 sessions.");
    }
    return new Organ({
      id: this.id,
      sessionId: this.id,
      product: this.product ?? "sherpapak-cts" /* SHERPAPAK */,
      organ: this.organ,
      advancedTrackerId: this.advancedTrackerId,
      reportData: this.reportData,
      logger: this.loggerSerialNo != null ? {
        serialNo: this.loggerSerialNo,
        passkey: this.loggerPasskey,
        state: this.loggerState,
        data: this.loggerData
      } : void 0
    });
  }
  /**
   * Converts session to a plain JSON object.
   * @returns Plain object representation of the session
   */
  toJSON() {
    return {
      // Identifiers
      id: this.id,
      unosId: this.unosId,
      matchRunId: this.matchRunId,
      key: this.key,
      joinKey: this.joinKey,
      // Session Hierarchy
      parentSessionId: this.parentSessionId,
      organPosition: this.organPosition,
      childSessionIds: this.childSessionIds,
      cancelledChildSessionIds: this.cancelledChildSessionIds,
      // Sharing
      shareKey: this.shareKey,
      shared: this.shared,
      sharedAt: this.sharedAt,
      sharedBy: this.sharedBy,
      // Session Configuration
      organ: this.organ,
      product: this.product,
      sessionType: this.sessionType,
      donationType: this.donationType,
      preservationMethods: this.preservationMethods,
      schemaVersion: this.schemaVersion,
      offerSchemaVersion: this.offerSchemaVersion,
      // Session State
      finished: this.finished,
      reviewState: this.reviewState,
      cancelled: this.cancelled,
      cancelledBy: this.cancelledBy,
      cancelledAt: this.cancelledAt,
      cancellationReason: this.cancellationReason,
      // Users & Access
      admin: this.admin,
      userIds: this.userIds,
      retainedUserIds: this.retainedUserIds,
      organizationIds: this.organizationIds,
      areaIds: this.areaIds,
      territoryIds: this.territoryIds,
      // Device Information
      device: this.device,
      appVersion: this.appVersion,
      os: this.os,
      osVersion: this.osVersion,
      pairedDeviceIdentifier: this.pairedDeviceIdentifier,
      // Location & Routing
      donorCenter: this.donorCenter,
      recipientCenter: this.recipientCenter,
      destination: this.destination,
      opoHandle: this.opoHandle,
      opo: this.opo,
      distance: this.distance,
      eta: this.eta,
      etaSetBy: this.etaSetBy,
      // Advanced Tracking & Flights
      hasAdvancedTracking: this.hasAdvancedTracking,
      advancedTrackerId: this.advancedTrackerId,
      advancedTracking: this.advancedTracking?.toJSON(),
      hasFlights: this.hasFlights,
      flights: this.flights?.map((flight) => flight.toJSON()) ?? [],
      flightPosition: this.flightPosition?.toJSON(),
      // Logger & Sensor Data
      loggerPasskey: this.loggerPasskey,
      loggerSerialNo: this.loggerSerialNo,
      loggerSerialNumbers: this.loggerSerialNumbers,
      currentLoggerSerialNumbers: this.currentLoggerSerialNumbers,
      loggerConnected: this.loggerConnected,
      advancedTrackerIds: this.advancedTrackerIds,
      products: this.products,
      loggerState: this.loggerState,
      loggerConnectedBy: this.loggerConnectedBy,
      loggerData: this.loggerData?.toJSON(),
      location: this.location?.toJSON(),
      loggers: this.loggers,
      ischemicTime: this.ischemicTime?.toJSON(),
      // Reports & Metrics
      metadata: this.metadata?.toJSON(),
      reportData: this.reportData?.toJSON(),
      metrics: this.metrics?.toJSON(),
      pdfSummaryAvailable: this.pdfSummaryAvailable,
      lastTimelineEvent: this.lastTimelineEvent,
      // OR Schedule
      donorOr: this.donorOr,
      recipientOr: this.recipientOr,
      // Offer Fields
      phase: this.phase,
      offerStatus: this.offerStatus,
      rank: this.rank,
      sequence: this.sequence,
      attachments: this.attachments.map((attachment) => attachment.toJSON()),
      // Timestamps & Creation
      createdAt: this.createdAt,
      createdBy: this.createdBy,
      createdByOrganization: this.createdByOrganization,
      createdFrom: this.createdFrom,
      endedAt: this.endedAt,
      // Demo Mode
      demo: this.demo,
      demoParentId: this.demoParentId,
      demoState: this.demoState?.toJSON()
    };
  }
  /**
   * Session duration in milliseconds.
   * @returns Duration or null if session hasn't ended
   */
  get duration() {
    return this.endedAt ? this.endedAt.getTime() - this.createdAt.getTime() : null;
  }
  /**
   * Computed session state based on logger and finished status.
   * @returns Current session state type
   */
  get state() {
    if (this.isCreatedState()) {
      return "CREATED" /* CREATED */;
    }
    if (this.isLoggerStartedState()) {
      return "LOGGER_STARTED" /* LOGGER_STARTED */;
    }
    if (this.isLoggerStoppedState()) {
      return "LOGGER_STOPPED" /* LOGGER_STOPPED */;
    }
    if (this.isFinishedState()) {
      return "FINISHED" /* FINISHED */;
    }
    return "UNKNOWN" /* UNKNOWN */;
  }
  /**
   * Serializes session to a JSON-safe object (removes class instances).
   * @returns JSON-safe plain object
   */
  serialize() {
    return JSON.parse(JSON.stringify(this));
  }
  // ─────────────────────────────────────────────────────────────────────────────
  // Private Methods
  // ─────────────────────────────────────────────────────────────────────────────
  isCreatedState() {
    return this.loggerState !== "STOPPED" && !this.finished && this.pairedDeviceIdentifier == null;
  }
  isLoggerStartedState() {
    return this.loggerState === "STARTED" && !this.finished;
  }
  isLoggerStoppedState() {
    return this.loggerState === "STOPPED" && !this.finished;
  }
  isFinishedState() {
    return this.finished;
  }
};
var SessionConverter = {
  toFirestore() {
    return {};
  },
  fromFirestore(doc42) {
    const data = doc42.data();
    return new Session({
      ...data,
      id: doc42.id,
      eta: toDate(data.eta),
      location: parseLocation(data.location),
      advancedTracking: parseAdvancedTracking(data.advancedTracking),
      flights: parseFlights(data.flights),
      flightPosition: parseFlightPosition(data.flightPosition),
      loggerData: parseLoggerData(data.loggerData, data.temperature),
      ischemicTime: parseSessionIschemicTime(data.ischemicTime),
      reportData: parseSessionReportData3(data.reportData),
      demoState: parseDemoState(data.demoState),
      donorOr: parseOrSchedule(data.donorOr),
      recipientOr: parseOrSchedule(data.recipientOr),
      createdAt: toDate(data.createdAt),
      endedAt: toDate(data.endedAt)
    });
  }
};
function parseOrSchedule(data) {
  if (!data) return void 0;
  return { ...data, time: toDate(data.time) };
}
function parseDemoState(data) {
  return data ? {
    ...data,
    originalStartTime: data.originalStartTime?.toDate != null && typeof data.originalStartTime?.toDate === "function" ? data.originalStartTime.toDate() : data.originalStartTime
  } : void 0;
}
function parseLocation(data) {
  return data ? { ...data, timestamp: data.timestamp?.toDate() } : void 0;
}
function parseAdvancedTracking(data) {
  return data ? { ...data, timestamp: data.timestamp?.toDate() } : void 0;
}
function parseFlights(data) {
  return data?.length > 0 ? data.map((flight) => ({
    ...flight,
    dataTimestamp: flight.dataTimestamp?.toDate(),
    actualArrival: flight.actualArrival?.toDate(),
    actualDeparture: flight.actualDeparture?.toDate(),
    estimatedDeparture: flight.estimatedDeparture?.toDate(),
    estimatedArrival: flight.estimatedArrival?.toDate(),
    plannedArrival: flight.plannedArrival?.toDate(),
    plannedDeparture: flight.plannedDeparture?.toDate()
  })).sort((a, b) => {
    return (a.actualDeparture?.getTime() ?? a.estimatedDeparture?.getTime() ?? a.plannedDeparture?.getTime() ?? 0) - (b.actualDeparture?.getTime() ?? b.estimatedDeparture?.getTime() ?? b.plannedDeparture?.getTime() ?? 0);
  }) : void 0;
}
function parseFlightPosition(data) {
  return data ? {
    ...data,
    timestamp: data.timestamp?.toDate()
  } : void 0;
}
function parseLoggerData(loggerData, temperature) {
  if (loggerData) {
    return {
      ...loggerData,
      timestamp: loggerData.timestamp?.toDate()
    };
  }
  if (temperature) {
    return {
      ...temperature,
      timestamp: temperature.timestamp?.toDate()
    };
  }
  return void 0;
}
function parseSessionIschemicTime(data) {
  return data ? {
    ...data,
    startTimestamp: data.startTimestamp?.toDate(),
    endTimestamp: data.endTimestamp?.toDate()
  } : void 0;
}
function parseSessionReportData3(data) {
  return data ? {
    ...data,
    loggerStartedAt: data.loggerStartedAt?.toDate(),
    loggerStoppedAt: data.loggerStoppedAt?.toDate(),
    firstReading: data.firstReading?.toDate(),
    lastReading: data.lastReading?.toDate(),
    timeOnPump: data.timeOnPump ? {
      ...data.timeOnPump,
      startTimestamp: data.timeOnPump.startTimestamp?.toDate(),
      endTimestamp: data.timeOnPump.endTimestamp?.toDate()
    } : void 0
  } : void 0;
}

// src/models/session-eta.ts
var SessionETA = class {
  id;
  destinationType;
  timestamp;
  userId;
  deleted;
  deletedAt;
  constructor(params) {
    this.id = params.id;
    this.destinationType = params.destinationType;
    this.userId = params.userId;
    this.deleted = params.deleted ?? false;
    this.timestamp = params.timestamp instanceof Date ? params.timestamp : new Date(params.timestamp);
    this.deletedAt = params.deletedAt instanceof Date ? params.deletedAt : params.deletedAt ? new Date(params.deletedAt) : null;
  }
  toJSON() {
    return {
      id: this.id,
      destinationType: this.destinationType,
      timestamp: this.timestamp,
      userId: this.userId,
      deleted: this.deleted,
      deletedAt: this.deletedAt
    };
  }
  serialize() {
    return JSON.parse(JSON.stringify(this));
  }
};
var SessionETAConverter = {
  toFirestore() {
    return {};
  },
  fromFirestore(doc42) {
    const data = doc42.data();
    return new SessionETA({
      ...data,
      id: doc42.id,
      timestamp: data.timestamp?.toDate(),
      deletedAt: data.deletedAt?.toDate() ?? null
    });
  }
};

// src/models/session-member.ts
var SessionMember = class {
  id;
  email;
  firstname;
  lastname;
  isParagonixUser;
  organization;
  organizationId;
  organizationHandle;
  organizationAccessRole;
  organizationRole;
  organizationTitle;
  joinedAt;
  deleted;
  deletedAt;
  muted;
  accessLevel;
  permissions;
  constructor(params) {
    this.id = params.id;
    this.email = params.email;
    this.firstname = params.firstname;
    this.lastname = params.lastname;
    this.isParagonixUser = params.isParagonixUser ?? false;
    this.organization = params.organization;
    this.organizationId = params.organizationId;
    this.organizationHandle = params.organizationHandle;
    this.organizationAccessRole = params.organizationAccessRole;
    this.organizationRole = params.organizationRole;
    this.organizationTitle = params.organizationTitle;
    this.joinedAt = typeof params.joinedAt === "string" ? new Date(params.joinedAt) : params.joinedAt;
    this.deleted = params.deleted ?? false;
    this.deletedAt = params.deletedAt != null ? typeof params.deletedAt === "string" ? new Date(params.deletedAt) : params.deletedAt : void 0;
    this.muted = params.muted ?? false;
    this.accessLevel = params.accessLevel;
    this.permissions = params.permissions;
  }
  toJSON() {
    return {
      id: this.id,
      email: this.email,
      firstname: this.firstname,
      lastname: this.lastname,
      isParagonixUser: this.isParagonixUser,
      organization: this.organization,
      organizationId: this.organizationId,
      organizationHandle: this.organizationHandle,
      organizationAccessRole: this.organizationAccessRole,
      organizationRole: this.organizationRole,
      organizationTitle: this.organizationTitle,
      joinedAt: this.joinedAt,
      deleted: this.deleted,
      deletedAt: this.deletedAt,
      muted: this.muted,
      accessLevel: this.accessLevel,
      permissions: this.permissions
    };
  }
  serialize() {
    return JSON.parse(JSON.stringify(this));
  }
  get fullname() {
    return `${this.firstname} ${this.lastname}`;
  }
  get initials() {
    return `${this.firstname?.charAt(0) ?? ""}${this.lastname?.charAt(0) ?? ""}`;
  }
};
var SessionMemberConverter = {
  toFirestore() {
    return {};
  },
  fromFirestore(doc42) {
    const data = doc42.data();
    return new SessionMember({ ...data, id: doc42.id, joinedAt: data.joinedAt?.toDate(), deletedAt: data.deletedAt?.toDate() });
  }
};

// src/models/shared-session.ts
var SharedSession = class {
  /** Shared session ID (matches the original session ID) */
  id;
  /** Whether the shared session is currently active */
  shared;
  /** Unique key used to construct the public share URL */
  shareKey;
  /** Timestamp when the session was shared */
  sharedAt;
  /** User ID of the person who shared the session */
  sharedBy;
  /** Organ position for breakout sessions */
  organPosition;
  /** Map of organ position to child breakout session IDs */
  childSessionIds;
  /** Whether the session has been completed */
  finished;
  /**
   * Device model that created the session.
   * @deprecated Not set for schemaVersion >= 7.
   */
  device;
  /**
   * App version that created the session.
   * @deprecated Not set for schemaVersion >= 7.
   */
  appVersion;
  /**
   * Operating system name.
   * @deprecated Not set for schemaVersion >= 7.
   */
  os;
  /**
   * Operating system version.
   * @deprecated Not set for schemaVersion >= 7.
   */
  osVersion;
  /** Organ type being transported */
  organ;
  /** Product type (e.g., KidneyVault) */
  product;
  /** Session type (clinical, demo, etc.) */
  sessionType;
  /** Donation type (DBD, DCD, etc.) */
  donationType;
  /** Schema version of the original session */
  schemaVersion;
  /** UNOS ID for the organ */
  unosId;
  /**
   * Sanitized loggers map for v7+ sessions.
   * @see {@link SharedSessionLoggers}
   */
  loggers;
  /** User IDs who have viewed the shared session */
  readByUserIds;
  /** Original session creation timestamp */
  createdAt;
  /** Session end timestamp */
  endedAt;
  constructor(params) {
    this.id = params.id;
    this.shared = params.shared ?? true;
    this.shareKey = params.shareKey;
    this.sharedAt = typeof params.sharedAt === "string" ? new Date(params.sharedAt) : params.sharedAt;
    this.sharedBy = params.sharedBy;
    this.organPosition = params.organPosition;
    this.childSessionIds = params.childSessionIds;
    this.finished = params.finished ?? false;
    this.device = params.device;
    this.appVersion = params.appVersion;
    this.os = params.os;
    this.osVersion = params.osVersion;
    this.organ = params.organ;
    this.product = params.product;
    this.sessionType = params.sessionType;
    this.donationType = params.donationType;
    this.schemaVersion = params.schemaVersion;
    this.unosId = params.unosId;
    this.loggers = params.loggers;
    this.readByUserIds = params.readByUserIds ?? [];
    this.createdAt = typeof params.createdAt === "string" ? new Date(params.createdAt) : params.createdAt;
    this.endedAt = typeof params.endedAt === "string" ? new Date(params.endedAt) : params.endedAt;
  }
  toJSON() {
    return {
      id: this.id,
      shared: this.shared,
      shareKey: this.shareKey,
      sharedAt: this.sharedAt,
      sharedBy: this.sharedBy,
      organPosition: this.organPosition,
      childSessionIds: this.childSessionIds,
      finished: this.finished,
      device: this.device,
      appVersion: this.appVersion,
      os: this.os,
      osVersion: this.osVersion,
      organ: this.organ,
      product: this.product,
      sessionType: this.sessionType,
      donationType: this.donationType,
      schemaVersion: this.schemaVersion,
      unosId: this.unosId,
      loggers: this.loggers,
      readByUserIds: this.readByUserIds,
      createdAt: this.createdAt,
      endedAt: this.endedAt
    };
  }
  serialize() {
    return JSON.parse(JSON.stringify(this));
  }
};
var SharedSessionConverter = {
  toFirestore() {
    return {};
  },
  fromFirestore(doc42) {
    const data = doc42.data();
    return new SharedSession({
      ...data,
      id: doc42.id,
      sharedAt: data.sharedAt?.toDate(),
      createdAt: data.createdAt?.toDate(),
      endedAt: data.endedAt?.toDate()
    });
  }
};

// src/models/timeline-entry.ts
var TimelineEntry2 = class {
  id;
  referenceId;
  type;
  title;
  annotations;
  senderId;
  skipPushNotification;
  timestamp;
  createdAt;
  committedAt;
  deleted;
  deletedAt;
  commentCount;
  unreadCount;
  constructor(params) {
    this.id = params.id;
    this.referenceId = params.referenceId;
    this.type = params.type;
    this.title = params.title;
    this.annotations = params.annotations ?? {};
    this.senderId = params.senderId;
    this.skipPushNotification = params.skipPushNotification ?? false;
    this.timestamp = typeof params.timestamp === "string" ? new Date(params.timestamp) : params.timestamp;
    this.createdAt = typeof params.createdAt === "string" ? new Date(params.createdAt) : params.createdAt;
    this.committedAt = typeof params.committedAt === "string" ? new Date(params.committedAt) : params.committedAt;
    this.deleted = params.deleted ?? false;
    this.deletedAt = typeof params.deletedAt === "string" ? new Date(params.deletedAt) : params.deletedAt;
    this.commentCount = params.commentCount ?? 0;
    this.unreadCount = params.unreadCount ?? {};
  }
  toJSON() {
    return {
      id: this.id,
      referenceId: this.referenceId,
      type: this.type,
      title: this.title,
      annotations: this.annotations,
      senderId: this.senderId,
      skipPushNotification: this.skipPushNotification,
      timestamp: this.timestamp,
      createdAt: this.createdAt,
      committedAt: this.committedAt,
      deleted: this.deleted,
      deletedAt: this.deletedAt,
      commentCount: this.commentCount,
      unreadCount: this.unreadCount
    };
  }
  serialize() {
    return JSON.parse(JSON.stringify(this));
  }
};
var TimelineEntryConverter = {
  toFirestore() {
    return {};
  },
  fromFirestore(doc42) {
    const data = doc42.data();
    return new TimelineEntry2({
      ...data,
      id: doc42.id,
      timestamp: data.timestamp?.toDate(),
      createdAt: data.createdAt?.toDate(),
      committedAt: data.committedAt?.toDate(),
      deletedAt: data.deletedAt?.toDate()
    });
  }
};
function compareTimelineEntriesDesc(a, b) {
  const timeDiff = b.timestamp.getTime() - a.timestamp.getTime();
  if (timeDiff !== 0) return timeDiff;
  return (b.createdAt?.getTime() ?? 0) - (a.createdAt?.getTime() ?? 0);
}

// src/models/timeline-entry-read-state.ts
var TimelineEntryReadState = class {
  userId;
  lastReadCommentCursor;
  constructor(params) {
    this.userId = params.userId;
    this.lastReadCommentCursor = params.lastReadCommentCursor ? new ReadCursor(params.lastReadCommentCursor) : void 0;
  }
  toJSON() {
    return {
      userId: this.userId,
      lastReadCommentCursor: this.lastReadCommentCursor?.toJSON()
    };
  }
  serialize() {
    return JSON.parse(JSON.stringify(this));
  }
};
var TimelineEntryReadStateConverter = {
  toFirestore() {
    return {};
  },
  fromFirestore(doc42) {
    const data = doc42.data();
    return new TimelineEntryReadState({
      userId: doc42.id,
      lastReadCommentCursor: data.lastReadCommentCursor ? { documentId: data.lastReadCommentCursor.documentId, readAt: data.lastReadCommentCursor.readAt?.toDate() } : void 0
    });
  }
};

// src/models/user-access.ts
var UserAccess = class {
  types;
  dev;
  admin;
  qa;
  global;
  areas;
  territories;
  organizations;
  features;
  restrictToOrgans;
  constructor(params) {
    this.types = params?.types ?? [];
    this.dev = params?.dev ?? false;
    this.admin = params?.admin ?? false;
    this.qa = params?.qa ?? false;
    this.global = params?.global ?? false;
    this.areas = params?.areas ?? [];
    this.territories = params?.territories ?? [];
    this.organizations = params?.organizations ?? [];
    this.features = params?.features ?? [];
    this.restrictToOrgans = params?.restrictToOrgans ?? [];
  }
  toJSON() {
    return {
      types: this.types,
      dev: this.dev,
      admin: this.admin,
      qa: this.qa,
      global: this.global,
      areas: this.areas,
      territories: this.territories,
      organizations: this.organizations,
      features: this.features,
      restrictToOrgans: this.restrictToOrgans
    };
  }
  serialize() {
    return JSON.parse(JSON.stringify(this));
  }
};

// src/utils/get-default-distance-unit.ts
function getDefaultDistanceUnit(localeHint) {
  const locale = localeHint ?? (typeof window !== "undefined" ? navigator.language ?? "en-US" : void 0);
  if (locale?.toLowerCase().startsWith("en-us")) {
    return "nautical" /* NAUTICAL */;
  }
  return "metric" /* METRIC */;
}

// src/models/user-settings.ts
var UserSettings = class {
  includeTimezone;
  force24hours;
  marketingMails;
  sessionReportMails;
  forceDistanceUnit;
  forceHeightUnit;
  forceWeightUnit;
  constructor(params, localeHint) {
    this.includeTimezone = params?.includeTimezone ?? false;
    this.force24hours = params?.force24hours ?? false;
    this.marketingMails = params?.marketingMails ?? false;
    this.sessionReportMails = params?.sessionReportMails ?? true;
    this.forceDistanceUnit = params?.forceDistanceUnit ?? getDefaultDistanceUnit(localeHint);
    this.forceHeightUnit = params?.forceHeightUnit ?? getDefaultMeasurementUnit(localeHint);
    this.forceWeightUnit = params?.forceWeightUnit ?? getDefaultMeasurementUnit(localeHint);
  }
  serialize() {
    return JSON.parse(JSON.stringify(this));
  }
  toJSON() {
    return {
      includeTimezone: this.includeTimezone,
      force24hours: this.force24hours,
      marketingMails: this.marketingMails,
      sessionReportMails: this.sessionReportMails,
      forceDistanceUnit: this.forceDistanceUnit,
      forceHeightUnit: this.forceHeightUnit,
      forceWeightUnit: this.forceWeightUnit
    };
  }
};

// src/models/user.ts
var User = class {
  id;
  access;
  email;
  emailVerified;
  acceptedTermsVersion;
  firstname;
  lastname;
  avatarFileName;
  avatarUrl;
  organization;
  role;
  settings;
  timezone;
  timezoneName;
  organizationIds;
  activeInOrganizationIds;
  needsOrganizationProfile;
  needsEmailVerification;
  createdAt;
  deactivatedAt;
  deactivationReason;
  inventoryLocationIds;
  isParagonixUser;
  twoFactorRequirement;
  hasSecurePassword;
  constructor(params, localeHint) {
    this.id = params.id;
    this.access = new UserAccess(params.access);
    this.email = params.email;
    this.emailVerified = params.emailVerified ?? false;
    this.hasSecurePassword = params.hasSecurePassword ?? false;
    this.firstname = params.firstname;
    this.lastname = params.lastname;
    this.avatarFileName = params.avatarFileName;
    this.avatarUrl = params.avatarUrl;
    this.organization = params.organization;
    this.role = params.role;
    this.acceptedTermsVersion = params.acceptedTermsVersion;
    this.settings = new UserSettings(params.settings, localeHint);
    this.timezone = params.timezone;
    this.timezoneName = params.timezoneName;
    this.organizationIds = params.organizationIds ?? [];
    this.activeInOrganizationIds = params.activeInOrganizationIds ?? [];
    this.needsOrganizationProfile = params.needsOrganizationProfile ?? false;
    this.needsEmailVerification = params.needsEmailVerification ?? false;
    this.createdAt = typeof params.createdAt === "string" ? new Date(params.createdAt) : params.createdAt;
    this.deactivatedAt = typeof params.deactivatedAt === "string" ? new Date(params.deactivatedAt) : params.deactivatedAt;
    this.deactivationReason = params.deactivationReason;
    this.inventoryLocationIds = params.inventoryLocationIds ?? [];
    this.isParagonixUser = params.isParagonixUser ?? false;
    this.twoFactorRequirement = params.twoFactorRequirement;
    this.hasSecurePassword = params.hasSecurePassword;
  }
  /**
   * User's full name (first + last).
   */
  get fullname() {
    return `${this.firstname} ${this.lastname}`;
  }
  /**
   * User's initials (first letter of first and last name).
   */
  get initials() {
    return `${this.firstname?.charAt(0) ?? ""}${this.lastname?.charAt(0) ?? ""}`;
  }
  serialize() {
    return JSON.parse(JSON.stringify(this));
  }
  toJSON() {
    return {
      id: this.id,
      access: this.access.toJSON(),
      email: this.email,
      emailVerified: this.emailVerified,
      firstname: this.firstname,
      lastname: this.lastname,
      avatarFileName: this.avatarFileName,
      avatarUrl: this.avatarUrl,
      organization: this.organization,
      role: this.role,
      acceptedTermsVersion: this.acceptedTermsVersion,
      settings: this.settings.toJSON(),
      timezone: this.timezone,
      timezoneName: this.timezoneName,
      organizationIds: this.organizationIds,
      activeInOrganizationIds: this.activeInOrganizationIds,
      needsOrganizationProfile: this.needsOrganizationProfile,
      needsEmailVerification: this.needsEmailVerification,
      createdAt: this.createdAt,
      deactivatedAt: this.deactivatedAt,
      deactivationReason: this.deactivationReason,
      inventoryLocationIds: this.inventoryLocationIds,
      isParagonixUser: this.isParagonixUser,
      twoFactorRequirement: this.twoFactorRequirement,
      hasSecurePassword: this.hasSecurePassword
    };
  }
};
var UserConverter = {
  toFirestore() {
    return {};
  },
  fromFirestore(doc42) {
    const data = doc42.data();
    return new User({ ...data, id: doc42.id, createdAt: data.createdAt?.toDate() });
  }
};

// src/models/user-session.ts
var UserSession = class {
  sessionId;
  userId;
  finished;
  muted;
  pinned;
  sessionUpdates;
  unreadTimelineEntryComments;
  unreadChatMessages;
  createdAt;
  updatedAt;
  constructor(params) {
    this.sessionId = params.sessionId;
    this.userId = params.userId;
    this.finished = params.finished ?? false;
    this.muted = params.muted ?? false;
    this.pinned = params.pinned ?? false;
    this.sessionUpdates = {
      unreadSessionUpdates: params.sessionUpdates?.unreadSessionUpdates ?? 0,
      lastReadCursor: params.sessionUpdates?.lastReadCursor ? new ReadCursor(params.sessionUpdates.lastReadCursor) : void 0
    };
    this.unreadTimelineEntryComments = params.unreadTimelineEntryComments ?? 0;
    this.unreadChatMessages = params.unreadChatMessages ?? 0;
    this.createdAt = toDate(params.createdAt) || /* @__PURE__ */ new Date();
    this.updatedAt = toDate(params.updatedAt);
  }
  toJSON() {
    return {
      sessionId: this.sessionId,
      userId: this.userId,
      finished: this.finished,
      muted: this.muted,
      pinned: this.pinned,
      sessionUpdates: {
        unreadSessionUpdates: this.sessionUpdates.unreadSessionUpdates,
        lastReadCursor: this.sessionUpdates.lastReadCursor?.toJSON()
      },
      unreadTimelineEntryComments: this.unreadTimelineEntryComments,
      unreadChatMessages: this.unreadChatMessages,
      createdAt: this.createdAt,
      updatedAt: this.updatedAt
    };
  }
  serialize() {
    return JSON.parse(JSON.stringify(this));
  }
};
var UserSessionConverter = {
  toFirestore() {
    return {};
  },
  fromFirestore(doc42) {
    const data = doc42.data();
    return new UserSession({
      ...data,
      sessionId: doc42.id,
      sessionUpdates: data.sessionUpdates ? {
        ...data.sessionUpdates,
        lastReadCursor: data.sessionUpdates.lastReadCursor ? { documentId: data.sessionUpdates.lastReadCursor.documentId, readAt: data.sessionUpdates.lastReadCursor.readAt?.toDate() } : void 0
      } : void 0,
      createdAt: data.createdAt?.toDate(),
      updatedAt: data.updatedAt?.toDate()
    });
  }
};

// src/client/attachments/use-attachment-storage.ts
function getAttachmentStorageRef(storage, collection53, collectionId, fileName) {
  return ref(storage, `${collection53}/${collectionId}/attachments/${fileName}`);
}
function createAttachmentRef(firestore, collection53, collectionId, file, thumbnailName, duration, dimensions) {
  const attachmentId = doc3(firestoreCollection(firestore, collection53, collectionId, "attachments")).id;
  const extension = file.name.split(".").pop() || "";
  const fileName = `${attachmentId}.${extension}`;
  let type;
  if (file.type.startsWith("image/")) {
    type = "IMAGE" /* IMAGE */;
  } else if (file.type.startsWith("video/")) {
    type = "VIDEO" /* VIDEO */;
  } else {
    type = "DOCUMENT" /* DOCUMENT */;
  }
  return {
    attachmentId,
    type,
    fileName,
    ...thumbnailName && { thumbnailName },
    ...duration && { duration },
    ...dimensions && { dimensions },
    status: "PENDING" /* PENDING */,
    fileSize: file.size,
    deleted: false,
    createdAt: Timestamp.now(),
    updatedAt: Timestamp.now()
  };
}
async function uploadAttachment(storage, collection53, collectionId, fileName, file, userId) {
  const storageRef = getAttachmentStorageRef(storage, collection53, collectionId, fileName);
  const attachmentId = fileName.split("_thumb.")[0].split(".")[0];
  await uploadBytes(storageRef, file, {
    customMetadata: {
      senderUid: userId,
      attachmentId
    }
  });
}
function uploadAttachmentWithProgress(storage, collection53, collectionId, fileName, file, userId) {
  const storageRef = getAttachmentStorageRef(storage, collection53, collectionId, fileName);
  const attachmentId = fileName.split("_thumb.")[0].split(".")[0];
  return uploadBytesResumable(storageRef, file, {
    customMetadata: {
      senderUid: userId,
      attachmentId
    }
  });
}
var urlCache = /* @__PURE__ */ new Map();
function getAttachmentUrl(storage, collection53, collectionId, fileName) {
  const storageRef = getAttachmentStorageRef(storage, collection53, collectionId, fileName);
  const cacheKey = storageRef.fullPath;
  const cached = urlCache.get(cacheKey);
  if (cached) return cached;
  const promise = getDownloadURL(storageRef).catch((error) => {
    urlCache.delete(cacheKey);
    throw error;
  });
  urlCache.set(cacheKey, promise);
  return promise;
}
async function getAttachmentBlob(storage, collection53, collectionId, fileName) {
  const storageRef = getAttachmentStorageRef(storage, collection53, collectionId, fileName);
  return getBlob(storageRef);
}

// src/client/auth.ts
import { browserLocalPersistence, signInWithEmailAndPassword as fbSignInWithEmailAndPassword, setPersistence } from "firebase/auth";
async function signInWithEmailAndPassword(auth, email, password) {
  await setPersistence(auth, browserLocalPersistence);
  return fbSignInWithEmailAndPassword(auth, email, password);
}
async function signOut(auth) {
  return auth.signOut();
}

// src/client/chats/use-delete-message.ts
import { doc as doc4, serverTimestamp, updateDoc as updateDoc2 } from "firebase/firestore";
async function deleteMessage(firestore, sessionId, chatId, messageId, userId) {
  const messageDoc = doc4(firestore, "sessions", sessionId, "chats", chatId, "messages", messageId);
  await updateDoc2(messageDoc, {
    deleted: true,
    deletedBy: userId,
    deletedAt: serverTimestamp()
  });
}

// src/client/chats/use-get-chat.ts
import { deleteField, doc as doc5, getDoc as getDoc2, serverTimestamp as serverTimestamp2, updateDoc as updateDoc3 } from "firebase/firestore";
import { useDocumentData } from "react-firebase-hooks/firestore";
function getChatRef(firestore, sessionId, chatId) {
  return doc5(firestore, "sessions", sessionId, "chats", chatId).withConverter(ChatConverter);
}
async function getChat(...args) {
  const snapshot = await getDoc2(getChatRef(...args));
  return snapshot.exists() ? snapshot.data() : null;
}
function useGetChat(...args) {
  return useDocumentData(getChatRef(...args));
}
async function updateChatDetails(firestore, sessionId, chatId, updates, meta) {
  const chatRef = doc5(firestore, "sessions", sessionId, "chats", chatId);
  await updateDoc3(chatRef, {
    meta,
    updatedAt: serverTimestamp2(),
    ...updates.name !== void 0 && { name: updates.name || deleteField() },
    ...updates.description !== void 0 && { description: updates.description || deleteField() },
    ...updates.avatar !== void 0 && { avatar: updates.avatar || deleteField() }
  });
}

// src/client/chats/use-get-chat-members.ts
import { collection, getDocs } from "firebase/firestore";
import { useMemo } from "react";
import { useCollectionData } from "react-firebase-hooks/firestore";
function getChatMembersQuery(firestore, sessionId, chatId) {
  return collection(firestore, "sessions", sessionId, "chats", chatId, "members").withConverter(ChatMemberConverter);
}
async function getChatMembers(...args) {
  const { docs } = await getDocs(getChatMembersQuery(...args));
  return docs.map((doc42) => doc42.data());
}
async function getActiveChatMembers(...args) {
  const members = await getChatMembers(...args);
  return members.filter((m) => !m.leftAt);
}
function useGetChatMembers(...args) {
  return useCollectionData(getChatMembersQuery(...args));
}
function useGetActiveChatMembers(...args) {
  const [members, loading, error] = useCollectionData(getChatMembersQuery(...args));
  const activeMembers = useMemo(() => members?.filter((m) => !m.leftAt), [members]);
  return [activeMembers, loading, error];
}

// src/client/chats/use-get-chats.ts
import { collection as collection2, getDocs as getDocs2 } from "firebase/firestore";
import { useCollectionData as useCollectionData2 } from "react-firebase-hooks/firestore";
function getSessionChatsQuery(firestore, sessionId) {
  return collection2(firestore, "sessions", sessionId, "chats").withConverter(ChatConverter);
}
async function getSessionChats(...args) {
  const { docs } = await getDocs2(getSessionChatsQuery(...args));
  return docs.map((doc42) => doc42.data());
}
function useGetSessionChats(...args) {
  return useCollectionData2(getSessionChatsQuery(...args));
}

// src/client/chats/use-get-message.ts
import { collection as collection3, getDocs as getDocs3 } from "firebase/firestore";
import { useCollectionData as useCollectionData3 } from "react-firebase-hooks/firestore";
function getMessageQuery(firestore, sessionId, chatId, messageId) {
  return collection3(firestore, "sessions", sessionId, "chats", chatId, "messages", messageId).withConverter(MessageConverter);
}
async function getMessage(...args) {
  const { docs } = await getDocs3(getMessageQuery(...args));
  return docs.map((doc42) => doc42.data());
}
function useGetMessage(...args) {
  return useCollectionData3(getMessageQuery(...args));
}

// src/client/chats/use-get-message-receipt.ts
import { collection as collection4, getDocs as getDocs4 } from "firebase/firestore";
import { useCollectionData as useCollectionData4 } from "react-firebase-hooks/firestore";
function getMessageReceiptsQuery(firestore, sessionId, chatId, messageId) {
  return collection4(firestore, "sessions", sessionId, "chats", chatId, "messages", messageId, "receipts").withConverter(
    MessageReceiptConverter
  );
}
async function getMessageReceipts(...args) {
  const { docs } = await getDocs4(getMessageReceiptsQuery(...args));
  return docs.map((doc42) => doc42.data());
}
function useGetMessageReceipts(...args) {
  return useCollectionData4(getMessageReceiptsQuery(...args));
}

// src/client/chats/use-get-messages.ts
import { and, collection as collection5, getDocs as getDocs5, limit, or, orderBy, query, startAfter, where } from "firebase/firestore";
import { useCollectionData as useCollectionData5 } from "react-firebase-hooks/firestore";
var BASE_MESSAGE_FILTERS = [
  where("deleted", "==", false),
  where("deliveryStatus", "in", ["SENT" /* SENT */, "DELIVERED" /* DELIVERED */, "READ" /* READ */])
];
function getMessagesQuery(firestore, sessionId, chatId, constraints = []) {
  const baseQuery = collection5(firestore, "sessions", sessionId, "chats", chatId, "messages").withConverter(MessageConverter);
  return query(baseQuery, ...constraints);
}
function getLiveMessagesQuery(firestore, sessionId, chatId, pageSize = 80, currentUserId) {
  const baseQuery = collection5(firestore, "sessions", sessionId, "chats", chatId, "messages").withConverter(MessageConverter);
  if (!currentUserId) {
    return query(baseQuery, ...BASE_MESSAGE_FILTERS, orderBy("timestamp", "desc"), limit(pageSize));
  }
  return query(
    baseQuery,
    or(
      where("senderId", "==", currentUserId),
      // All messages of current user (including deleted)
      and(
        where("deleted", "==", false),
        where("deliveryStatus", "in", ["SENT" /* SENT */, "DELIVERED" /* DELIVERED */, "READ" /* READ */])
      )
    ),
    orderBy("timestamp", "desc"),
    limit(pageSize)
  );
}
function getEarlierMessagesQuery(firestore, sessionId, chatId, oldestMessageTimestamp, pageSize = 80) {
  return getMessagesQuery(firestore, sessionId, chatId, [
    ...BASE_MESSAGE_FILTERS,
    orderBy("timestamp", "desc"),
    startAfter(oldestMessageTimestamp),
    limit(pageSize)
  ]);
}
async function getMessages(...args) {
  const { docs } = await getDocs5(getMessagesQuery(...args));
  return docs.map((doc42) => doc42.data());
}
async function getEarlierMessages(firestore, sessionId, chatId, oldestMessageTimestamp, pageSize = 80) {
  return getMessages(firestore, sessionId, chatId, [
    ...BASE_MESSAGE_FILTERS,
    orderBy("timestamp", "desc"),
    startAfter(oldestMessageTimestamp),
    limit(pageSize)
  ]);
}
function useGetMessages(...args) {
  return useCollectionData5(getMessagesQuery(...args));
}
function useGetLiveMessages(firestore, sessionId, chatId, pageSize = 80) {
  return useGetMessages(firestore, sessionId, chatId, [...BASE_MESSAGE_FILTERS, orderBy("timestamp", "desc"), limit(pageSize)]);
}
function useMessagesFromQuery(query35) {
  return useCollectionData5(query35);
}

// src/client/chats/use-get-user-chats.ts
import { collection as collection6, doc as doc6, getDocs as getDocs6 } from "firebase/firestore";
import { useCollectionData as useCollectionData6, useDocumentData as useDocumentData2 } from "react-firebase-hooks/firestore";
function getUserChatsQuery(firestore, userId) {
  return collection6(firestore, "users", userId, "chats").withConverter(UserChatConverter);
}
async function getUserChats(...args) {
  const { docs } = await getDocs6(getUserChatsQuery(...args));
  return docs.map((doc42) => doc42.data());
}
function useGetUserChats(...args) {
  return useCollectionData6(getUserChatsQuery(...args));
}
function getUserChatRef(firestore, userId, chatId) {
  return doc6(firestore, "users", userId, "chats", chatId).withConverter(UserChatConverter);
}
function useGetUserChat(...args) {
  return useDocumentData2(getUserChatRef(...args));
}

// src/client/chats/use-message-reactions.ts
import { deleteField as deleteField2, doc as doc7, serverTimestamp as serverTimestamp3, updateDoc as updateDoc4 } from "firebase/firestore";
async function setMessageReaction(firestore, sessionId, chatId, messageId, userId, emoji) {
  const messageDoc = doc7(firestore, "sessions", sessionId, "chats", chatId, "messages", messageId);
  if (emoji) {
    await updateDoc4(messageDoc, {
      [`reactions.${userId}`]: {
        emoji,
        updatedAt: serverTimestamp3()
      }
    });
  } else {
    await updateDoc4(messageDoc, {
      [`reactions.${userId}`]: deleteField2()
    });
  }
}
async function removeMessageReaction(firestore, sessionId, chatId, messageId, userId) {
  const messageDoc = doc7(firestore, "sessions", sessionId, "chats", chatId, "messages", messageId);
  await updateDoc4(messageDoc, {
    [`reactions.${userId}`]: deleteField2()
  });
}

// src/client/chats/use-send-message.ts
import { addDoc, collection as collection7, doc as doc8, serverTimestamp as serverTimestamp4, setDoc } from "firebase/firestore";
function generateMessageId(firestore, sessionId, chatId) {
  const messagesCollection = sendMessageQuery(firestore, sessionId, chatId);
  return doc8(messagesCollection).id;
}
function sendMessageQuery(firestore, sessionId, chatId) {
  return collection7(firestore, "sessions", sessionId, "chats", chatId, "messages");
}
async function sendMessage(firestore, sessionId, chatId, senderId, content, attachments, replyTo, messageId, mentionsParams) {
  if (!content.trim() && (!attachments || attachments.length === 0)) {
    throw new Error("Message must have either content or attachments");
  }
  const mentionRanges = mentionsParams?.mentionRanges?.length ? mentionsParams.mentionRanges : void 0;
  const messageData = {
    parentId: chatId,
    senderId,
    content: content.trim(),
    createdAt: serverTimestamp4(),
    timestamp: serverTimestamp4(),
    deliveryStatus: "PENDING" /* PENDING */,
    deleted: false,
    type: "TEXT" /* TEXT */,
    ...attachments && { attachments },
    ...replyTo && { replyTo },
    ...mentionRanges && { mentionRanges }
  };
  const messagesCollection = sendMessageQuery(firestore, sessionId, chatId);
  if (messageId) {
    const docRef = doc8(messagesCollection, messageId);
    await setDoc(docRef, messageData);
    return messageId;
  } else {
    const docRef = await addDoc(messagesCollection, messageData);
    return docRef.id;
  }
}

// src/client/drafts/set-draft-donor-data.ts
import { doc as doc9, setDoc as setDoc2 } from "firebase/firestore";
async function setDraftDonorData(firestore, draftId, data) {
  await setDoc2(doc9(firestore, "drafts", draftId, "data", "donor"), data);
}

// src/client/drafts/set-draft-notes-data.ts
import { doc as doc10, setDoc as setDoc3 } from "firebase/firestore";
async function setDraftNotesData(firestore, draftId, data) {
  await setDoc3(doc10(firestore, "drafts", draftId, "data", "notes"), data, { merge: true });
}

// src/client/drafts/set-draft-recipient-data.ts
import { doc as doc11, setDoc as setDoc4 } from "firebase/firestore";
async function setDraftRecipientData(firestore, draftId, data) {
  await setDoc4(doc11(firestore, "drafts", draftId, "data", "recipient"), data);
}

// src/client/drafts/update-draft-document.ts
import { doc as doc12, updateDoc as updateDoc5 } from "firebase/firestore";
async function updateDraftDocument(firestore, draftId, data) {
  await updateDoc5(doc12(firestore, "drafts", draftId), data);
}

// src/client/drafts/upload-draft-pdf.ts
import { ref as ref2, uploadBytes as uploadBytes2 } from "firebase/storage";
async function uploadDraftPdf(storage, draftId, file) {
  const storageRef = ref2(storage, `drafts/${draftId}/donor_summary.pdf`);
  await uploadBytes2(storageRef, file);
}

// src/client/drafts/use-get-draft.ts
import { doc as doc13, getDoc as getDoc3 } from "firebase/firestore";
import { useDocumentData as useDocumentData3 } from "react-firebase-hooks/firestore";
function getDraftQuery(firestore, draftId) {
  return doc13(firestore, "drafts", draftId).withConverter(DraftConverter);
}
async function getDraft(...args) {
  const doc42 = await getDoc3(getDraftQuery(...args));
  return doc42.data();
}
function useGetDraft(...args) {
  return useDocumentData3(getDraftQuery(...args));
}

// src/client/https-callable.ts
import { httpsCallable as firebaseHttpsCallable } from "firebase/functions";
function httpsCallable(functions, name, options) {
  const callable = options === void 0 ? firebaseHttpsCallable(functions, name) : firebaseHttpsCallable(functions, name, options);
  return async (data) => {
    try {
      return await callable(data);
    } catch (error) {
      if (isOfflineError(error)) {
        throw new Error("You appear to be offline. Please check your connection and try again.");
      }
      throw error;
    }
  };
}
function isOfflineError(error) {
  const isGenericInternalError = error?.code === "functions/internal" && error?.message === "internal" && error?.details === void 0;
  return isGenericInternalError && typeof window !== "undefined" && typeof navigator !== "undefined" && typeof navigator.onLine === "boolean" && !navigator.onLine;
}

// src/client/functions.ts
function createCallableMeta(functionVersion, meta) {
  return { ...meta ?? {}, functionVersion };
}
async function sendEmailVerificationFn(functions) {
  const fn = httpsCallable(functions, "sendEmailVerification");
  return fn({ source: "web" });
}
async function confirmEmailVerificationFn(functions, code) {
  const fn = httpsCallable(functions, "confirmEmailVerification");
  return fn({ code });
}
async function sendPasswordResetFn(functions, email) {
  const fn = httpsCallable(functions, "sendPasswordReset");
  return fn({ email, source: "web" });
}
function startOrganizationValidationFn(functions, organizationId) {
  const fn = httpsCallable(functions, "startOrganizationValidation");
  return fn({ organizationId });
}
async function confirmOrganizationValidationFn(functions, organizationId, userIds) {
  const fn = httpsCallable(functions, "confirmOrganizationValidation");
  return fn({ organizationId, userIds });
}
function inviteToOrganizationFn(functions, organizationId, email) {
  const fn = httpsCallable(functions, "inviteToOrganization");
  return fn({ organizationId, email });
}
function sendOrganizationMemberInviteFn(functions, organizationId, userIds) {
  const fn = httpsCallable(functions, "sendOrganizationMemberInvite");
  return fn({ organizationId, userIds });
}
async function requestOrganizationAccessFn(functions, params) {
  const fn = httpsCallable(functions, "requestOrganizationAccess");
  return fn(params);
}
function grantOrganizationAccessFn(functions, organizationId, userId) {
  const fn = httpsCallable(functions, "grantOrganizationAccess");
  return fn({ organizationId, userIds: [userId] });
}
function rejectOrganizationAccessRequestFn(functions, organizationId, userId) {
  const fn = httpsCallable(functions, "rejectOrganizationAccessRequest");
  return fn({ organizationId, userIds: [userId] });
}
async function cancelOrganizationAccessRequestFn(functions, organizationId, userId) {
  const fn = httpsCallable(functions, "cancelOrganizationAccessRequest");
  return fn({ organizationId, userId });
}
async function joinOrganizationFn(functions, params) {
  const fn = httpsCallable(functions, "joinOrganization");
  return fn(params);
}
async function leaveOrganizationFn(functions, organizationId) {
  const fn = httpsCallable(functions, "leaveOrganization");
  return fn({ organizationId });
}
async function searchAirports(functions, query35) {
  const fn = httpsCallable(functions, "searchAirports");
  return fn({ query: query35 });
}
async function exportSessionsToSheetFn(functions, sessionIds) {
  const fn = httpsCallable(functions, "exportSessionsToSheet", { timeout: 3e5 });
  return fn({ sessionIds });
}
async function createBreakoutSessionFn(functions, params) {
  const fn = httpsCallable(functions, "createBreakoutSession");
  return fn(params);
}
async function cancelBreakoutSessionFn(functions, sessionId, reason) {
  const fn = httpsCallable(functions, "cancelBreakoutSession");
  return fn({ sessionId, reason });
}
async function createSharedSessionFn(functions, sessionId) {
  const fn = httpsCallable(functions, "createSharedSession");
  return fn({ sessionId });
}
async function unlinkLoggerFn(functions, params) {
  const fn = httpsCallable(functions, "unlinkLogger");
  return fn(params);
}
function activateOrganizationMemberFn(functions, organizationId, userId) {
  const fn = httpsCallable(functions, "activateOrganizationMember");
  return fn({ organizationId, userId });
}
function deactivateOrganizationMemberFn(functions, organizationId, userId) {
  const fn = httpsCallable(functions, "deactivateOrganizationMember");
  return fn({ organizationId, userId });
}
async function addPotentialSessionsFn(functions, organizationId, sessionIds, notes) {
  const callable = httpsCallable(functions, "addPotentialSessions");
  const { data } = await callable({
    meta: { functionVersion: "v1" },
    data: { organizationId, sessionIds, notes: notes || void 0 }
  });
  return data;
}
async function requestPotentialSessionReviewFn(functions, organizationId, sessionIds) {
  const fn = httpsCallable(functions, "requestPotentialSessionReview");
  const { data } = await fn({ organizationId, sessionIds });
  return data;
}
async function approvePotentialSessionFn(functions, organizationId, sessionId) {
  const callable = httpsCallable(
    functions,
    "approvePotentialSession"
  );
  const { data } = await callable({
    meta: { functionVersion: "v1" },
    data: { organizationId, sessionId }
  });
  return data;
}
async function rejectPotentialSessionFn(functions, organizationId, sessionId, reason) {
  const callable = httpsCallable(
    functions,
    "rejectPotentialSession"
  );
  const { data } = await callable({
    meta: { functionVersion: "v1" },
    data: { organizationId, sessionId, reason }
  });
  return data;
}
async function identifyPotentialSessionsFn(functions, organizationId) {
  const callable = httpsCallable(
    functions,
    "identifyPotentialSessions"
  );
  const { data } = await callable({
    meta: { functionVersion: "v1" },
    data: { organizationId }
  });
  return data;
}
async function createChatFn(functions, params) {
  const fn = httpsCallable(functions, "createChat");
  const { data } = await fn(params);
  return data;
}
async function addChatMembersFn(functions, params) {
  const fn = httpsCallable(functions, "addChatMembers");
  const { data } = await fn(params);
  return data;
}
async function removeChatMemberFn(functions, params) {
  const fn = httpsCallable(functions, "removeChatMembers");
  const { data } = await fn(params);
  return data;
}
async function addManualFlightFn(functions, params, meta) {
  const fn = httpsCallable(
    functions,
    "addManualFlight"
  );
  const { data } = await fn({ meta: createCallableMeta("v1", meta), data: params });
  return data;
}
async function searchFlightsFn(functions, params) {
  const fn = httpsCallable(
    functions,
    "searchFlights"
  );
  const { data } = await fn({ meta: { functionVersion: "v2" }, data: params });
  return data;
}
async function addFlightFn(functions, params, meta) {
  const fn = httpsCallable(functions, "addFlight");
  const { data } = await fn({ meta: createCallableMeta("v2", meta), data: params });
  return data;
}
async function removeFlightFn(functions, params, meta) {
  const fn = httpsCallable(functions, "removeFlight");
  const { data } = await fn({ meta: createCallableMeta("v2", meta), data: params });
  return data;
}
async function setReadCursorFn(functions, documentPath) {
  const callable = httpsCallable(functions, "setReadCursor");
  await callable({ meta: { functionVersion: "v1" }, data: { documentPath } });
}
async function getOrganizationMembersForSessionFn(functions, sessionId) {
  const callable = httpsCallable(
    functions,
    "getOrganizationMembersForSession"
  );
  const { data } = await callable({
    meta: { functionVersion: "v1" },
    data: { sessionId }
  });
  return data;
}
async function addMembersToSessionFn(functions, params) {
  const callable = httpsCallable(
    functions,
    "addMembersToSession"
  );
  const { data } = await callable({
    meta: { functionVersion: "v1" },
    data: params
  });
  return data;
}

// src/client/inventory.ts
import { collection as collection8, doc as doc14, limit as firestoreLimit, orderBy as orderBy2, query as query2, where as where2 } from "firebase/firestore";
import { useMemo as useMemo2 } from "react";
import { useCollectionData as useCollectionData7, useDocumentData as useDocumentData4 } from "react-firebase-hooks/firestore";
function useGetInventoryLocations(firestore, options = {}) {
  const { types, limit: limitN } = options;
  const typesKey = types?.join(",") ?? "";
  const q = useMemo2(() => {
    const base = collection8(firestore, "inventory_locations").withConverter(InventoryLocationConverter);
    const constraints = [orderBy2("name")];
    if (types && types.length > 0) constraints.push(where2("type", "in", types));
    if (limitN !== void 0) constraints.push(firestoreLimit(limitN));
    return query2(base, ...constraints);
  }, [firestore, typesKey, limitN]);
  return useCollectionData7(q);
}
function getInventoryLocationRef(firestore, locationId) {
  return doc14(firestore, "inventory_locations", locationId).withConverter(InventoryLocationConverter);
}
function useGetInventoryLocation(firestore, locationId) {
  const ref3 = useMemo2(() => getInventoryLocationRef(firestore, locationId), [firestore, locationId]);
  return useDocumentData4(ref3);
}

// src/client/loggers/use-get-logger.ts
import { doc as doc15, getDoc as getDoc4 } from "firebase/firestore";
import { useDocumentData as useDocumentData5 } from "react-firebase-hooks/firestore";
function getLoggerQuery(firestore, loggerId) {
  return doc15(firestore, "loggers", loggerId).withConverter(LoggerConverter);
}
async function getLogger(...args) {
  const doc42 = await getDoc4(getLoggerQuery(...args));
  return doc42.data();
}
function useGetLogger(...args) {
  return useDocumentData5(getLoggerQuery(...args));
}

// src/client/loggers/use-get-logger-advanced-tracking.ts
import { collection as collection9, getDocs as getDocs7, orderBy as orderBy3, query as query3 } from "firebase/firestore";
import { useCollectionData as useCollectionData8 } from "react-firebase-hooks/firestore";
function getLoggerAdvancedTrackingQuery(firestore, loggerId) {
  return query3(collection9(firestore, "loggers", loggerId, "vantagePoints"), orderBy3("timestamp", "asc")).withConverter(
    AdvancedTrackingConverter
  );
}
async function getLoggerAdvancedTracking(...args) {
  const { docs } = await getDocs7(getLoggerAdvancedTrackingQuery(...args));
  return docs.map((doc42) => doc42.data());
}
function useGetLoggerAdvancedTracking(...args) {
  return useCollectionData8(getLoggerAdvancedTrackingQuery(...args));
}

// src/client/loggers/use-get-logger-app-locations.ts
import { collection as collection10, getDocs as getDocs8, orderBy as orderBy4, query as query4 } from "firebase/firestore";
import { useCollectionData as useCollectionData9 } from "react-firebase-hooks/firestore";
function getLoggerAppLocationsQuery(firestore, loggerId) {
  return query4(collection10(firestore, "loggers", loggerId, "appLocations"), orderBy4("timestamp", "asc")).withConverter(
    LoggerAppLocationConverter
  );
}
async function getLoggerAppLocations(...args) {
  const { docs } = await getDocs8(getLoggerAppLocationsQuery(...args));
  return docs.map((doc42) => doc42.data());
}
function useGetLoggerAppLocations(...args) {
  return useCollectionData9(getLoggerAppLocationsQuery(...args));
}

// src/client/loggers/use-get-logger-app-log-pages.ts
import { collection as collection11, getDocs as getDocs9, orderBy as orderBy5, query as query5 } from "firebase/firestore";
import { useCollectionData as useCollectionData10 } from "react-firebase-hooks/firestore";
function getLoggerAppLogPagesQuery(firestore, loggerId) {
  return query5(collection11(firestore, "loggers", loggerId, "appLogs"), orderBy5("order", "asc")).withConverter(
    LoggerAppLogPageConverter
  );
}
async function getLoggerAppLogPages(...args) {
  const { docs } = await getDocs9(getLoggerAppLogPagesQuery(...args));
  return docs.map((doc42) => doc42.data());
}
function useGetLoggerAppLogPages(...args) {
  return useCollectionData10(getLoggerAppLogPagesQuery(...args));
}

// src/client/loggers/use-get-logger-chart.ts
import { doc as doc16, getDoc as getDoc5 } from "firebase/firestore";
import { useDocumentData as useDocumentData6 } from "react-firebase-hooks/firestore";
function getLoggerChartDataQuery(firestore, loggerId) {
  return doc16(firestore, "loggers", loggerId, "chart", "data").withConverter(LoggerChartDataConverter);
}
async function getLoggerChartData(...args) {
  const doc42 = await getDoc5(getLoggerChartDataQuery(...args));
  return doc42.data();
}
function useGetLoggerChartData(...args) {
  return useDocumentData6(getLoggerChartDataQuery(...args));
}

// src/client/loggers/use-get-logger-last-advanced-tracking.ts
import { collection as collection12, getDocs as getDocs10, limit as limit2, orderBy as orderBy6, query as query6 } from "firebase/firestore";
import { useCollectionData as useCollectionData11 } from "react-firebase-hooks/firestore";
function getLoggerLastAdvancedTrackingQuery(firestore, loggerId) {
  return query6(
    collection12(firestore, "loggers", loggerId, "vantagePoints"),
    orderBy6("timestamp", "desc"),
    limit2(1)
  ).withConverter(AdvancedTrackingConverter);
}
async function getLoggerLastAdvancedTracking(...args) {
  const { docs } = await getDocs10(getLoggerLastAdvancedTrackingQuery(...args));
  return docs.map((doc42) => doc42.data())[0];
}
function useGetLoggerLastAdvancedTracking(...args) {
  return useCollectionData11(getLoggerLastAdvancedTrackingQuery(...args));
}

// src/client/loggers/use-get-logger-last-app-location.ts
import { collection as collection13, getDocs as getDocs11, limit as limit3, orderBy as orderBy7, query as query7 } from "firebase/firestore";
import { useCollectionData as useCollectionData12 } from "react-firebase-hooks/firestore";
function getLoggerLastAppLocationQuery(firestore, loggerId) {
  return query7(
    collection13(firestore, "loggers", loggerId, "appLocations"),
    orderBy7("timestamp", "desc"),
    limit3(1)
  ).withConverter(LoggerAppLocationConverter);
}
async function getLoggerLastAppLocation(...args) {
  const { docs } = await getDocs11(getLoggerLastAppLocationQuery(...args));
  return docs.map((doc42) => doc42.data())[0];
}
function useGetLoggerLastAppLocation(...args) {
  return useCollectionData12(getLoggerLastAppLocationQuery(...args));
}

// src/client/loggers/use-get-logger-last-app-log-page.ts
import { collection as collection14, getDocs as getDocs12, limit as limit4, orderBy as orderBy8, query as query8 } from "firebase/firestore";
import { useCollectionData as useCollectionData13 } from "react-firebase-hooks/firestore";
function getLoggerLastAppLogPageQuery(firestore, loggerId) {
  return query8(collection14(firestore, "loggers", loggerId, "appLogs"), orderBy8("order", "desc"), limit4(1)).withConverter(
    LoggerAppLogPageConverter
  );
}
async function getLoggerLastAppLogPage(...args) {
  const { docs } = await getDocs12(getLoggerLastAppLogPageQuery(...args));
  return docs.map((doc42) => doc42.data());
}
function useGetLoggerLastAppLogPage(...args) {
  return useCollectionData13(getLoggerLastAppLogPageQuery(...args));
}

// src/client/loggers/use-get-loggers.ts
import { collection as collection15, documentId, getDocs as getDocs13, query as query9, where as where3 } from "firebase/firestore";
import { useCollectionData as useCollectionData14 } from "react-firebase-hooks/firestore";
function getLoggersQuery(firestore, loggerIds) {
  const ids = loggerIds.length > 0 ? loggerIds : ["__none__"];
  return query9(collection15(firestore, "loggers").withConverter(LoggerConverter), where3(documentId(), "in", ids));
}
async function getLoggers(...args) {
  const { docs } = await getDocs13(getLoggersQuery(...args));
  return docs.map((doc42) => doc42.data());
}
function useGetLoggers(...args) {
  return useCollectionData14(getLoggersQuery(...args));
}

// src/client/messaging.ts
import { doc as doc17, setDoc as setDoc5 } from "firebase/firestore";
import { getToken, onMessage } from "firebase/messaging";
var registeredPushTokenKeys = /* @__PURE__ */ new Set();
async function getMessagingToken(config, vapidKey, messaging, firestore, userId) {
  const serviceWorkerRegistration = await registerMessagingServiceWorker(config);
  const currentToken = await getToken(messaging, {
    vapidKey,
    serviceWorkerRegistration
  });
  if (currentToken != null) {
    const tokenKey = `${userId}:${currentToken}`;
    if (!registeredPushTokenKeys.has(tokenKey)) {
      await setDoc5(doc17(firestore, "users", userId, "pushTokens", currentToken), {
        platform: "web",
        appId: "PARAGONIX"
      });
      registeredPushTokenKeys.add(tokenKey);
    }
  }
  return currentToken;
}
function onFirebaseMessage(messaging, callback) {
  return onMessage(messaging, callback);
}
async function registerMessagingServiceWorker(config) {
  const firebaseConfig = encodeURIComponent(JSON.stringify(config));
  return navigator.serviceWorker.register(`/firebase-messaging-sw.js?firebaseConfig=${firebaseConfig}`);
}

// src/client/organizations.ts
import {
  addDoc as addDoc2,
  collection as collection16,
  doc as doc18,
  getDoc as getDoc6,
  getDocs as getDocs14,
  limit as limit5,
  query as query10,
  serverTimestamp as serverTimestamp5,
  updateDoc as updateDoc6,
  where as where4
} from "firebase/firestore";
import { useCollectionData as useCollectionData15, useDocumentData as useDocumentData7 } from "react-firebase-hooks/firestore";
function getOrganizationsQuery(firestore) {
  return collection16(firestore, "organizations").withConverter(OrganizationConverter);
}
async function getOrganizations(...args) {
  const { docs } = await getDocs14(getOrganizationsQuery(...args));
  return docs.map((doc42) => doc42.data());
}
function useGetOrganizations(...args) {
  return useCollectionData15(getOrganizationsQuery(...args));
}
function getOrganizationQuery(firestore, organizationId) {
  return doc18(firestore, "organizations", organizationId).withConverter(OrganizationConverter);
}
async function getOrganization(...args) {
  const doc42 = await getDoc6(getOrganizationQuery(...args));
  return doc42.data();
}
function useGetOrganization(...args) {
  return useDocumentData7(getOrganizationQuery(...args));
}
function getOrganizationPreselectedMembersQuery(firestore, organizationId) {
  return collection16(firestore, "organizations", organizationId, "preselectedUsers").withConverter(OrganizationPreselectedMemberConverter);
}
async function getOrganizationPreselectedMembers(...args) {
  const { docs } = await getDocs14(getOrganizationPreselectedMembersQuery(...args));
  return docs.map((doc42) => doc42.data());
}
function useGetOrganizationPreselectedMembers(...args) {
  return useCollectionData15(getOrganizationPreselectedMembersQuery(...args));
}
function getOrganizationPreselectedMemberQuery(firestore, organizationId, userId) {
  return doc18(firestore, "organizations", organizationId, "preselectedUsers", userId).withConverter(OrganizationPreselectedMemberConverter);
}
async function getOrganizationPreselectedMember(...args) {
  const doc42 = await getDoc6(getOrganizationPreselectedMemberQuery(...args));
  return doc42.data();
}
function useGetOrganizationPreselectedMember(...args) {
  return useDocumentData7(getOrganizationPreselectedMemberQuery(...args));
}
function getOrganizationMembersQuery(firestore, organizationId) {
  return collection16(firestore, "organizations", organizationId, "users").withConverter(OrganizationMemberConverter);
}
async function getOrganizationMembers(...args) {
  const { docs } = await getDocs14(getOrganizationMembersQuery(...args));
  return docs.map((doc42) => doc42.data());
}
function useGetOrganizationMembers(...args) {
  return useCollectionData15(getOrganizationMembersQuery(...args));
}
function getOrganizationMemberQuery(firestore, organizationId, userId) {
  return doc18(firestore, "organizations", organizationId, "users", userId).withConverter(OrganizationMemberConverter);
}
async function getOrganizationMember(...args) {
  const doc42 = await getDoc6(getOrganizationMemberQuery(...args));
  return doc42.data();
}
function useGetOrganizationMember(...args) {
  return useDocumentData7(getOrganizationMemberQuery(...args));
}
function getOrganizationUnregisteredInvitesQuery(firestore, organizationId) {
  return query10(collection16(firestore, "organizations_unregistered_invites"), where4("organizationId", "==", organizationId)).withConverter(
    OrganizationUnregisteredInviteConverter
  );
}
async function getOrganizationUnregisteredInvites(...args) {
  const { docs } = await getDocs14(getOrganizationUnregisteredInvitesQuery(...args));
  return docs.map((doc42) => doc42.data());
}
function useGetOrganizationUnregisteredInvites(...args) {
  return useCollectionData15(getOrganizationUnregisteredInvitesQuery(...args));
}
function findOrganizationsQuery(firestore, filter) {
  if (!filter) {
    return getOrganizationsQuery(firestore);
  }
  const conditions = [
    filter.userId ? where4("userIds", "array-contains", filter.userId) : null,
    filter.status ? where4("status", "==", filter.status) : null,
    filter.limit ? limit5(filter.limit) : null
  ].filter(Boolean);
  return query10(collection16(firestore, "organizations"), ...conditions).withConverter(OrganizationConverter);
}
async function findOrganizations(...args) {
  const { docs } = await getDocs14(findOrganizationsQuery(...args));
  return docs.map((doc42) => doc42.data());
}
function useFindOrganizations(...args) {
  return useCollectionData15(findOrganizationsQuery(...args));
}
async function createOrganization(firestore, data) {
  const ref3 = await addDoc2(collection16(firestore, "organizations"), {
    ...data,
    status: "CREATED" /* CREATED */,
    createdAt: serverTimestamp5(),
    updatedAt: serverTimestamp5()
  });
  return ref3;
}
async function updateOrganization(firestore, organizationId, data) {
  await updateDoc6(doc18(firestore, "organizations", organizationId), { ...data, updatedAt: serverTimestamp5() });
}

// src/client/public-profiles.ts
import { collection as collection17, documentId as documentId2, query as query11, where as where5 } from "firebase/firestore";
function getPublicProfilesByIdsQuery(firestore, ids) {
  return query11(collection17(firestore, "public_profiles"), where5(documentId2(), "in", ids));
}

// src/client/sessions/add-session-eta.ts
import { doc as doc19, setDoc as setDoc6, Timestamp as Timestamp2 } from "firebase/firestore";
async function addSessionETA(firestore, sessionId, data) {
  const etaId = data.destinationType;
  const etaRef = doc19(firestore, "sessions", sessionId, "eta", etaId);
  await setDoc6(etaRef, {
    id: etaId,
    destinationType: data.destinationType,
    timestamp: Timestamp2.fromDate(data.timestamp),
    userId: data.userId,
    deleted: null,
    ...data.meta ? { meta: data.meta } : {}
  });
  return etaId;
}

// src/client/sessions/create-session-audit-log.ts
import { addDoc as addDoc3, collection as collection18 } from "firebase/firestore";
async function createSessionAuditLog(firestore, sessionId, data) {
  const ref3 = await addDoc3(collection18(firestore, "sessions", sessionId, "auditLogs"), data);
  return ref3;
}

// src/client/sessions/delete-session-eta.ts
import { doc as doc20, serverTimestamp as serverTimestamp6, updateDoc as updateDoc7 } from "firebase/firestore";
async function deleteSessionETA(firestore, sessionId, data) {
  const etaId = data.destinationType;
  const etaRef = doc20(firestore, "sessions", sessionId, "eta", etaId);
  await updateDoc7(etaRef, {
    deleted: true,
    deletedAt: serverTimestamp6(),
    ...data.meta ? { meta: data.meta } : {}
  });
  return etaId;
}

// src/client/sessions/toggle-session-mute.ts
import { doc as doc21, setDoc as setDoc7 } from "firebase/firestore";
async function toggleSessionMute(firestore, params) {
  const { userId, sessionId, schemaVersion, muted } = params;
  const sessionMemberRef = doc21(firestore, "sessions", sessionId, "users", userId);
  await setDoc7(sessionMemberRef, { muted }, { merge: true });
  if (schemaVersion >= 7) {
    const userSessionRef = doc21(firestore, "users", userId, "sessions", sessionId);
    await setDoc7(userSessionRef, { muted }, { merge: true });
  }
}

// src/client/sessions/update-session-eta.ts
import { doc as doc22, Timestamp as Timestamp3, updateDoc as updateDoc8 } from "firebase/firestore";
async function updateSessionETA(firestore, sessionId, data) {
  const etaId = data.destinationType;
  const etaRef = doc22(firestore, "sessions", sessionId, "eta", etaId);
  await updateDoc8(etaRef, {
    timestamp: Timestamp3.fromDate(data.timestamp),
    userId: data.userId,
    ...data.meta ? { meta: data.meta } : {}
  });
  return etaId;
}

// src/client/sessions/use-find-sessions.ts
import { collection as collection19, getDocs as getDocs15, limit as limit6, query as query12, where as where6 } from "firebase/firestore";
import { useCollectionData as useCollectionData16 } from "react-firebase-hooks/firestore";
function findSessionsQuery(firestore, filter) {
  if (!filter) {
    return getSessionsQuery(firestore);
  }
  const conditions = [
    filter.userId ? where6("userIds", "array-contains", filter.userId) : null,
    filter.organizationIds && filter.organizationIds.length > 0 ? where6("organizationIds", "array-contains-any", filter.organizationIds) : null,
    filter.areaIds && filter.areaIds.length > 0 ? where6("areaIds", "array-contains-any", filter.areaIds) : null,
    filter.territoryIds && filter.territoryIds.length > 0 ? where6("territoryIds", "array-contains-any", filter.territoryIds) : null,
    filter.finished != null ? where6("finished", "==", filter.finished) : null,
    filter.organs && filter.organs.length > 0 ? where6("organ", "in", filter.organs) : null,
    filter.unosId ? where6("unosId", "==", filter.unosId) : null,
    filter.limit ? limit6(filter.limit) : null
  ].filter(Boolean);
  return query12(collection19(firestore, "sessions"), ...conditions).withConverter(SessionConverter);
}
async function findSessions(...args) {
  const { docs } = await getDocs15(findSessionsQuery(...args));
  return docs.map((doc42) => doc42.data());
}
function useFindSessions(...args) {
  return useCollectionData16(findSessionsQuery(...args));
}

// src/client/sessions/use-get-donor-data.ts
import { doc as doc23 } from "firebase/firestore";
import { useDocumentData as useDocumentData8 } from "react-firebase-hooks/firestore";
function getDonorDataQuery(firestore, sessionId) {
  return doc23(firestore, "sessions", sessionId, "data", "donor").withConverter(DonorDataConverter);
}
function useGetDonorData(...args) {
  return useDocumentData8(getDonorDataQuery(...args));
}

// src/client/sessions/use-get-notes-data.ts
import { doc as doc24 } from "firebase/firestore";
import { useDocumentData as useDocumentData9 } from "react-firebase-hooks/firestore";
function getNotesDataQuery(firestore, sessionId) {
  return doc24(firestore, "sessions", sessionId, "data", "notes").withConverter(NotesDataConverter);
}
function useGetNotesData(...args) {
  return useDocumentData9(getNotesDataQuery(...args));
}

// src/client/sessions/use-get-organ.ts
import { doc as doc25, getDoc as getDoc7 } from "firebase/firestore";
import { useDocumentData as useDocumentData10 } from "react-firebase-hooks/firestore";
function getOrganQuery(firestore, sessionId, organId) {
  return doc25(firestore, "sessions", sessionId, "organs", organId).withConverter(OrganConverter);
}
async function getOrgan(...args) {
  const doc42 = await getDoc7(getOrganQuery(...args));
  return doc42.data();
}
function useGetOrgan(...args) {
  return useDocumentData10(getOrganQuery(...args));
}

// src/client/sessions/use-get-organ-advanced-tracking.ts
import { collection as collection20, getDocs as getDocs16, orderBy as orderBy9, query as query13 } from "firebase/firestore";
import { useCollectionData as useCollectionData17 } from "react-firebase-hooks/firestore";
function getOrganAdvancedTrackingQuery(firestore, sessionId, organId) {
  return query13(collection20(firestore, "sessions", sessionId, "organs", organId, "vantagePoints"), orderBy9("timestamp", "asc")).withConverter(
    AdvancedTrackingConverter
  );
}
async function getOrganAdvancedTracking(...args) {
  const { docs } = await getDocs16(getOrganAdvancedTrackingQuery(...args));
  return docs.map((doc42) => doc42.data());
}
function useGetOrganAdvancedTracking(...args) {
  return useCollectionData17(getOrganAdvancedTrackingQuery(...args));
}

// src/client/sessions/use-get-organ-last-advanced-tracking.ts
import { collection as collection21, getDocs as getDocs17, limit as limit7, orderBy as orderBy10, query as query14 } from "firebase/firestore";
import { useCollectionData as useCollectionData18 } from "react-firebase-hooks/firestore";
function getOrganLastAdvancedTrackingQuery(firestore, sessionId, organId) {
  return query14(
    collection21(firestore, "sessions", sessionId, "organs", organId, "vantagePoints"),
    orderBy10("timestamp", "desc"),
    limit7(1)
  ).withConverter(AdvancedTrackingConverter);
}
async function getOrganLastAdvancedTracking(...args) {
  const { docs } = await getDocs17(getOrganLastAdvancedTrackingQuery(...args));
  return docs.map((doc42) => doc42.data())[0];
}
function useGetOrganLastAdvancedTracking(...args) {
  return useCollectionData18(getOrganLastAdvancedTrackingQuery(...args));
}

// src/client/sessions/use-get-organ-last-log.ts
import { collection as collection22, getDocs as getDocs18, limit as limit8, orderBy as orderBy11, query as query15 } from "firebase/firestore";
import { useCollectionData as useCollectionData19 } from "react-firebase-hooks/firestore";
function getOrganLastLogQuery(firestore, sessionId, organId) {
  return query15(collection22(firestore, "sessions", sessionId, "organs", organId, "logs"), orderBy11("order", "desc"), limit8(1)).withConverter(
    OrganReadingsPageConverter
  );
}
async function getLastOrganLog(...args) {
  const { docs } = await getDocs18(getOrganLastLogQuery(...args));
  return docs.map((doc42) => doc42.data());
}
function useGetLastOrganLog(...args) {
  return useCollectionData19(getOrganLastLogQuery(...args));
}

// src/client/sessions/use-get-organ-last-logger-location.ts
import { collection as collection23, getDocs as getDocs19, limit as limit9, orderBy as orderBy12, query as query16 } from "firebase/firestore";
import { useCollectionData as useCollectionData20 } from "react-firebase-hooks/firestore";
function getOrganLastLoggerLocationQuery(firestore, sessionId, organId) {
  return query16(
    collection23(firestore, "sessions", sessionId, "organs", organId, "locations"),
    orderBy12("timestamp", "desc"),
    limit9(1)
  ).withConverter(LoggerLocationConverter);
}
async function getOrganLastLoggerLocation(...args) {
  const { docs } = await getDocs19(getOrganLastLoggerLocationQuery(...args));
  return docs.map((doc42) => doc42.data())[0];
}
function useGetOrganLastLoggerLocation(...args) {
  return useCollectionData20(getOrganLastLoggerLocationQuery(...args));
}

// src/client/sessions/use-get-organ-logger-locations.ts
import { collection as collection24, getDocs as getDocs20, orderBy as orderBy13, query as query17 } from "firebase/firestore";
import { useCollectionData as useCollectionData21 } from "react-firebase-hooks/firestore";
function getOrganLoggerLocationsQuery(firestore, sessionId, organId) {
  return query17(collection24(firestore, "sessions", sessionId, "organs", organId, "locations"), orderBy13("timestamp", "asc")).withConverter(
    LoggerLocationConverter
  );
}
async function getOrganLoggerLocations(...args) {
  const { docs } = await getDocs20(getOrganLoggerLocationsQuery(...args));
  return docs.map((doc42) => doc42.data());
}
function useGetOrganLoggerLocations(...args) {
  return useCollectionData21(getOrganLoggerLocationsQuery(...args));
}

// src/client/sessions/use-get-organ-logs.ts
import { collection as collection25, getDocs as getDocs21, orderBy as orderBy14, query as query18 } from "firebase/firestore";
import { useCollectionData as useCollectionData22 } from "react-firebase-hooks/firestore";
function getOrganLogsQuery(firestore, sessionId, organId) {
  return query18(collection25(firestore, "sessions", sessionId, "organs", organId, "logs"), orderBy14("order", "asc")).withConverter(
    OrganReadingsPageConverter
  );
}
async function getOrganLogs(...args) {
  const { docs } = await getDocs21(getOrganLogsQuery(...args));
  return docs.map((doc42) => doc42.data());
}
function useGetOrganLogs(...args) {
  return useCollectionData22(getOrganLogsQuery(...args));
}

// src/client/sessions/use-get-organ-marker-details.ts
import { useMemo as useMemo3 } from "react";

// src/client/sessions/use-get-session-last-advanced-tracking.ts
import { collection as collection26, getDocs as getDocs22, orderBy as orderBy15, query as query19 } from "firebase/firestore";
import { useCollectionData as useCollectionData23 } from "react-firebase-hooks/firestore";
function getSessionLastAdvancedTrackingQuery(firestore, sessionId) {
  return query19(collection26(firestore, "sessions", sessionId, "vantagePoints"), orderBy15("timestamp", "desc")).withConverter(
    AdvancedTrackingConverter
  );
}
async function getSessionLastAdvancedTracking(...args) {
  const { docs } = await getDocs22(getSessionLastAdvancedTrackingQuery(...args));
  return docs.map((doc42) => doc42.data())[0];
}
function useGetSessionLastAdvancedTracking(...args) {
  return useCollectionData23(getSessionLastAdvancedTrackingQuery(...args));
}

// src/client/sessions/use-get-session-last-logger-location.ts
import { collection as collection27, getDocs as getDocs23, orderBy as orderBy16, query as query20 } from "firebase/firestore";
import { useCollectionData as useCollectionData24 } from "react-firebase-hooks/firestore";
function getSessionLastLoggerLocationQuery(firestore, sessionId) {
  return query20(collection27(firestore, "sessions", sessionId, "locations"), orderBy16("timestamp", "desc")).withConverter(
    LoggerLocationConverter
  );
}
async function getSessionLastLoggerLocation(...args) {
  const { docs } = await getDocs23(getSessionLastLoggerLocationQuery(...args));
  return docs.map((doc42) => doc42.data())[0];
}
function useGetSessionLastLoggerLocation(...args) {
  return useCollectionData24(getSessionLastLoggerLocationQuery(...args));
}

// src/client/sessions/use-get-organ-marker-details.ts
function useGetOrganMarkerDetails(firestore, sessionId, organId, schemaVersion) {
  const isV7 = (schemaVersion ?? 0) >= 7;
  const [v7Location, isV7LocationLoading] = useGetLoggerLastAppLocation(firestore, isV7 ? organId : "__none__");
  const [v7Tracking, isV7TrackingLoading] = useGetLoggerLastAdvancedTracking(firestore, isV7 ? organId : "__none__");
  const [legacyLocation, isLegacyLocationLoading] = useOrganLastLoggerLocation(firestore, sessionId, !isV7 ? organId : "__none__");
  const [legacyTracking, isLegacyTrackingLoading] = useOrganLastAdvancedTracking(firestore, sessionId, !isV7 ? organId : "__none__");
  const isLoading = useMemo3(() => {
    if (isV7) return isV7LocationLoading || isV7TrackingLoading;
    return isLegacyLocationLoading || isLegacyTrackingLoading;
  }, [isV7, isV7LocationLoading, isV7TrackingLoading, isLegacyLocationLoading, isLegacyTrackingLoading]);
  const lastLoggerLocation = useMemo3(() => {
    if (isV7) {
      const loc = v7Location?.[0];
      if (!loc) return void 0;
      return { latitude: loc.latitude, longitude: loc.longitude, timestamp: loc.timestamp, horizontalAccuracy: loc.horizontalAccuracy, verticalAccuracy: loc.verticalAccuracy };
    }
    return legacyLocation;
  }, [isV7, v7Location, legacyLocation]);
  const lastAdvancedTracking = useMemo3(() => {
    if (isV7) return v7Tracking?.[0];
    return legacyTracking;
  }, [isV7, v7Tracking, legacyTracking]);
  return {
    lastLoggerLocation,
    lastAdvancedTracking,
    isLoading
  };
}
function useOrganLastLoggerLocation(firestore, sessionId, organId) {
  const [lastSessionLoggerLocation, isLastSessionLoggerLocationLoading] = useGetSessionLastLoggerLocation(firestore, sessionId);
  const [lastOrganLoggerLocation, isLastOrganLoggerLocationLoading] = useGetOrganLastLoggerLocation(firestore, sessionId, organId);
  const isLoading = useMemo3(() => {
    return isLastSessionLoggerLocationLoading || isLastOrganLoggerLocationLoading;
  }, [isLastSessionLoggerLocationLoading, isLastOrganLoggerLocationLoading]);
  const lastLoggerLocation = useMemo3(() => {
    return lastOrganLoggerLocation?.[0] ?? lastSessionLoggerLocation?.[0];
  }, [lastSessionLoggerLocation, lastOrganLoggerLocation, isLoading]);
  return [lastLoggerLocation, isLoading];
}
function useOrganLastAdvancedTracking(firestore, sessionId, organId) {
  const [lastSessionAdvancedTracking, isLastSessionAdvancedTrackingLoading] = useGetSessionLastAdvancedTracking(firestore, sessionId);
  const [lastOrganAdvancedTracking, isLastOrganAdvancedTrackingLoading] = useGetOrganLastAdvancedTracking(firestore, sessionId, organId);
  const isLoading = useMemo3(() => {
    return isLastSessionAdvancedTrackingLoading || isLastOrganAdvancedTrackingLoading;
  }, [isLastSessionAdvancedTrackingLoading, isLastOrganAdvancedTrackingLoading]);
  const lastAdvancedTracking = useMemo3(() => {
    return lastOrganAdvancedTracking?.[0] ?? lastSessionAdvancedTracking?.[0];
  }, [lastOrganAdvancedTracking, lastSessionAdvancedTracking, isLoading]);
  return [lastAdvancedTracking, isLoading];
}

// src/client/sessions/use-get-organ-polylines-details.ts
import { useMemo as useMemo4 } from "react";

// src/client/sessions/use-get-session-advanced-tracking.ts
import { collection as collection28, getDocs as getDocs24, orderBy as orderBy17, query as query21 } from "firebase/firestore";
import { useCollectionData as useCollectionData25 } from "react-firebase-hooks/firestore";
function getSessionAdvancedTrackingQuery(firestore, sessionId) {
  return query21(collection28(firestore, "sessions", sessionId, "vantagePoints"), orderBy17("timestamp", "asc")).withConverter(
    AdvancedTrackingConverter
  );
}
async function getSessionAdvancedTracking(...args) {
  const { docs } = await getDocs24(getSessionAdvancedTrackingQuery(...args));
  return docs.map((doc42) => doc42.data());
}
function useGetSessionAdvancedTracking(...args) {
  return useCollectionData25(getSessionAdvancedTrackingQuery(...args));
}

// src/client/sessions/use-get-session-logger-locations.ts
import { collection as collection29, getDocs as getDocs25, orderBy as orderBy18, query as query22 } from "firebase/firestore";
import { useCollectionData as useCollectionData26 } from "react-firebase-hooks/firestore";
function getSessionLoggerLocationsQuery(firestore, sessionId) {
  return query22(collection29(firestore, "sessions", sessionId, "locations"), orderBy18("timestamp", "asc")).withConverter(
    LoggerLocationConverter
  );
}
async function getSessionLoggerLocations(...args) {
  const { docs } = await getDocs25(getSessionLoggerLocationsQuery(...args));
  return docs.map((doc42) => doc42.data());
}
function useGetSessionLoggerLocations(...args) {
  return useCollectionData26(getSessionLoggerLocationsQuery(...args));
}

// src/client/sessions/use-get-organ-polylines-details.ts
function useGetOrganPolylinesDetails(firestore, sessionId, organId, schemaVersion) {
  const isV7 = (schemaVersion ?? 0) >= 7;
  const [v7Locations, areV7LocationsLoading] = useGetLoggerAppLocations(firestore, isV7 ? organId : "__none__");
  const [v7Tracking, isV7TrackingLoading] = useGetLoggerAdvancedTracking(firestore, isV7 ? organId : "__none__");
  const [legacyLocations, areLegacyLocationsLoading] = useOrganLoggerLocations(firestore, sessionId, !isV7 ? organId : "__none__");
  const [legacyTracking, isLegacyTrackingLoading] = useOrganAdvancedTracking(firestore, sessionId, !isV7 ? organId : "__none__");
  const isLoading = useMemo4(() => {
    if (isV7) return areV7LocationsLoading || isV7TrackingLoading;
    return areLegacyLocationsLoading || isLegacyTrackingLoading;
  }, [isV7, areV7LocationsLoading, isV7TrackingLoading, areLegacyLocationsLoading, isLegacyTrackingLoading]);
  const loggerLocations = useMemo4(() => {
    if (isV7) {
      return (v7Locations ?? []).map((loc) => ({
        latitude: loc.latitude,
        longitude: loc.longitude,
        timestamp: loc.timestamp,
        horizontalAccuracy: loc.horizontalAccuracy,
        verticalAccuracy: loc.verticalAccuracy
      }));
    }
    return legacyLocations;
  }, [isV7, v7Locations, legacyLocations]);
  const advancedTracking = useMemo4(() => {
    if (isV7) return v7Tracking ?? [];
    return legacyTracking;
  }, [isV7, v7Tracking, legacyTracking]);
  return {
    loggerLocations,
    advancedTracking,
    isLoading
  };
}
function useOrganLoggerLocations(firestore, sessionId, organId) {
  const [sessionLoggerLocations, areSessionLoggerLocationsLoading] = useGetSessionLoggerLocations(firestore, sessionId);
  const [organLoggerLocations, areOrganLoggerLocationsLoading] = useGetOrganLoggerLocations(firestore, sessionId, organId);
  const isLoading = useMemo4(() => {
    return areSessionLoggerLocationsLoading || areOrganLoggerLocationsLoading;
  }, [areSessionLoggerLocationsLoading, areOrganLoggerLocationsLoading]);
  const loggerLocations = useMemo4(() => {
    return organLoggerLocations && organLoggerLocations.length > 0 ? organLoggerLocations : sessionLoggerLocations ?? [];
  }, [sessionLoggerLocations, organLoggerLocations, isLoading]);
  return [loggerLocations, isLoading];
}
function useOrganAdvancedTracking(firestore, sessionId, organId) {
  const [sessionAdvancedTracking, isSessionAdvancedTrackingLoading] = useGetSessionAdvancedTracking(firestore, sessionId);
  const [organAdvancedTracking, isOrganAdvancedTrackingLoading] = useGetOrganAdvancedTracking(firestore, sessionId, organId);
  const isLoading = useMemo4(() => {
    return isSessionAdvancedTrackingLoading || isOrganAdvancedTrackingLoading;
  }, [isSessionAdvancedTrackingLoading, isOrganAdvancedTrackingLoading]);
  const advancedTracking = useMemo4(() => {
    return organAdvancedTracking && organAdvancedTracking.length > 0 ? organAdvancedTracking : sessionAdvancedTracking ?? [];
  }, [organAdvancedTracking, sessionAdvancedTracking, isLoading]);
  return [advancedTracking, isLoading];
}

// src/client/sessions/use-get-organs.ts
import { collection as collection30, getDocs as getDocs26 } from "firebase/firestore";
import { useCollectionData as useCollectionData27 } from "react-firebase-hooks/firestore";
function getOrgansQuery(firestore, sessionId) {
  return collection30(firestore, "sessions", sessionId, "organs").withConverter(OrganConverter);
}
async function getOrgans(...args) {
  const { docs } = await getDocs26(getOrgansQuery(...args));
  return docs.map((doc42) => doc42.data());
}
function useGetOrgans(...args) {
  return useCollectionData27(getOrgansQuery(...args));
}

// src/client/sessions/use-get-potential-sessions.ts
import { collection as collection31, doc as doc26, getDoc as getDoc8, getDocs as getDocs27, limit as limit10, orderBy as orderBy19, query as query23, where as where7 } from "firebase/firestore";
import { useCollectionData as useCollectionData28, useDocumentData as useDocumentData11 } from "react-firebase-hooks/firestore";
function getPotentialSessionsQuery(firestore, organizationId) {
  return query23(collection31(firestore, "organizations", organizationId, "potentialSessions"), orderBy19("createdAt", "desc")).withConverter(
    PotentialSessionConverter
  );
}
async function getPotentialSessions(...args) {
  const { docs } = await getDocs27(getPotentialSessionsQuery(...args));
  return docs.map((doc42) => doc42.data());
}
function useGetPotentialSessions(...args) {
  return useCollectionData28(getPotentialSessionsQuery(...args));
}
function getPotentialSessionQuery(firestore, organizationId, sessionId) {
  return doc26(firestore, "organizations", organizationId, "potentialSessions", sessionId).withConverter(PotentialSessionConverter);
}
async function getPotentialSession(...args) {
  const doc42 = await getDoc8(getPotentialSessionQuery(...args));
  return doc42.data();
}
function useGetPotentialSession(...args) {
  return useDocumentData11(getPotentialSessionQuery(...args));
}
function findPotentialSessionsQuery(firestore, organizationId, filter) {
  const conditions = [];
  if (filter?.status) {
    if (Array.isArray(filter.status)) {
      conditions.push(where7("status", "in", filter.status));
    } else {
      conditions.push(where7("status", "==", filter.status));
    }
  }
  const orderField = filter?.orderByField || "createdAt";
  const direction = filter?.orderDirection || "desc";
  conditions.push(orderBy19(orderField, direction));
  if (filter?.limit) {
    conditions.push(limit10(filter.limit));
  }
  return query23(collection31(firestore, "organizations", organizationId, "potentialSessions"), ...conditions).withConverter(
    PotentialSessionConverter
  );
}
async function findPotentialSessions(...args) {
  const { docs } = await getDocs27(findPotentialSessionsQuery(...args));
  return docs.map((doc42) => doc42.data());
}
function useFindPotentialSessions(...args) {
  return useCollectionData28(findPotentialSessionsQuery(...args));
}
function getPotentialSessionsByStatusQuery(firestore, organizationId, status) {
  return query23(
    collection31(firestore, "organizations", organizationId, "potentialSessions"),
    where7("status", "==", status),
    orderBy19("createdAt", "desc")
  ).withConverter(PotentialSessionConverter);
}
async function getPotentialSessionsByStatus(...args) {
  const { docs } = await getDocs27(getPotentialSessionsByStatusQuery(...args));
  return docs.map((doc42) => doc42.data());
}
function useGetPotentialSessionsByStatus(...args) {
  return useCollectionData28(getPotentialSessionsByStatusQuery(...args));
}
async function countPotentialSessionsByStatus(firestore, organizationId) {
  const baseCollection = collection31(firestore, "organizations", organizationId, "potentialSessions");
  const [identified, pendingReview, approved, rejected] = await Promise.all([
    getDocs27(query23(baseCollection, where7("status", "==", "identified" /* IDENTIFIED */))),
    getDocs27(query23(baseCollection, where7("status", "==", "pending_review" /* PENDING_REVIEW */))),
    getDocs27(query23(baseCollection, where7("status", "==", "approved" /* APPROVED */))),
    getDocs27(query23(baseCollection, where7("status", "==", "rejected" /* REJECTED */)))
  ]);
  return {
    identified: identified.size,
    pendingReview: pendingReview.size,
    approved: approved.size,
    rejected: rejected.size,
    total: identified.size + pendingReview.size + approved.size + rejected.size
  };
}

// src/client/sessions/use-get-recipient-data.ts
import { doc as doc27 } from "firebase/firestore";
import { useDocumentData as useDocumentData12 } from "react-firebase-hooks/firestore";
function getRecipientDataQuery(firestore, sessionId) {
  return doc27(firestore, "sessions", sessionId, "data", "recipient").withConverter(RecipientDataConverter);
}
function useGetRecipientData(...args) {
  return useDocumentData12(getRecipientDataQuery(...args));
}

// src/client/sessions/use-get-session.ts
import { doc as doc28, getDoc as getDoc9 } from "firebase/firestore";
import { useDocumentData as useDocumentData13 } from "react-firebase-hooks/firestore";
function getSessionQuery(firestore, sessionId) {
  return doc28(firestore, "sessions", sessionId).withConverter(SessionConverter);
}
async function getSession(...args) {
  const doc42 = await getDoc9(getSessionQuery(...args));
  return doc42.data();
}
function useGetSession(...args) {
  return useDocumentData13(getSessionQuery(...args));
}

// src/client/sessions/use-get-session-audit-log.ts
import { collection as collection32, getDocs as getDocs28, orderBy as orderBy20, query as query24 } from "firebase/firestore";
import { useCollectionData as useCollectionData29 } from "react-firebase-hooks/firestore";
function getSessionAuditLogQuery(firestore, sessionId) {
  return query24(collection32(firestore, "sessions", sessionId, "auditLogs"), orderBy20("timestampServer", "asc")).withConverter(
    AuditLogConverter
  );
}
async function getSessionAuditLog(...args) {
  const { docs } = await getDocs28(getSessionAuditLogQuery(...args));
  return docs.map((doc42) => doc42.data());
}
function useGetSessionAuditLog(...args) {
  return useCollectionData29(getSessionAuditLogQuery(...args));
}

// src/client/sessions/use-get-session-details.ts
import { useEffect, useMemo as useMemo5, useState } from "react";

// src/client/config/use-get-config-destination.ts
import { doc as doc29, getDoc as getDoc10 } from "firebase/firestore";
import { useDocumentData as useDocumentData14 } from "react-firebase-hooks/firestore";
function getConfigDestinationQuery(firestore, destinationId) {
  return doc29(firestore, "config", "organizations", "destinations", destinationId).withConverter(DestinationConverter);
}
async function getConfigDestination(...args) {
  const doc42 = await getDoc10(getConfigDestinationQuery(...args));
  return doc42.data();
}

// src/client/sessions/use-get-session-ischemic-times.ts
import { collection as collection33, getDocs as getDocs29 } from "firebase/firestore";
import { useCollectionData as useCollectionData30 } from "react-firebase-hooks/firestore";
function getSessionIschemicTimesQuery(firestore, sessionId) {
  return collection33(firestore, "sessions", sessionId, "ischemicTimes").withConverter(SessionIschemicTimeConverter);
}
async function getSessionIschemicTimes(...args) {
  const { docs } = await getDocs29(getSessionIschemicTimesQuery(...args));
  return docs.map((doc42) => doc42.data());
}
function useGetSessionIschemicTimes(...args) {
  return useCollectionData30(getSessionIschemicTimesQuery(...args));
}

// src/client/sessions/use-get-session-details.ts
function useGetSessionDetails(firestore, sessionId, options) {
  const [session, isSessionLoading] = useGetSession(firestore, sessionId);
  const [destination, isDestinationLoading] = useSessionDestination(firestore, { session, isSessionLoading });
  const [eta, isETALoading] = useSessionETA(firestore, sessionId, { session, isSessionLoading });
  const [ischemicTimes, areIschemicTimesLoading] = useSessionIschemicTimes(firestore, sessionId, { session, isSessionLoading });
  const [members, areMembersLoading] = useGetSessionMembers(firestore, sessionId);
  const [timeline, isTimelineLoading] = useGetSessionTimeline(firestore, sessionId, {
    excludeCustom: options?.excludeCustomTimelineEntries
  });
  const [auditLog, isAuditLogLoading] = useGetSessionAuditLog(firestore, sessionId);
  const [flights, areFlightsLoading] = useSessionFlights(firestore, sessionId, { session, isSessionLoading });
  const [flightPositions, areFlightPositionsLoading] = useGetSessionFlightPositions(firestore, sessionId);
  const [loggers, areLoggersLoading] = useLoggers(firestore, sessionId, { session, isSessionLoading });
  const [donorData] = useGetDonorData(firestore, sessionId);
  const [recipientData] = useGetRecipientData(firestore, sessionId);
  const [notesData] = useGetNotesData(firestore, sessionId);
  const isLoading = useMemo5(() => {
    return isSessionLoading || isDestinationLoading || isETALoading || areIschemicTimesLoading || areMembersLoading || isTimelineLoading || isAuditLogLoading || areFlightsLoading || areFlightPositionsLoading || areLoggersLoading;
  }, [
    isSessionLoading,
    isDestinationLoading,
    isETALoading,
    areIschemicTimesLoading,
    areMembersLoading,
    isTimelineLoading,
    isAuditLogLoading,
    areFlightsLoading,
    areFlightPositionsLoading,
    areLoggersLoading
  ]);
  const timelineAll = useMemo5(() => {
    if (!timeline) return [];
    return timeline.filter((t) => !t.deleted).sort(compareTimelineEntriesDesc);
  }, [timeline]);
  const timelineLeft = useMemo5(() => {
    if (!timelineAll) return [];
    return timelineAll.filter((t) => {
      const entryOrganPosition = TimelineEntryTypeUtil.getOrganPosition(t.type);
      return !entryOrganPosition || entryOrganPosition === "LEFT" /* LEFT */;
    });
  }, [timelineAll]);
  const timelineRight = useMemo5(() => {
    if (!timelineAll) return [];
    return timelineAll.filter((t) => {
      const entryOrganPosition = TimelineEntryTypeUtil.getOrganPosition(t.type);
      return !entryOrganPosition || entryOrganPosition === "RIGHT" /* RIGHT */;
    });
  }, [timelineAll]);
  return {
    session,
    destination,
    eta: eta ?? [],
    ischemicTimes: ischemicTimes ?? [],
    members: members ?? [],
    timelines: {
      all: timelineAll,
      ["LEFT" /* LEFT */]: timelineLeft,
      ["RIGHT" /* RIGHT */]: timelineRight
    },
    auditLog: auditLog ?? [],
    flights: flights ?? [],
    flightPositions: flightPositions ?? [],
    loggers: loggers ?? [],
    donorData,
    recipientData,
    notesData,
    isLoading
  };
}
function useSessionDestination(firestore, data) {
  const { session, isSessionLoading } = data;
  const [destination, setDestination] = useState();
  const [isLoading, setIsLoading] = useState(true);
  useEffect(() => {
    let isMounted = true;
    if (isSessionLoading || !session) {
      setDestination(void 0);
      setIsLoading(false);
      return;
    }
    if (session.destination) {
      setIsLoading(true);
      getConfigDestination(firestore, session.destination).then((destination2) => {
        if (isMounted) {
          setDestination(destination2);
        }
      }).finally(() => {
        if (isMounted) {
          setIsLoading(false);
        }
      });
      return () => {
        isMounted = false;
      };
    }
    if (session.recipientCenter) {
      const recipientCenterAsDestination = new Destination({
        id: session.recipientCenter.handle,
        name: session.recipientCenter.name,
        handle: session.recipientCenter.handle,
        organs: [],
        types: [],
        placeId: session.recipientCenter.placeId,
        location: session.recipientCenter.location,
        address: session.recipientCenter.address,
        addressComponents: session.recipientCenter.addressComponents
      });
      setDestination(recipientCenterAsDestination);
      setIsLoading(false);
      return;
    }
    setDestination(void 0);
    setIsLoading(false);
  }, [firestore, session?.destination, session?.recipientCenter, isSessionLoading]);
  return [destination, isLoading];
}
function useSessionETA(firestore, sessionId, data) {
  const { session, isSessionLoading } = data;
  const [etaCollection, isETACollectionLoading] = useGetSessionETA(firestore, sessionId);
  const eta = useMemo5(() => {
    if (!session || isSessionLoading || isETACollectionLoading) {
      return [];
    }
    if (etaCollection && etaCollection.length > 0) {
      return etaCollection.filter((e) => !e.deleted).sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());
    }
    return session.eta ? [new SessionETA({ destinationType: "RECIPIENT" /* RECIPIENT */, timestamp: session.eta })] : [];
  }, [session, isSessionLoading, etaCollection, isETACollectionLoading]);
  const isLoading = useMemo5(() => {
    return isSessionLoading || isETACollectionLoading;
  }, [isSessionLoading, isETACollectionLoading]);
  return [eta, isLoading];
}
function useSessionIschemicTimes(firestore, sessionId, data) {
  const { session, isSessionLoading } = data;
  const [ischemicTimesCollection, isIschemicTimesCollectionLoading] = useGetSessionIschemicTimes(firestore, sessionId);
  const ischemicTimes = useMemo5(() => {
    if (!session || isSessionLoading || isIschemicTimesCollectionLoading) {
      return [];
    }
    if (ischemicTimesCollection && ischemicTimesCollection.length > 0) {
      return ischemicTimesCollection;
    }
    return session.ischemicTime ? [session.ischemicTime] : [];
  }, [session, isSessionLoading, ischemicTimesCollection, isIschemicTimesCollectionLoading]);
  const isLoading = useMemo5(() => {
    return isSessionLoading || isIschemicTimesCollectionLoading;
  }, [isSessionLoading, isIschemicTimesCollectionLoading]);
  return [ischemicTimes, isLoading];
}
function useSessionFlights(firestore, sessionId, data) {
  const { session, isSessionLoading } = data;
  const [flightsCollection, isFlightsCollectionLoading] = useGetSessionFlights(firestore, sessionId);
  const flights = useMemo5(() => {
    if (!session || isSessionLoading || isFlightsCollectionLoading) {
      return [];
    }
    if (session.schemaVersion >= 6) {
      return flightsCollection?.filter((f) => !f.deleted) ?? [];
    }
    return session.flights ?? [];
  }, [session, isSessionLoading, flightsCollection, isFlightsCollectionLoading]);
  const isLoading = useMemo5(() => {
    return isSessionLoading || isFlightsCollectionLoading;
  }, [isSessionLoading, isFlightsCollectionLoading]);
  return [flights, isLoading];
}
function useLoggers(firestore, sessionId, data) {
  const { session, isSessionLoading } = data;
  const [organsCollection, isOrgansCollectionLoading] = useGetOrgans(firestore, sessionId);
  const defaultLoggerId = useMemo5(() => {
    if (!session || session.schemaVersion < 7 || !session.loggers) return void 0;
    return session.loggers.DEFAULT?.loggerId;
  }, [session]);
  const leftLoggerId = useMemo5(() => {
    if (!session || session.schemaVersion < 7 || !session.loggers) return void 0;
    return session.loggers.LEFT?.loggerId;
  }, [session]);
  const rightLoggerId = useMemo5(() => {
    if (!session || session.schemaVersion < 7 || !session.loggers) return void 0;
    return session.loggers.RIGHT?.loggerId;
  }, [session]);
  const [loggerDefault, isLoggerDefaultLoading] = useGetLogger(firestore, defaultLoggerId ?? "__none__");
  const [loggerLeft, isLoggerLeftLoading] = useGetLogger(firestore, leftLoggerId ?? "__none__");
  const [loggerRight, isLoggerRightLoading] = useGetLogger(firestore, rightLoggerId ?? "__none__");
  const isV7 = session != null && session.schemaVersion >= 7 && session.loggers != null && Object.keys(session.loggers).length > 0;
  const loggers = useMemo5(() => {
    if (!session || isSessionLoading) return [];
    if (isV7) {
      const result = [];
      if (loggerDefault) result.push({ logger: loggerDefault, position: void 0 });
      if (loggerLeft) result.push({ logger: loggerLeft, position: "LEFT" /* LEFT */ });
      if (loggerRight) result.push({ logger: loggerRight, position: "RIGHT" /* RIGHT */ });
      return result;
    }
    if (session.schemaVersion < 6) {
      const organ = session.toOrgan();
      return [{ logger: organ.toLogger(), position: organ.position }];
    }
    if (isOrgansCollectionLoading || !organsCollection) return [];
    return [
      organsCollection.find((o) => o.position === "LEFT" /* LEFT */),
      organsCollection.find((o) => o.position === "RIGHT" /* RIGHT */),
      organsCollection.find((o) => !o.position)
    ].filter(Boolean).map((organ) => ({
      logger: organ.toLogger(),
      position: organ.position
    }));
  }, [session, isSessionLoading, isV7, organsCollection, isOrgansCollectionLoading, loggerDefault, loggerLeft, loggerRight]);
  const isLoading = useMemo5(() => {
    if (!session) return isSessionLoading;
    if (isV7) {
      return isSessionLoading || defaultLoggerId != null && isLoggerDefaultLoading || leftLoggerId != null && isLoggerLeftLoading || rightLoggerId != null && isLoggerRightLoading;
    }
    return isSessionLoading || isOrgansCollectionLoading;
  }, [
    session,
    isSessionLoading,
    isV7,
    isOrgansCollectionLoading,
    defaultLoggerId,
    isLoggerDefaultLoading,
    leftLoggerId,
    isLoggerLeftLoading,
    rightLoggerId,
    isLoggerRightLoading
  ]);
  return [loggers, isLoading];
}

// src/client/sessions/use-get-session-eta.ts
import { collection as collection34, getDocs as getDocs30 } from "firebase/firestore";
import { useCollectionData as useCollectionData31 } from "react-firebase-hooks/firestore";
function getSessionETAQuery(firestore, sessionId) {
  return collection34(firestore, "sessions", sessionId, "eta").withConverter(SessionETAConverter);
}
async function getSessionETA(...args) {
  const { docs } = await getDocs30(getSessionETAQuery(...args));
  return docs.map((doc42) => doc42.data());
}
function useGetSessionETA(...args) {
  return useCollectionData31(getSessionETAQuery(...args));
}

// src/client/sessions/use-get-session-flight-positions.ts
import { collection as collection35, getDocs as getDocs31, orderBy as orderBy21, query as query25 } from "firebase/firestore";
import { useCollectionData as useCollectionData32 } from "react-firebase-hooks/firestore";
function getSessionFlightPositionsQuery(firestore, sessionId) {
  return query25(collection35(firestore, "sessions", sessionId, "flightPositions"), orderBy21("timestamp", "asc")).withConverter(
    FlightPositionConverter
  );
}
async function getSessionFlightPositions(...args) {
  const { docs } = await getDocs31(getSessionFlightPositionsQuery(...args));
  return docs.map((doc42) => doc42.data());
}
function useGetSessionFlightPositions(...args) {
  return useCollectionData32(getSessionFlightPositionsQuery(...args));
}

// src/client/sessions/use-get-session-flights.ts
import { collection as collection36, getDocs as getDocs32, query as query26 } from "firebase/firestore";
import { useCollectionData as useCollectionData33 } from "react-firebase-hooks/firestore";
function getSessionFlightsQuery(firestore, sessionId) {
  return query26(collection36(firestore, "sessions", sessionId, "flights")).withConverter(FlightConverter);
}
async function getSessionFlights(...args) {
  const { docs } = await getDocs32(getSessionFlightsQuery(...args));
  return docs.map((doc42) => doc42.data());
}
function useGetSessionFlights(...args) {
  return useCollectionData33(getSessionFlightsQuery(...args));
}

// src/client/sessions/use-get-session-last-timeline-entry.ts
import { collection as collection37, getDocs as getDocs33, limit as limit11, orderBy as orderBy22, query as query27 } from "firebase/firestore";
import { useCollectionData as useCollectionData34 } from "react-firebase-hooks/firestore";
function getSessionLastTimelineEntryQuery(firestore, sessionId) {
  return query27(collection37(firestore, "sessions", sessionId, "timeline"), orderBy22("timestamp", "desc"), limit11(1)).withConverter(
    TimelineEntryConverter
  );
}
async function getSessionLastTimelineEntry(...args) {
  const { docs } = await getDocs33(getSessionLastTimelineEntryQuery(...args));
  return docs.map((doc42) => doc42.data())[0];
}
function useGetSessionLastTimelineEntry(...args) {
  return useCollectionData34(getSessionLastTimelineEntryQuery(...args));
}

// src/client/sessions/use-get-session-log.ts
import { doc as doc30, getDoc as getDoc11 } from "firebase/firestore";
import { useDocumentData as useDocumentData15 } from "react-firebase-hooks/firestore";
function getSessionLogQuery(firestore, sessionId) {
  return doc30(firestore, "sessions", sessionId, "log", "log").withConverter(LogConverter);
}
async function getSessionLog(...args) {
  const doc42 = await getDoc11(getSessionLogQuery(...args));
  return doc42.data();
}
function useGetSessionLog(...args) {
  return useDocumentData15(getSessionLogQuery(...args));
}

// src/client/sessions/use-get-session-marker-details.ts
import { useMemo as useMemo6 } from "react";
function useGetSessionMarkerDetails(firestore, sessionId) {
  const [session, isSessionLoading] = useGetSession(firestore, sessionId);
  const [flights, lastFlightPositions, areFlightsLoading] = useSessionFlights2(firestore, sessionId, {
    session,
    isSessionLoading
  });
  const [loggers, areLoggersLoading] = useLoggers2(firestore, sessionId, { session, isSessionLoading });
  const isLoading = useMemo6(() => {
    return isSessionLoading || areFlightsLoading || areLoggersLoading;
  }, [isSessionLoading, areFlightsLoading, areLoggersLoading]);
  return {
    session,
    flights,
    lastFlightPositions,
    loggers,
    isLoading
  };
}
function useSessionFlights2(firestore, sessionId, data) {
  const { session, isSessionLoading } = data;
  const [flightPositions, areFlightPositionsLoading] = useGetSessionFlightPositions(firestore, sessionId);
  const [flightsCollection, isFlightsCollectionLoading] = useGetSessionFlights(firestore, sessionId);
  const flights = useMemo6(() => {
    if (!session || isSessionLoading || isFlightsCollectionLoading) {
      return [];
    }
    if (session.schemaVersion >= 6) {
      return flightsCollection?.filter((f) => !f.deleted) ?? [];
    }
    return session.flights ?? [];
  }, [session, isSessionLoading, flightsCollection, isFlightsCollectionLoading]);
  const lastFlightPositions = useMemo6(() => {
    if (!flights || !flightPositions || flightPositions.length === 0 || areFlightPositionsLoading) {
      return null;
    }
    return flights.reduce(
      (acc, flight) => {
        if (!flight.flightId) {
          return acc;
        }
        const positions = flightPositions.filter((fp) => fp.flightId === flight.flightId);
        if (positions.length === 0) {
          return acc;
        }
        acc[flight.flightId] = positions[positions.length - 1];
        return acc;
      },
      {}
    );
  }, [flights, flightPositions, areFlightPositionsLoading]);
  const isLoading = useMemo6(() => {
    return isSessionLoading || isFlightsCollectionLoading || areFlightPositionsLoading;
  }, [isSessionLoading, isFlightsCollectionLoading, areFlightPositionsLoading]);
  return [flights, lastFlightPositions, isLoading];
}
function useLoggers2(firestore, sessionId, data) {
  const { session, isSessionLoading } = data;
  const [organsCollection, isOrgansCollectionLoading] = useGetOrgans(firestore, sessionId);
  const loggerEntries = useMemo6(() => {
    if (!session || session.schemaVersion < 7 || !session.loggers) return [];
    return Object.entries(session.loggers).filter(([_, info]) => info?.loggerId).map(([key, info]) => ({
      loggerId: info.loggerId,
      position: key === "DEFAULT" ? void 0 : key
    }));
  }, [session]);
  const [loggerDefault, isLoggerDefaultLoading] = useGetLogger(
    firestore,
    loggerEntries.find((e) => !e.position)?.loggerId ?? "__none__"
  );
  const [loggerLeft, isLoggerLeftLoading] = useGetLogger(
    firestore,
    loggerEntries.find((e) => e.position === "LEFT" /* LEFT */)?.loggerId ?? "__none__"
  );
  const [loggerRight, isLoggerRightLoading] = useGetLogger(
    firestore,
    loggerEntries.find((e) => e.position === "RIGHT" /* RIGHT */)?.loggerId ?? "__none__"
  );
  const loggers = useMemo6(() => {
    if (!session || isSessionLoading) return [];
    if (session.schemaVersion >= 7 && session.loggers && Object.keys(session.loggers).length > 0) {
      const result = [];
      if (loggerDefault) result.push({ logger: loggerDefault, position: void 0 });
      if (loggerLeft) result.push({ logger: loggerLeft, position: "LEFT" /* LEFT */ });
      if (loggerRight) result.push({ logger: loggerRight, position: "RIGHT" /* RIGHT */ });
      return result;
    }
    if (session.schemaVersion < 6) {
      const organ = session.toOrgan();
      return [{ logger: organ.toLogger(), position: organ.position }];
    }
    if (isOrgansCollectionLoading || !organsCollection) return [];
    const sorted = [
      organsCollection.find((o) => o.position === "LEFT" /* LEFT */),
      organsCollection.find((o) => o.position === "RIGHT" /* RIGHT */),
      organsCollection.find((o) => !o.position)
    ].filter(Boolean);
    return sorted.map((organ) => ({
      logger: organ.toLogger(),
      position: organ.position
    }));
  }, [session, isSessionLoading, organsCollection, isOrgansCollectionLoading, loggerDefault, loggerLeft, loggerRight]);
  const isLoading = useMemo6(() => {
    if (!session) return isSessionLoading;
    if (session.schemaVersion >= 7) {
      return isSessionLoading || isLoggerDefaultLoading || isLoggerLeftLoading || isLoggerRightLoading;
    }
    return isSessionLoading || isOrgansCollectionLoading;
  }, [session, isSessionLoading, isOrgansCollectionLoading, isLoggerDefaultLoading, isLoggerLeftLoading, isLoggerRightLoading]);
  return [loggers, isLoading];
}

// src/client/sessions/use-get-session-member.ts
import { doc as doc31 } from "firebase/firestore";
import { useDocumentData as useDocumentData16 } from "react-firebase-hooks/firestore";
function useGetSessionMember(firestore, sessionId, userId) {
  return useDocumentData16(doc31(firestore, "sessions", sessionId, "users", userId).withConverter(SessionMemberConverter));
}

// src/client/sessions/use-get-session-members.ts
import { collection as collection38, getDocs as getDocs34, query as query28 } from "firebase/firestore";
import { useCollectionData as useCollectionData35 } from "react-firebase-hooks/firestore";
function getSessionMembersQuery(firestore, sessionId) {
  return query28(collection38(firestore, "sessions", sessionId, "users")).withConverter(SessionMemberConverter);
}
async function getSessionMembers(...args) {
  const { docs } = await getDocs34(getSessionMembersQuery(...args));
  return docs.map((doc42) => doc42.data());
}
function useGetSessionMembers(...args) {
  return useCollectionData35(getSessionMembersQuery(...args));
}

// src/client/sessions/use-get-session-recent-timeline.ts
import { collection as collection39, getDocs as getDocs35, limit as limit12, orderBy as orderBy23, query as query29, where as where8 } from "firebase/firestore";
import { useCollectionData as useCollectionData36 } from "react-firebase-hooks/firestore";
function getSessionRecentTimelineQuery(firestore, sessionId, pageSize = 12, options) {
  const col = collection39(firestore, "sessions", sessionId, "timeline");
  if (options?.excludeCustom) {
    return query29(col, where8("type", "!=", "CUSTOM"), limit12(pageSize)).withConverter(TimelineEntryConverter);
  }
  return query29(col, orderBy23("timestamp", "desc"), limit12(pageSize)).withConverter(TimelineEntryConverter);
}
async function getSessionRecentTimeline(...args) {
  const { docs } = await getDocs35(getSessionRecentTimelineQuery(...args));
  return docs.map((doc42) => doc42.data());
}
function useGetSessionRecentTimeline(...args) {
  return useCollectionData36(getSessionRecentTimelineQuery(...args));
}

// src/client/sessions/use-get-session-timeline.ts
import { collection as collection40, getDocs as getDocs36, orderBy as orderBy24, query as query30, where as where9 } from "firebase/firestore";
import { useCollectionData as useCollectionData37 } from "react-firebase-hooks/firestore";
function getSessionTimelineQuery(firestore, sessionId, options) {
  const col = collection40(firestore, "sessions", sessionId, "timeline");
  if (options?.excludeCustom) {
    return query30(col, where9("type", "!=", "CUSTOM")).withConverter(TimelineEntryConverter);
  }
  return query30(col, orderBy24("timestamp", "desc")).withConverter(TimelineEntryConverter);
}
async function getSessionTimeline(...args) {
  const { docs } = await getDocs36(getSessionTimelineQuery(...args));
  return docs.map((doc42) => doc42.data());
}
function useGetSessionTimeline(...args) {
  return useCollectionData37(getSessionTimelineQuery(...args));
}
function useSessionTimelineFromQuery(timelineQuery) {
  return useCollectionData37(timelineQuery);
}

// src/client/sessions/use-get-sessions.ts
import { collection as collection41, getDocs as getDocs37 } from "firebase/firestore";
import { useCollectionData as useCollectionData38 } from "react-firebase-hooks/firestore";
function getSessionsQuery(firestore) {
  return collection41(firestore, "sessions").withConverter(SessionConverter);
}
async function getSessions(...args) {
  const { docs } = await getDocs37(getSessionsQuery(...args));
  return docs.map((doc42) => doc42.data());
}
function useGetSessions(...args) {
  return useCollectionData38(getSessionsQuery(...args));
}

// src/client/shared-sessions/use-get-shared-session.ts
import { doc as doc32, getDoc as getDoc12 } from "firebase/firestore";
import { useDocumentData as useDocumentData17 } from "react-firebase-hooks/firestore";
function getSharedSessionQuery(firestore, sessionId) {
  return doc32(firestore, "shared_sessions", sessionId).withConverter(SharedSessionConverter);
}
async function getSharedSession(...args) {
  const doc42 = await getDoc12(getSharedSessionQuery(...args));
  return doc42.data();
}
function useGetSharedSession(...args) {
  return useDocumentData17(getSharedSessionQuery(...args));
}

// src/client/shared-sessions/use-get-shared-session-details.ts
import { useMemo as useMemo7 } from "react";
function useGetSharedSessionDetails(firestore, sessionId) {
  const [session, isSessionLoading] = useGetSharedSession(firestore, sessionId);
  const [ischemicTimes, areIschemicTimesLoading] = useGetSharedSessionIschemicTimes(firestore, sessionId);
  const [timeline, isTimelineLoading] = useGetSharedSessionTimeline(firestore, sessionId);
  const [organsCollection, isOrgansCollectionLoading] = useGetSharedSessionOrgans(firestore, sessionId);
  const v7Loggers = useSharedSessionLoggers(firestore, { session, isSessionLoading });
  const isLoading = useMemo7(() => {
    return isSessionLoading || areIschemicTimesLoading || isTimelineLoading || isOrgansCollectionLoading;
  }, [isSessionLoading, areIschemicTimesLoading, isTimelineLoading, isOrgansCollectionLoading]);
  const organs = useMemo7(() => {
    return [
      organsCollection?.find((o) => o.position === "LEFT" /* LEFT */),
      organsCollection?.find((o) => o.position === "RIGHT" /* RIGHT */),
      organsCollection?.find((o) => !o.position)
    ].filter(Boolean);
  }, [organsCollection, isOrgansCollectionLoading]);
  const loggers = useMemo7(() => {
    if (v7Loggers.length > 0) return v7Loggers;
    return organs.map((organ) => ({ logger: organ.toLogger(), position: organ.position }));
  }, [v7Loggers, organs]);
  const timelineAll = useMemo7(() => {
    if (!timeline || isTimelineLoading) {
      return [];
    }
    return timeline.filter((t) => !t.deleted);
  }, [timeline, isTimelineLoading]);
  const timelineLeft = useMemo7(() => {
    if (!timeline || isTimelineLoading) {
      return [];
    }
    return timelineAll.filter((t) => {
      const entryOrganPosition = TimelineEntryTypeUtil.getOrganPosition(t.type);
      return !entryOrganPosition || entryOrganPosition === "LEFT" /* LEFT */;
    });
  }, [timelineAll]);
  const timelineRight = useMemo7(() => {
    if (!timeline || isTimelineLoading) {
      return [];
    }
    return timelineAll.filter((t) => {
      const entryOrganPosition = TimelineEntryTypeUtil.getOrganPosition(t.type);
      return !entryOrganPosition || entryOrganPosition === "RIGHT" /* RIGHT */;
    });
  }, [timelineAll]);
  return {
    session,
    ischemicTimes: ischemicTimes ?? [],
    timelines: {
      all: timelineAll,
      ["LEFT" /* LEFT */]: timelineLeft,
      ["RIGHT" /* RIGHT */]: timelineRight
    },
    organs: organs ?? [],
    loggers,
    isLoading
  };
}
function useSharedSessionLoggers(firestore, data) {
  const { session, isSessionLoading } = data;
  const leftLoggerId = useMemo7(() => session?.loggers?.LEFT?.loggerId, [session]);
  const rightLoggerId = useMemo7(() => session?.loggers?.RIGHT?.loggerId, [session]);
  const defaultLoggerId = useMemo7(() => session?.loggers?.DEFAULT?.loggerId, [session]);
  const [loggerDefault] = useGetLogger(firestore, defaultLoggerId ?? "__none__");
  const [loggerLeft] = useGetLogger(firestore, leftLoggerId ?? "__none__");
  const [loggerRight] = useGetLogger(firestore, rightLoggerId ?? "__none__");
  return useMemo7(() => {
    if (!session || isSessionLoading || !session.loggers) return [];
    const result = [];
    const entries = [
      { key: "DEFAULT", logger: loggerDefault, position: void 0 },
      { key: "LEFT", logger: loggerLeft, position: "LEFT" /* LEFT */ },
      { key: "RIGHT", logger: loggerRight, position: "RIGHT" /* RIGHT */ }
    ];
    for (const { key, logger, position } of entries) {
      const loggerInfo = session.loggers[key];
      if (!loggerInfo?.loggerId) continue;
      const resolved = logger ?? createStubLogger(loggerInfo.loggerId, loggerInfo.product);
      result.push({ logger: resolved, position });
    }
    return result;
  }, [session, isSessionLoading, loggerDefault, loggerLeft, loggerRight]);
}
function createStubLogger(loggerId, product) {
  return new Logger({
    id: loggerId,
    serialNo: "",
    type: "",
    passkey: "",
    shared: false,
    product,
    linkedSessions: [],
    userIds: [],
    organizationIds: [],
    areaIds: [],
    territoryIds: [],
    createdAt: /* @__PURE__ */ new Date()
  });
}

// src/client/shared-sessions/use-get-shared-session-ischemic-times.ts
import { collection as collection42, getDocs as getDocs38 } from "firebase/firestore";
import { useCollectionData as useCollectionData39 } from "react-firebase-hooks/firestore";
function getSharedSessionIschemicTimesQuery(firestore, sessionId) {
  return collection42(firestore, "shared_sessions", sessionId, "ischemicTimes").withConverter(SessionIschemicTimeConverter);
}
async function getSharedSessionIschemicTimes(...args) {
  const { docs } = await getDocs38(getSharedSessionIschemicTimesQuery(...args));
  return docs.map((doc42) => doc42.data());
}
function useGetSharedSessionIschemicTimes(...args) {
  return useCollectionData39(getSharedSessionIschemicTimesQuery(...args));
}

// src/client/shared-sessions/use-get-shared-session-organ.ts
import { doc as doc33, getDoc as getDoc13 } from "firebase/firestore";
import { useDocumentData as useDocumentData18 } from "react-firebase-hooks/firestore";
function getSharedSessionOrganQuery(firestore, sessionId, organId) {
  return doc33(firestore, "shared_sessions", sessionId, "organs", organId).withConverter(OrganConverter);
}
async function getSharedSessionOrgan(...args) {
  const doc42 = await getDoc13(getSharedSessionOrganQuery(...args));
  return doc42.data();
}
function useGetSharedSessionOrgan(...args) {
  return useDocumentData18(getSharedSessionOrganQuery(...args));
}

// src/client/shared-sessions/use-get-shared-session-organ-logs.ts
import { collection as collection43, getDocs as getDocs39, orderBy as orderBy25, query as query31 } from "firebase/firestore";
import { useCollectionData as useCollectionData40 } from "react-firebase-hooks/firestore";
function getSharedSessionOrganLogsQuery(firestore, sessionId, organId) {
  return query31(collection43(firestore, "shared_sessions", sessionId, "organs", organId, "logs"), orderBy25("order", "asc")).withConverter(
    OrganReadingsPageConverter
  );
}
async function getSharedSessionOrganLogs(...args) {
  const { docs } = await getDocs39(getSharedSessionOrganLogsQuery(...args));
  return docs.map((doc42) => doc42.data());
}
function useGetSharedSessionOrganLogs(...args) {
  return useCollectionData40(getSharedSessionOrganLogsQuery(...args));
}

// src/client/shared-sessions/use-get-shared-session-organs.ts
import { collection as collection44, getDocs as getDocs40 } from "firebase/firestore";
import { useCollectionData as useCollectionData41 } from "react-firebase-hooks/firestore";
function getSharedSessionOrgansQuery(firestore, sessionId) {
  return collection44(firestore, "shared_sessions", sessionId, "organs").withConverter(OrganConverter);
}
async function getSharedSessionOrgans(...args) {
  const { docs } = await getDocs40(getSharedSessionOrgansQuery(...args));
  return docs.map((doc42) => doc42.data());
}
function useGetSharedSessionOrgans(...args) {
  return useCollectionData41(getSharedSessionOrgansQuery(...args));
}

// src/client/shared-sessions/use-get-shared-session-timeline.ts
import { collection as collection45, getDocs as getDocs41, orderBy as orderBy26, query as query32 } from "firebase/firestore";
import { useCollectionData as useCollectionData42 } from "react-firebase-hooks/firestore";
function getSharedSessionTimelineQuery(firestore, sessionId) {
  return query32(collection45(firestore, "shared_sessions", sessionId, "timeline"), orderBy26("timestamp", "desc")).withConverter(
    TimelineEntryConverter
  );
}
async function getSharedSessionTimeline(...args) {
  const { docs } = await getDocs41(getSharedSessionTimelineQuery(...args));
  return docs.map((doc42) => doc42.data());
}
function useGetSharedSessionTimeline(...args) {
  return useCollectionData42(getSharedSessionTimelineQuery(...args));
}

// src/client/timeline/add-timeline-entry.ts
import { collection as collection46, doc as doc34, serverTimestamp as serverTimestamp7, writeBatch } from "firebase/firestore";
function generateEntryId(firestore, entryType) {
  const uniqueSuffix = doc34(collection46(firestore, "_")).id;
  return `${entryType}_${uniqueSuffix}`;
}
async function addTimelineEntry(firestore, sessionId, data, options) {
  const entryId = generateEntryId(firestore, data.type);
  const entryData = {
    ...data,
    id: entryId,
    deleted: false,
    createdAt: serverTimestamp7(),
    updatedAt: serverTimestamp7(),
    committedAt: serverTimestamp7(),
    ...options?.meta ? { meta: options.meta } : {}
  };
  const batch = writeBatch(firestore);
  const parentRef = doc34(firestore, "sessions", sessionId, "timeline", entryId);
  batch.set(parentRef, entryData);
  if (options?.childSessionIds?.length) {
    for (const childSessionId of options.childSessionIds) {
      const childRef = doc34(firestore, "sessions", childSessionId, "timeline", entryId);
      batch.set(childRef, {
        ...entryData,
        referenceId: entryId
        // Link to parent entry for traceability
      });
    }
  }
  await batch.commit();
  return entryId;
}

// src/client/timeline/comment-reactions.ts
import { deleteField as deleteField3, doc as doc35, serverTimestamp as serverTimestamp8, updateDoc as updateDoc9 } from "firebase/firestore";
async function setCommentReaction(firestore, sessionId, entryId, commentId, userId, emoji) {
  const commentDoc = doc35(firestore, "sessions", sessionId, "timeline", entryId, "comments", commentId);
  if (emoji) {
    await updateDoc9(commentDoc, {
      [`reactions.${userId}`]: {
        emoji,
        updatedAt: serverTimestamp8()
      }
    });
  } else {
    await updateDoc9(commentDoc, {
      [`reactions.${userId}`]: deleteField3()
    });
  }
}
async function removeCommentReaction(firestore, sessionId, entryId, commentId, userId) {
  const commentDoc = doc35(firestore, "sessions", sessionId, "timeline", entryId, "comments", commentId);
  await updateDoc9(commentDoc, {
    [`reactions.${userId}`]: deleteField3()
  });
}

// src/client/timeline/delete-comment.ts
import { doc as doc36, serverTimestamp as serverTimestamp9, updateDoc as updateDoc10 } from "firebase/firestore";
async function deleteComment(firestore, sessionId, entryId, commentId, userId) {
  const commentDoc = doc36(firestore, "sessions", sessionId, "timeline", entryId, "comments", commentId);
  await updateDoc10(commentDoc, {
    deleted: true,
    deletedBy: userId,
    deletedAt: serverTimestamp9()
  });
}

// src/client/timeline/delete-timeline-entry.ts
import { doc as doc37, serverTimestamp as serverTimestamp10, writeBatch as writeBatch2 } from "firebase/firestore";
async function deleteTimelineEntry(firestore, sessionId, entryId, userId, options) {
  const deleteData = {
    deleted: true,
    deletedBy: userId,
    deletedAt: serverTimestamp10(),
    updatedAt: serverTimestamp10(),
    ...options?.meta ? { meta: options.meta } : {}
  };
  const batch = writeBatch2(firestore);
  const parentRef = doc37(firestore, "sessions", sessionId, "timeline", entryId);
  batch.update(parentRef, deleteData);
  if (options?.childSessionIds?.length) {
    for (const childSessionId of options.childSessionIds) {
      const childRef = doc37(firestore, "sessions", childSessionId, "timeline", entryId);
      batch.update(childRef, deleteData);
    }
  }
  await batch.commit();
}

// src/client/timeline/get-comment-receipts.ts
import { collection as collection47, getDocs as getDocs42 } from "firebase/firestore";
import { useCollectionData as useCollectionData43 } from "react-firebase-hooks/firestore";
function getCommentReceiptsQuery(firestore, sessionId, entryId, commentId) {
  if (!sessionId || !entryId || !commentId) {
    return null;
  }
  return collection47(firestore, "sessions", sessionId, "timeline", entryId, "comments", commentId, "receipts").withConverter(
    MessageReceiptConverter
  );
}
async function getCommentReceipts(firestore, sessionId, entryId, commentId) {
  const query35 = getCommentReceiptsQuery(firestore, sessionId, entryId, commentId);
  if (!query35) return [];
  const { docs } = await getDocs42(query35);
  return docs.map((doc42) => doc42.data());
}
function useGetCommentReceipts(firestore, sessionId, entryId, commentId) {
  const query35 = getCommentReceiptsQuery(firestore, sessionId, entryId, commentId);
  return useCollectionData43(query35);
}

// src/client/timeline/get-entry-read-states.ts
import { collection as collection48, getDocs as getDocs43 } from "firebase/firestore";
import { useCollectionData as useCollectionData44 } from "react-firebase-hooks/firestore";
function getEntryReadStatesQuery(firestore, sessionId, entryId) {
  return collection48(firestore, "sessions", sessionId, "timeline", entryId, "read_states").withConverter(TimelineEntryReadStateConverter);
}
async function getEntryReadStates(...args) {
  const { docs } = await getDocs43(getEntryReadStatesQuery(...args));
  return docs.map((doc42) => doc42.data());
}
function useGetEntryReadStates(...args) {
  return useCollectionData44(getEntryReadStatesQuery(...args));
}

// src/client/timeline/update-timeline-entry.ts
import { doc as doc38, serverTimestamp as serverTimestamp11, writeBatch as writeBatch3 } from "firebase/firestore";
async function updateTimelineEntry(firestore, sessionId, entryId, data, options) {
  const batch = writeBatch3(firestore);
  const updateData = {
    ...data,
    updatedAt: serverTimestamp11(),
    ...options?.meta ? { meta: options.meta } : {}
  };
  const parentRef = doc38(firestore, "sessions", sessionId, "timeline", entryId);
  batch.update(parentRef, updateData);
  if (options?.childSessionIds?.length) {
    for (const childSessionId of options.childSessionIds) {
      const childRef = doc38(firestore, "sessions", childSessionId, "timeline", entryId);
      batch.update(childRef, updateData);
    }
  }
  await batch.commit();
}

// src/client/timeline/use-get-timeline-comments.ts
import { collection as collection49, getDocs as getDocs44, limit as limit13, orderBy as orderBy27, query as query33, startAfter as startAfter2, where as where10 } from "firebase/firestore";
import { useCollectionData as useCollectionData45 } from "react-firebase-hooks/firestore";
var BASE_COMMENT_FILTERS = [
  where10("deleted", "==", false),
  where10("deliveryStatus", "in", ["SENT" /* SENT */, "DELIVERED" /* DELIVERED */, "READ" /* READ */])
];
function getTimelineCommentsCollectionRef(firestore, sessionId, entryId) {
  return collection49(firestore, "sessions", sessionId, "timeline", entryId, "comments");
}
function getTimelineCommentsQuery(firestore, sessionId, entryId, constraints = []) {
  const baseQuery = getTimelineCommentsCollectionRef(firestore, sessionId, entryId).withConverter(MessageConverter);
  return query33(baseQuery, ...constraints);
}
function getLiveTimelineCommentsQuery(firestore, sessionId, entryId, pageSize = 80, _currentUserId) {
  const baseQuery = getTimelineCommentsCollectionRef(firestore, sessionId, entryId).withConverter(MessageConverter);
  return query33(baseQuery, ...BASE_COMMENT_FILTERS, orderBy27("timestamp", "desc"), limit13(pageSize));
}
async function getTimelineComments(...args) {
  const { docs } = await getDocs44(getTimelineCommentsQuery(...args));
  return docs.map((doc42) => doc42.data());
}
async function getEarlierTimelineComments(firestore, sessionId, entryId, oldestTimestamp, pageSize = 80) {
  return getTimelineComments(firestore, sessionId, entryId, [
    ...BASE_COMMENT_FILTERS,
    orderBy27("timestamp", "desc"),
    startAfter2(oldestTimestamp),
    limit13(pageSize)
  ]);
}
function useGetTimelineComments(...args) {
  return useCollectionData45(getTimelineCommentsQuery(...args));
}
function useGetLiveTimelineComments(firestore, sessionId, entryId, pageSize = 80) {
  return useGetTimelineComments(firestore, sessionId, entryId, [...BASE_COMMENT_FILTERS, orderBy27("timestamp", "desc"), limit13(pageSize)]);
}
function useTimelineCommentsFromQuery(commentsQuery) {
  return useCollectionData45(commentsQuery);
}

// src/client/timeline/use-send-comment.ts
import { addDoc as addDoc4, collection as collection50, doc as doc39, serverTimestamp as serverTimestamp12, setDoc as setDoc8 } from "firebase/firestore";
function getCommentsCollection(firestore, sessionId, entryId) {
  return collection50(firestore, "sessions", sessionId, "timeline", entryId, "comments");
}
function generateCommentId(firestore, sessionId, entryId) {
  const commentsCollection = getCommentsCollection(firestore, sessionId, entryId);
  return doc39(commentsCollection).id;
}
async function sendComment(firestore, sessionId, entryId, senderId, content, attachments, replyTo, commentId) {
  const hasContent = content.trim().length > 0;
  const hasAttachments = attachments && attachments.length > 0;
  if (!hasContent && !hasAttachments) {
    throw new Error("Comment must have content or attachments");
  }
  const commentData = {
    parentId: entryId,
    senderId,
    content: content.trim(),
    createdAt: serverTimestamp12(),
    timestamp: serverTimestamp12(),
    deliveryStatus: "PENDING" /* PENDING */,
    deleted: false,
    type: "TEXT" /* TEXT */,
    ...hasAttachments && { attachments },
    ...replyTo && { replyTo }
  };
  const commentsCollection = getCommentsCollection(firestore, sessionId, entryId);
  if (commentId) {
    const docRef = doc39(commentsCollection, commentId);
    await setDoc8(docRef, commentData);
    return commentId;
  } else {
    const docRef = await addDoc4(commentsCollection, commentData);
    return docRef.id;
  }
}

// src/client/user-sessions/use-get-user-session.ts
import { doc as doc40, getDoc as getDoc14 } from "firebase/firestore";
import { useDocumentData as useDocumentData19 } from "react-firebase-hooks/firestore";
function getUserSessionQuery(firestore, userId, sessionId) {
  return doc40(firestore, "users", userId, "sessions", sessionId).withConverter(UserSessionConverter);
}
async function getUserSession(...args) {
  const snapshot = await getDoc14(getUserSessionQuery(...args));
  return snapshot.data();
}
function useGetUserSession(...args) {
  return useDocumentData19(getUserSessionQuery(...args));
}

// src/client/user-sessions/use-get-user-sessions.ts
import { collection as collection51, getDocs as getDocs45 } from "firebase/firestore";
import { useCollectionData as useCollectionData46 } from "react-firebase-hooks/firestore";
function getUserSessionsQuery(firestore, userId) {
  return collection51(firestore, "users", userId, "sessions").withConverter(UserSessionConverter);
}
async function getUserSessions(...args) {
  const { docs } = await getDocs45(getUserSessionsQuery(...args));
  return docs.map((doc42) => doc42.data());
}
function useGetUserSessions(...args) {
  return useCollectionData46(getUserSessionsQuery(...args));
}

// src/client/users.ts
import { collection as collection52, doc as doc41, getDoc as getDoc15, getDocs as getDocs46, query as query34, serverTimestamp as serverTimestamp13, setDoc as setDoc9, updateDoc as updateDoc11, where as where11 } from "firebase/firestore";
import { useCollectionData as useCollectionData47, useDocumentData as useDocumentData20 } from "react-firebase-hooks/firestore";
function getUsersQuery(firestore) {
  return collection52(firestore, "users").withConverter(UserConverter);
}
async function getUsers(...args) {
  const { docs } = await getDocs46(getUsersQuery(...args));
  return docs.map((doc42) => doc42.data());
}
function useGetUsers(...args) {
  return useCollectionData47(getUsersQuery(...args));
}
function getParagonixUsersQuery(firestore) {
  return query34(collection52(firestore, "users"), where11("isParagonixUser", "==", true)).withConverter(UserConverter);
}
async function getParagonixUsers(...args) {
  const { docs } = await getDocs46(getParagonixUsersQuery(...args));
  return docs.map((doc42) => doc42.data());
}
function useGetParagonixUsers(...args) {
  return useCollectionData47(getParagonixUsersQuery(...args));
}
function getUserQuery(firestore, userId) {
  return doc41(firestore, "users", userId).withConverter(UserConverter);
}
async function getUser(...args) {
  const doc42 = await getDoc15(getUserQuery(...args));
  return doc42.data();
}
function useGetUser(...args) {
  return useDocumentData20(getUserQuery(...args));
}
async function getUserByEmail(firestore, email) {
  const usersQuery = query34(collection52(firestore, "users"), where11("email", "==", email.toLowerCase())).withConverter(UserConverter);
  const { docs } = await getDocs46(usersQuery);
  return docs.length > 0 ? docs[0].data() : void 0;
}
async function createUser(firestore, id, data) {
  await setDoc9(doc41(firestore, "users", id), {
    ...data,
    hasSecurePassword: data.hasSecurePassword ?? false,
    needsOrganizationProfile: true,
    needsEmailVerification: true,
    createdAt: serverTimestamp13()
  });
}
async function updateUser(firestore, userId, data) {
  await updateDoc11(doc41(firestore, "users", userId), data);
}
async function updateUserSettings(firestore, userId, data) {
  await Promise.all(Object.entries(data).map(([key, value]) => updateDoc11(doc41(firestore, "users", userId), { [`settings.${key}`]: value })));
}
async function updateUserDevice(firestore, userId, deviceId, data) {
  const ref3 = doc41(firestore, "users", userId, "devices", deviceId);
  await setDoc9(ref3, { ...data, lastSeen: serverTimestamp13() }, { merge: true });
}
export {
  activateOrganizationMemberFn,
  addAttachments,
  addChatMembersFn,
  addFlightFn,
  addManualFlightFn,
  addMembersToSessionFn,
  addPotentialSessionsFn,
  addSessionETA,
  addTimelineEntry,
  approvePotentialSessionFn,
  cancelBreakoutSessionFn,
  cancelOrganizationAccessRequestFn,
  confirmEmailVerificationFn,
  confirmOrganizationValidationFn,
  countPotentialSessionsByStatus,
  createAttachmentRef,
  createBreakoutSessionFn,
  createChatFn,
  createOrganization,
  createSessionAuditLog,
  createSharedSessionFn,
  createUser,
  deactivateOrganizationMemberFn,
  deleteComment,
  deleteMessage,
  deleteSessionETA,
  deleteTimelineEntry,
  exportSessionsToSheetFn,
  findOrganizations,
  findOrganizationsQuery,
  findPotentialSessions,
  findPotentialSessionsQuery,
  findSessions,
  findSessionsQuery,
  generateCommentId,
  generateMessageId,
  getActiveChatMembers,
  getAttachmentBlob,
  getAttachmentStorageRef,
  getAttachmentUrl,
  getChat,
  getChatMembers,
  getChatMembersQuery,
  getChatRef,
  getCommentReceipts,
  getCommentReceiptsQuery,
  getDonorDataQuery,
  getDraft,
  getDraftQuery,
  getEarlierMessages,
  getEarlierMessagesQuery,
  getEarlierTimelineComments,
  getEntryReadStates,
  getEntryReadStatesQuery,
  getLastOrganLog,
  getLiveMessagesQuery,
  getLiveTimelineCommentsQuery,
  getLogger,
  getLoggerAdvancedTracking,
  getLoggerAdvancedTrackingQuery,
  getLoggerAppLocations,
  getLoggerAppLocationsQuery,
  getLoggerAppLogPages,
  getLoggerAppLogPagesQuery,
  getLoggerChartData,
  getLoggerChartDataQuery,
  getLoggerLastAdvancedTracking,
  getLoggerLastAdvancedTrackingQuery,
  getLoggerLastAppLocation,
  getLoggerLastAppLocationQuery,
  getLoggerLastAppLogPage,
  getLoggerLastAppLogPageQuery,
  getLoggerQuery,
  getLoggers,
  getLoggersQuery,
  getMessage,
  getMessageQuery,
  getMessageReceipts,
  getMessageReceiptsQuery,
  getMessages,
  getMessagesQuery,
  getMessagingToken,
  getNotesDataQuery,
  getOrgan,
  getOrganAdvancedTracking,
  getOrganAdvancedTrackingQuery,
  getOrganLastAdvancedTracking,
  getOrganLastAdvancedTrackingQuery,
  getOrganLastLogQuery,
  getOrganLastLoggerLocation,
  getOrganLastLoggerLocationQuery,
  getOrganLoggerLocations,
  getOrganLoggerLocationsQuery,
  getOrganLogs,
  getOrganLogsQuery,
  getOrganQuery,
  getOrganization,
  getOrganizationMember,
  getOrganizationMemberQuery,
  getOrganizationMembers,
  getOrganizationMembersForSessionFn,
  getOrganizationMembersQuery,
  getOrganizationPreselectedMember,
  getOrganizationPreselectedMemberQuery,
  getOrganizationPreselectedMembers,
  getOrganizationPreselectedMembersQuery,
  getOrganizationQuery,
  getOrganizationUnregisteredInvites,
  getOrganizationUnregisteredInvitesQuery,
  getOrganizations,
  getOrganizationsQuery,
  getOrgans,
  getOrgansQuery,
  getParagonixUsers,
  getParagonixUsersQuery,
  getPotentialSession,
  getPotentialSessionQuery,
  getPotentialSessions,
  getPotentialSessionsByStatus,
  getPotentialSessionsByStatusQuery,
  getPotentialSessionsQuery,
  getPublicProfilesByIdsQuery,
  getRecipientDataQuery,
  getSession,
  getSessionAdvancedTracking,
  getSessionAdvancedTrackingQuery,
  getSessionAuditLog,
  getSessionAuditLogQuery,
  getSessionChats,
  getSessionChatsQuery,
  getSessionETA,
  getSessionETAQuery,
  getSessionFlightPositions,
  getSessionFlightPositionsQuery,
  getSessionFlights,
  getSessionFlightsQuery,
  getSessionIschemicTimes,
  getSessionIschemicTimesQuery,
  getSessionLastAdvancedTracking,
  getSessionLastAdvancedTrackingQuery,
  getSessionLastLoggerLocation,
  getSessionLastLoggerLocationQuery,
  getSessionLastTimelineEntry,
  getSessionLastTimelineEntryQuery,
  getSessionLog,
  getSessionLogQuery,
  getSessionLoggerLocations,
  getSessionLoggerLocationsQuery,
  getSessionMembers,
  getSessionMembersQuery,
  getSessionQuery,
  getSessionRecentTimeline,
  getSessionRecentTimelineQuery,
  getSessionTimeline,
  getSessionTimelineQuery,
  getSessions,
  getSessionsQuery,
  getSharedSession,
  getSharedSessionIschemicTimes,
  getSharedSessionIschemicTimesQuery,
  getSharedSessionOrgan,
  getSharedSessionOrganLogs,
  getSharedSessionOrganLogsQuery,
  getSharedSessionOrganQuery,
  getSharedSessionOrgans,
  getSharedSessionOrgansQuery,
  getSharedSessionQuery,
  getSharedSessionTimeline,
  getSharedSessionTimelineQuery,
  getTimelineComments,
  getTimelineCommentsCollectionRef,
  getTimelineCommentsQuery,
  getUser,
  getUserByEmail,
  getUserChatRef,
  getUserChats,
  getUserChatsQuery,
  getUserQuery,
  getUserSession,
  getUserSessionQuery,
  getUserSessions,
  getUserSessionsQuery,
  getUsers,
  getUsersQuery,
  grantOrganizationAccessFn,
  identifyPotentialSessionsFn,
  inviteToOrganizationFn,
  joinOrganizationFn,
  leaveOrganizationFn,
  onFirebaseMessage,
  rejectOrganizationAccessRequestFn,
  rejectPotentialSessionFn,
  removeAttachmentRefs,
  removeChatMemberFn,
  removeCommentReaction,
  removeFlightFn,
  removeMessageReaction,
  requestOrganizationAccessFn,
  requestPotentialSessionReviewFn,
  searchAirports,
  searchFlightsFn,
  sendComment,
  sendEmailVerificationFn,
  sendMessage,
  sendMessageQuery,
  sendOrganizationMemberInviteFn,
  sendPasswordResetFn,
  setCommentReaction,
  setDraftDonorData,
  setDraftNotesData,
  setDraftRecipientData,
  setMessageReaction,
  setReadCursorFn,
  signInWithEmailAndPassword,
  signOut,
  softDeleteAttachments,
  startOrganizationValidationFn,
  toggleSessionMute,
  unlinkLoggerFn,
  updateChatDetails,
  updateDraftDocument,
  updateOrganization,
  updateSessionETA,
  updateTimelineEntry,
  updateUser,
  updateUserDevice,
  updateUserSettings,
  uploadAttachment,
  uploadAttachmentWithProgress,
  uploadDraftPdf,
  useFindOrganizations,
  useFindPotentialSessions,
  useFindSessions,
  useGetActiveChatMembers,
  useGetChat,
  useGetChatMembers,
  useGetCommentReceipts,
  useGetDonorData,
  useGetDraft,
  useGetEntryReadStates,
  useGetInventoryLocation,
  useGetInventoryLocations,
  useGetLastOrganLog,
  useGetLiveMessages,
  useGetLiveTimelineComments,
  useGetLogger,
  useGetLoggerAdvancedTracking,
  useGetLoggerAppLocations,
  useGetLoggerAppLogPages,
  useGetLoggerChartData,
  useGetLoggerLastAdvancedTracking,
  useGetLoggerLastAppLocation,
  useGetLoggerLastAppLogPage,
  useGetLoggers,
  useGetMessage,
  useGetMessageReceipts,
  useGetMessages,
  useGetNotesData,
  useGetOrgan,
  useGetOrganAdvancedTracking,
  useGetOrganLastAdvancedTracking,
  useGetOrganLastLoggerLocation,
  useGetOrganLoggerLocations,
  useGetOrganLogs,
  useGetOrganMarkerDetails,
  useGetOrganPolylinesDetails,
  useGetOrganization,
  useGetOrganizationMember,
  useGetOrganizationMembers,
  useGetOrganizationPreselectedMember,
  useGetOrganizationPreselectedMembers,
  useGetOrganizationUnregisteredInvites,
  useGetOrganizations,
  useGetOrgans,
  useGetParagonixUsers,
  useGetPotentialSession,
  useGetPotentialSessions,
  useGetPotentialSessionsByStatus,
  useGetRecipientData,
  useGetSession,
  useGetSessionAdvancedTracking,
  useGetSessionAuditLog,
  useGetSessionChats,
  useGetSessionDetails,
  useGetSessionETA,
  useGetSessionFlightPositions,
  useGetSessionFlights,
  useGetSessionIschemicTimes,
  useGetSessionLastAdvancedTracking,
  useGetSessionLastLoggerLocation,
  useGetSessionLastTimelineEntry,
  useGetSessionLog,
  useGetSessionLoggerLocations,
  useGetSessionMarkerDetails,
  useGetSessionMember,
  useGetSessionMembers,
  useGetSessionRecentTimeline,
  useGetSessionTimeline,
  useGetSessions,
  useGetSharedSession,
  useGetSharedSessionDetails,
  useGetSharedSessionIschemicTimes,
  useGetSharedSessionOrgan,
  useGetSharedSessionOrganLogs,
  useGetSharedSessionOrgans,
  useGetSharedSessionTimeline,
  useGetTimelineComments,
  useGetUser,
  useGetUserChat,
  useGetUserChats,
  useGetUserSession,
  useGetUserSessions,
  useGetUsers,
  useMessagesFromQuery,
  useSessionTimelineFromQuery,
  useTimelineCommentsFromQuery
};
